import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Image as ImageIcon,
  User,
  Type,
  Palette,
  Badge,
  Sparkles,
  Lightbulb,
  Check,
  CheckCircle2,
  Minus,
  Plus,
  Loader2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Info,
  X,
  ImagePlus,
  MoreHorizontal,
  Clock,
} from "lucide-react";
import CachedImg from "../../components/CachedImg";
import GeneratedImagesInline from "../../components/GeneratedImagesInline";
import SubscriptionLimitModal from "../../components/SubscriptionLimitModal";
import { costoDeGeneracion } from "../../lib/costoGeneracion";
import LoadingAnimation from "../../components/LoadingAnimation";
import {
  postGenerateModelFace,
  postSaveModelFromGenerated,
} from "../../lib/modelsBackend";
import { postPromptInstrucciones } from "../../lib/promptInstructions";
import { GenerateAdError } from "../../lib/generateAdRequest";
import { generateClassicImages } from "../v2/imageGenerationRecovery";
import { supabase } from "../../lib/supabase";
import { fetchInChunks } from "../../utils/supabaseChunkedIn";
import {
  loadLastCrearAnuncioState,
  mergeLastCrearAnuncioState,
} from "../../utils/crearAnuncioStorage";
import { useSidebar } from "../../context/SidebarContext";
import { isHeicFile, isWebpFile, isUnsupportedUploadFile, UNSUPPORTED_FORMAT_MESSAGE, convertHeicToJpeg } from "../../utils/heicConvert";
import {
  preloadGeneratedImages,
  type PreloadedImage,
} from "../../utils/preloadGeneratedImages";

type ProductImage = {
  id: string;
  url: string;
  principal: boolean;
  orden: number;
  variante_id: string;
};
type Variant = { id: string; nombre: string; images?: ProductImage[] };

type ScenarioKey =
  | "sugerido"
  | "fondo_blanco"
  | "personalizado"
  | "biblioteca"
  | "escenas_guardadas";
type FormatoKey =
  | "post"
  | "story"
  | "square"
  | "portrait"
  | "banner"
  | "editorial"
  | "billboard";

export const PruebaNuevaInterfaz: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarWidth } = useSidebar();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const idsParam = params.get("ids") || "";
  const ids = useMemo(
    () =>
      idsParam
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [idsParam],
  );
  const modeloMode = params.get("modelo_mode") as
    | "ia"
    | "guardados"
    | "personalizado"
    | null;
  const textoSelected = params.get("texto_enabled") === "1";
  const modeloBadgeText =
    modeloMode === "guardados"
      ? "Añadiendo modelo guardado"
      : modeloMode === "personalizado"
        ? "Añadiendo modelo personalizado"
        : modeloMode === "ia"
          ? "Añadiendo modelo automático"
          : null;

  const tapFlash = (e: React.MouseEvent<HTMLElement>) => {
    const el = e.currentTarget;
    el.classList.remove("tap-flash");
    void el.offsetWidth;
    el.classList.add("tap-flash");
  };

  const cantidadImagenesParam = params.get("cantidad_imagenes");
  const formatoParam = params.get("formato") as FormatoKey | null;

  const [variants, setVariants] = useState<Variant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedScenarios, setSelectedScenarios] = useState<
    Record<ScenarioKey, boolean>
  >({
    sugerido: true,
    fondo_blanco: false,
    personalizado: false,
    biblioteca: false,
    escenas_guardadas: false,
  });
  const [imageCount, setImageCount] = useState<number>(() => {
    const n = Number(cantidadImagenesParam);
    return Number.isFinite(n) && n > 0 ? n : 2;
  });
  const [format, setFormat] = useState<FormatoKey>(() => {
    if (
      formatoParam &&
      ["post", "story", "square", "portrait", "banner", "editorial", "billboard"].includes(formatoParam)
    )
      return formatoParam;
    return "post";
  });
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [signedUrls, setSignedUrls] = useState<
    Record<string, { full: string; thumbnail: string }>
  >({});
  const [soonFeatureKey, setSoonFeatureKey] = useState<string | null>(null);
  const [deleteConfirmKey, setDeleteConfirmKey] = useState<string | null>(null);
  const [generating, setGenerating] = useState<boolean>(false);
  const [imagesReady, setImagesReady] = useState<boolean>(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState<boolean>(false);
  const [loadingExiting, setLoadingExiting] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [generatedImageIds, setGeneratedImageIds] = useState<number[]>([]);
  const [companyIdForImages, setCompanyIdForImages] = useState<string>("");
  const [genError, setGenError] = useState<string | null>(null);
  useEffect(() => {
    if (!genError) return;
    const t = setTimeout(() => setGenError(null), 6000);
    return () => clearTimeout(t);
  }, [genError]);
  const consecutiveErrorCountRef = useRef<number>(0);
  const [showContactQR, setShowContactQR] = useState<boolean>(false);
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] =
    useState<boolean>(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<
    "free-trial-ended" | "plan-limit-reached"
  >("free-trial-ended");
  const loadingImageKeysRef = useRef<Set<string>>(new Set());
  const defaultsInitializedRef = useRef(false);
  // Ref to block rapid clicks before React re-renders
  const generatingLockRef = useRef<boolean>(false);
  const [loadingDurationMs, setLoadingDurationMs] = useState<number>(80000);
  const [activeTab, setActiveTab] = useState<
    "automatico" | "personalizado" | "creativo"
  >("automatico");
  const [tabSlideDir, setTabSlideDir] = useState<"left" | "right" | null>(null);
  const prevTabRef = useRef<"automatico" | "personalizado" | "creativo">(
    "automatico",
  );
  const [modeloEnabled, setModeloEnabled] = useState<boolean>(true);
  const [selectedModeloOption, setSelectedModeloOption] = useState<
    "ia" | "personalizado" | "real" | "anteriores" | null
  >(
    modeloMode === "ia"
      ? "ia"
      : modeloMode === "personalizado"
        ? "personalizado"
        : modeloMode === "guardados"
          ? "anteriores"
          : null,
  );
  // Prioridad fija en "Calidad" (modo pro): el toggle Velocidad/Calidad se
  // eliminó (las rápidas salían de mala calidad). Única excepción: si el
  // kill-switch config_system_atlas.pro_model_active apaga el modelo pro,
  // se cae a "normal" para no romper la generación.
  const [proModelActive, setProModelActive] = useState<boolean>(true);
  const calidad: "normal" | "pro" = proModelActive ? "pro" : "normal";
  const [countAnimating, setCountAnimating] = useState<boolean>(false);
  const [showFormatoMenu, setShowFormatoMenu] = useState<boolean>(false);
  const [showEscenaMenu, setShowEscenaMenu] = useState<boolean>(false);
  const [showBibliotecaMenu, setShowBibliotecaMenu] = useState<boolean>(false);
  const [formatoMenuDragY, setFormatoMenuDragY] = useState<number>(0);
  const [escenaMenuDragY, setEscenaMenuDragY] = useState<number>(0);
  const [bibliotecaMenuDragY, setBibliotecaMenuDragY] = useState<number>(0);
  const [formatoMenuAnimated, setFormatoMenuAnimated] =
    useState<boolean>(false);
  const [escenaMenuAnimated, setEscenaMenuAnimated] = useState<boolean>(false);
  const [bibliotecaMenuAnimated, setBibliotecaMenuAnimated] =
    useState<boolean>(false);
  const formatoDragStartY = useRef<number | null>(null);
  const escenaDragStartY = useRef<number | null>(null);
  const bibliotecaDragStartY = useRef<number | null>(null);
  const [escenaPrompt, setEscenaPrompt] = useState<string>(
    () => params.get("escenario_desc") || "",
  );
  const [escenaIdeas, setEscenaIdeas] = useState<string[]>([]);
  const [escenaIdeasLoading, setEscenaIdeasLoading] = useState<boolean>(false);
  const [bibliotecaFondos, setBibliotecaFondos] = useState<
    Array<{ id: number; link_img: string; categoria: string; nombre?: string }>
  >([]);
  const [bibliotecaLoading, setBibliotecaLoading] = useState<boolean>(false);
  const [bibliotecaActiveCategoria, setBibliotecaActiveCategoria] =
    useState<string>("");
  const [bibliotecaSelectedFondoId, setBibliotecaSelectedFondoId] = useState<
    number | null
  >(() => {
    const id = params.get("escenario_fondo_id");
    return id ? Number(id) : null;
  });
  const [bibliotecaSelectedFondoUrl, setBibliotecaSelectedFondoUrl] =
    useState<string>(() => params.get("escenario_fondo_url") || "");
  // Escenas Guardadas menu states
  const [showEscenasGuardadasMenu, setShowEscenasGuardadasMenu] =
    useState<boolean>(false);
  const [escenasGuardadasMenuAnimated, setEscenasGuardadasMenuAnimated] =
    useState<boolean>(false);
  const [escenasGuardadasMenuDragY, setEscenasGuardadasMenuDragY] =
    useState<number>(0);
  const escenasGuardadasDragStartY = useRef<number | null>(null);
  const [savedScenes, setSavedScenes] = useState<
    Array<{
      id: string;
      nombre: string | null;
      nombre_archivo: string;
      signedUrl?: string;
    }>
  >([]);
  const [savedScenesLoading, setSavedScenesLoading] = useState<boolean>(false);
  const [selectedSavedSceneId, setSelectedSavedSceneId] = useState<
    string | null
  >(() => params.get("escena_guardada_id") || null);
  const escenaGuardadaSnapshot = useRef<string | null>(null);
  const [showAddSceneForm, setShowAddSceneForm] = useState<boolean>(false);
  const [addSceneName, setAddSceneName] = useState<string>("");
  const [addSceneFile, setAddSceneFile] = useState<File | null>(null);
  const [addScenePreviewUrl, setAddScenePreviewUrl] = useState<string | null>(
    null,
  );
  const [addSceneSaving, setAddSceneSaving] = useState<boolean>(false);
  const [addSceneError, setAddSceneError] = useState<string | null>(null);
  const [openSceneMenuId, setOpenSceneMenuId] = useState<string | null>(null);
  const [showTextoMenu, setShowTextoMenu] = useState<boolean>(false);
  const [textoMenuAnimated, setTextoMenuAnimated] = useState<boolean>(false);
  const [textoMenuDragY, setTextoMenuDragY] = useState<number>(0);
  const textoDragStartY = useRef<number | null>(null);
  const [textoComposiciones, setTextoComposiciones] = useState<
    Array<{ id: number; link_img: string; categoria: string; nombre?: string }>
  >([]);
  const [textoLoading, setTextoLoading] = useState<boolean>(false);
  const [textoActiveCategoria, setTextoActiveCategoria] = useState<string>("");
  const [textoSelectedCompId, setTextoSelectedCompId] = useState<number | null>(
    () => {
      const id = params.get("texto_comp_id");
      return id ? Number(id) : null;
    },
  );
  const [textoSelectedCompUrl, setTextoSelectedCompUrl] = useState<string>(
    () => params.get("texto_comp_url") || "",
  );
  const [textoText, setTextoText] = useState<string>(
    () => params.get("texto_texto") || "",
  );
  const [brandAnalysis, setBrandAnalysis] = useState<string | null>(null);
  const [activeBrandName, setActiveBrandName] = useState<string | null>(null);
  // Instrucción adicional menu states
  const [instruccionAdicional, setInstruccionAdicional] = useState<string>("");
  const [showInstruccionMenu, setShowInstruccionMenu] =
    useState<boolean>(false);
  const [instruccionMenuAnimated, setInstruccionMenuAnimated] =
    useState<boolean>(false);
  const [instruccionMenuDragY, setInstruccionMenuDragY] = useState<number>(0);
  const instruccionDragStartY = useRef<number | null>(null);
  const instruccionSnapshot = useRef<string>("");

  const [creativoPrompt, setCreativoPrompt] = useState<string>("");
  const [creativoPromptFromIdea, setCreativoPromptFromIdea] =
    useState<boolean>(false);
  const [creativoIdeas, setCreativoIdeas] = useState<
    Array<{ titulo: string; prompt: string }>
  >([]);
  const [creativoIdeasLoading, setCreativoIdeasLoading] =
    useState<boolean>(false);
  const [showCreativoIdeasMenu, setShowCreativoIdeasMenu] =
    useState<boolean>(false);
  const [creativoIdeasMenuAnimated, setCreativoIdeasMenuAnimated] =
    useState<boolean>(false);
  const [creativoIdeasMenuDragY, setCreativoIdeasMenuDragY] =
    useState<number>(0);
  const creativoIdeasDragStartY = useRef<number | null>(null);
  const [creativoHistorial, setCreativoHistorial] = useState<string[]>([]);
  const [showCreativoHistorialMenu, setShowCreativoHistorialMenu] =
    useState<boolean>(false);
  const [creativoHistorialMenuAnimated, setCreativoHistorialMenuAnimated] =
    useState<boolean>(false);
  const [creativoHistorialMenuDragY, setCreativoHistorialMenuDragY] =
    useState<number>(0);
  const creativoHistorialDragStartY = useRef<number | null>(null);

  // Snapshot refs for cancel/restore on menu close
  const poseSnapshot = useRef<string[]>([]);
  const vistaProductoSnapshot = useRef<string>("");
  const bibliotecaSnapshot = useRef<{
    fondoId: number | null;
    fondoUrl: string;
  }>({ fondoId: null, fondoUrl: "" });
  const modeloGuardadoSnapshot = useRef<number | null>(null);
  const modeloPersonalizadoSnapshot = useRef<{
    genero: string;
    edad: string;
    tipoCuerpo: string;
    tonoPiel: string;
    colorPelo: string;
    largoPelo: string;
    descripcion: string;
  }>({
    genero: "",
    edad: "",
    tipoCuerpo: "",
    tonoPiel: "",
    colorPelo: "",
    largoPelo: "",
    descripcion: "",
  });

  // Modelo Personalizado menu states
  const [showModeloPersonalizadoMenu, setShowModeloPersonalizadoMenu] =
    useState<boolean>(false);
  const [modeloPersonalizadoMenuAnimated, setModeloPersonalizadoMenuAnimated] =
    useState<boolean>(false);
  const [modeloPersonalizadoMenuDragY, setModeloPersonalizadoMenuDragY] =
    useState<number>(0);
  const modeloPersonalizadoDragStartY = useRef<number | null>(null);
  const [modeloGenero, setModeloGenero] = useState<string>(
    () => params.get("genero") || "",
  );
  const [modeloEdad, setModeloEdad] = useState<string>(
    () => params.get("edad") || "",
  );
  const [modeloTipoCuerpo, setModeloTipoCuerpo] = useState<string>(
    () => params.get("tipo_cuerpo") || "",
  );
  const [modeloTonoPiel, setModeloTonoPiel] = useState<string>(
    () => params.get("tono_piel") || "",
  );
  const [modeloColorPelo, setModeloColorPelo] = useState<string>(
    () => params.get("color_pelo") || "",
  );
  const [modeloLargoPelo, setModeloLargoPelo] = useState<string>(
    () => params.get("largo_pelo") || "",
  );
  const [modeloDescripcion, setModeloDescripcion] = useState<string>(
    () => params.get("desc_modelo") || "",
  );

  // Modelo Guardado menu states
  const [showModeloGuardadoMenu, setShowModeloGuardadoMenu] =
    useState<boolean>(false);
  const [modeloGuardadoMenuAnimated, setModeloGuardadoMenuAnimated] =
    useState<boolean>(false);
  const [modeloGuardadoMenuDragY, setModeloGuardadoMenuDragY] =
    useState<number>(0);
  const modeloGuardadoDragStartY = useRef<number | null>(null);
  const [savedModels, setSavedModels] = useState<
    Array<{ id: number; nombre: string; signedUrl?: string }>
  >([]);
  const [savedModelsLoading, setSavedModelsLoading] = useState<boolean>(false);
  const [selectedSavedModelId, setSelectedSavedModelId] = useState<
    number | null
  >(() => {
    const id = params.get("modelo_guardado_id");
    return id ? Number(id) : null;
  });
  const [showAddModelForm, setShowAddModelForm] = useState<
    false | "chooser" | "real" | "ia" | "historial"
  >(false);
  const [addModelName, setAddModelName] = useState<string>("");
  const [addModelFile, setAddModelFile] = useState<File | null>(null);
  const [addModelPreviewUrl, setAddModelPreviewUrl] = useState<string | null>(
    null,
  );
  const [addModelConsentA, setAddModelConsentA] = useState<boolean>(false);
  const [addModelConsentB, setAddModelConsentB] = useState<boolean>(false);
  const [addModelSaving, setAddModelSaving] = useState<boolean>(false);
  const [addModelError, setAddModelError] = useState<string | null>(null);
  const [convertingHeic, setConvertingHeic] = useState<boolean>(false);
  const [openModelMenuId, setOpenModelMenuId] = useState<number | null>(null);
  const [deletingModelId, setDeletingModelId] = useState<number | null>(null);
  // AI model form state
  const [aiModelName, setAiModelName] = useState<string>("");
  const [aiModelGenero, setAiModelGenero] = useState<string>("");
  const [aiModelEdad, setAiModelEdad] = useState<string>("");
  const [aiModelTipoCuerpo, setAiModelTipoCuerpo] = useState<string>("");
  const [aiModelTonoPiel, setAiModelTonoPiel] = useState<string>("");
  const [aiModelColorPelo, setAiModelColorPelo] = useState<string>("");
  const [aiModelLargoPelo, setAiModelLargoPelo] = useState<string>("");
  const [aiModelDescripcion, setAiModelDescripcion] = useState<string>("");
  const [aiModelGenerating, setAiModelGenerating] = useState<boolean>(false);
  const [aiModelResultB64, setAiModelResultB64] = useState<string | null>(null);
  const [aiModelSaving, setAiModelSaving] = useState<boolean>(false);
  const [aiModelError, setAiModelError] = useState<string | null>(null);

  // Historial (desde imagen generada) state
  type GenImageItem = {
    id: string;
    fileName: string;
    thumbUrl: string;
    fullUrl: string;
  };
  const [genImages, setGenImages] = useState<GenImageItem[]>([]);
  const [genLoading, setGenLoading] = useState(false);
  const [genLoadingMore, setGenLoadingMore] = useState(false);
  const [historialError, setHistorialError] = useState<string | null>(null);
  const [genHasMore, setGenHasMore] = useState(true);
  const [genPage, setGenPage] = useState(0);
  const GEN_PAGE_SIZE = 32;
  const genContainerRefDesktop = useRef<HTMLDivElement | null>(null);
  const genContainerRefMobile = useRef<HTMLDivElement | null>(null);
  const genLoadMoreRef = useRef<HTMLDivElement | null>(null);
  const genLoadInFlightRef = useRef<boolean>(false);
  const [selectedGenImage, setSelectedGenImage] = useState<GenImageItem | null>(
    null,
  );
  const [savingModelFromImage, setSavingModelFromImage] = useState(false);
  const [historialSuccess, setHistorialSuccess] = useState(false);

  // Pose menu states
  type PoseOption = {
    id: string;
    name: string;
    image: string;
    areas_principales: string[];
    areas_secundarias: string[];
    categoria: string;
  };
  const POSE_BASE_URL =
    "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/poses";
  const [poseOptions, setPoseOptions] = useState<PoseOption[]>([]);
  const [showPoseMenu, setShowPoseMenu] = useState<boolean>(false);
  const [poseActiveTab, setPoseActiveTab] = useState<string>("recomendado");
  const [poseMenuAnimated, setPoseMenuAnimated] = useState<boolean>(false);
  const [poseMenuDragY, setPoseMenuDragY] = useState<number>(0);
  const poseDragStartY = useRef<number | null>(null);
  const [selectedPoses, setSelectedPoses] = useState<string[]>(() => {
    const p = params.get("pose") || "";
    return p ? p.split(",").filter(Boolean) : [];
  });
  const [multiPoseToggle, setMultiPoseToggle] = useState<boolean>(false);

  // Rubro state (for conditional UI like Pose)
  const [isFashionRubro, setIsFashionRubro] = useState<boolean>(true);
  const [userRubro, setUserRubro] = useState<string>("Moda e Indumentaria");
  const [userPlan, setUserPlan] = useState<string | null>(null);

  // Escena personalizada: historial + mejorar
  const [escenaHistorial, setEscenaHistorial] = useState<string[]>([]);
  const [mejorandoEscena, setMejorandoEscena] = useState<boolean>(false);
  const [escenaVisibleCount, setEscenaVisibleCount] = useState<number>(4);
  const [escenaGeneratedIdeas, setEscenaGeneratedIdeas] = useState<string[]>(
    [],
  );

  // Swipe-to-delete state for product rows
  const [swipeX, setSwipeX] = useState<Record<string, number>>({});
  const [swipeDismissing, setSwipeDismissing] = useState<string | null>(null);
  const [productMenuOpen, setProductMenuOpen] = useState<string | null>(null);
  const swipeStartX = useRef<Record<string, number>>({});
  const swipeStartY = useRef<Record<string, number>>({});
  const swipeLocked = useRef<Record<string, boolean>>({});

  const handleRemoveProduct = (variantId: string) => {
    setSwipeDismissing(variantId);
    setTimeout(() => {
      const newIds = ids.filter((id) => id !== variantId);
      const qp = new URLSearchParams(location.search);
      if (newIds.length > 0) {
        qp.set("ids", newIds.join(","));
      } else {
        qp.delete("ids");
      }
      navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
      mergeLastCrearAnuncioState({ selectedVariantIds: newIds });
      setSwipeDismissing(null);
      setSwipeX((prev) => {
        const n = { ...prev };
        delete n[variantId];
        return n;
      });
    }, 300);
  };

  // All selected products' model_view values (for pose recommendations)
  const [productModelViews, setProductModelViews] = useState<string[]>([]);

  // Vista del producto states (when modelo is off and single product)
  const [productModelView, setProductModelView] = useState<string | null>(null);
  const [selectedVistaProducto, setSelectedVistaProducto] = useState<string>(
    () => params.get("vista_producto") || "",
  );
  // Multi-vista (solo para zapatillas)
  const [selectedVistas, setSelectedVistas] = useState<string[]>(() => {
    const raw = params.get("vista_producto") || "";
    return raw ? raw.split(",").filter(Boolean) : [];
  });
  const [multiVistaToggle, setMultiVistaToggle] = useState<boolean>(false);
  const [showVistaProductoMenu, setShowVistaProductoMenu] =
    useState<boolean>(false);
  const [vistaProductoMenuAnimated, setVistaProductoMenuAnimated] =
    useState<boolean>(false);
  const [vistaProductoMenuDragY, setVistaProductoMenuDragY] =
    useState<number>(0);
  const vistaProductoDragStartY = useRef<number | null>(null);

  // Vista options based on model_view
  const getVistaOptions = () => {
    console.log(
      "[PruebaNuevaInterfaz] getVistaOptions called with productModelView:",
      productModelView,
    );
    // Fallback: when model_view is missing, show frente/posterior (same as clothing)
    if (!productModelView) {
      return [
        { id: "frente", name: "Frente" },
        { id: "posterior", name: "Posterior" },
      ];
    }
    // Clothing products (torso, piernas, cuerpo-completo, cuerpo_completo)
    // — para ropa renombramos "Frente/Posterior" a "Plano frente/posterior"
    // y sumamos las vistas de maniquí invisible (sin modelo, prenda
    // sostenida en el aire por ghost-mannequin) + la prenda doblada. Todas
    // tienen thumbnails de referencia en `/images/multi-vistas/otros/`.
    // Espejo de `VISTAS_SOLO_PRODUCTO` (v2/vistasCatalogo.ts): si agregás una
    // vista de ropa acá, agregala allá también — y al revés.
    if (
      productModelView === "torso" ||
      productModelView === "piernas" ||
      productModelView === "cuerpo-completo" ||
      productModelView === "cuerpo_completo"
    ) {
      const base = "/images/multi-vistas/otros";
      return [
        { id: "frente", name: "Plano frente", image: `${base}/plano_delantero.jpeg` },
        { id: "posterior", name: "Plano posterior", image: `${base}/plano_trasero.jpeg` },
        { id: "maniqui_frontal", name: "Maniquí invisible frontal", image: `${base}/maniqui_frente.jpeg` },
        { id: "maniqui_trasero", name: "Maniquí invisible trasero", image: `${base}/maniqui_trasero.jpeg` },
        { id: "maniqui_angulo", name: "Maniquí invisible ángulo", image: `${base}/maniqui_angulo.jpeg` },
        // Maniquí femenino: torso y cuerpo completo (vestidos, monos). En
        // `piernas` queda afuera: en un jean o una pollera no agrega nada
        // sobre el maniquí neutro.
        ...(productModelView === "torso" ||
        productModelView === "cuerpo-completo" ||
        productModelView === "cuerpo_completo"
          ? [
              { id: "maniqui_mujer_frontal", name: "Maniquí femenino frontal", image: `${base}/maniqui_mujer_frente.jpeg` },
              { id: "maniqui_mujer_angulo", name: "Maniquí femenino ángulo", image: `${base}/maniqui_mujer_angulo.jpeg` },
            ]
          : []),
        { id: "prenda_doblada", name: "Prenda doblada", image: `${base}/prenda_doblada.jpeg` },
      ];
    }
    // Calzado and cabeza - no automatica
    if (productModelView === "calzado" || productModelView === "cabeza") {
      const base = "/images/multi-vistas/zapatillas";
      return [
        { id: "lateral", name: "Lateral", image: `${base}/lateral.webp` },
        { id: "frente", name: "Frente", image: `${base}/frente.webp` },
        { id: "posterior", name: "Posterior", image: `${base}/posterior.webp` },
        { id: "superior", name: "Superior", image: `${base}/superior.webp` },
        { id: "perspectiva", name: "Perspectiva", image: `${base}/perspectiva.webp` },
      ];
    }
    // Other non-clothing products - includes automatica
    return [
      { id: "automatica", name: "Automática" },
      { id: "lateral", name: "Lateral" },
      { id: "frente", name: "Frente" },
      { id: "posterior", name: "Posterior" },
      { id: "superior", name: "Superior" },
      { id: "perspectiva", name: "Perspectiva" },
    ];
  };

  const [showBrandAnalysisMenu, setShowBrandAnalysisMenu] =
    useState<boolean>(false);
  const [brandAnalysisMenuAnimated, setBrandAnalysisMenuAnimated] =
    useState<boolean>(false);
  const [brandAnalysisMenuDragY, setBrandAnalysisMenuDragY] =
    useState<number>(0);
  const brandAnalysisDragStartY = useRef<number | null>(null);

  // Fetch pro_model_active from config_system_atlas on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase
          .from("config_system_atlas")
          .select("pro_model_active")
          .eq("id", 1)
          .single();
        if (data && typeof (data as any).pro_model_active === "boolean") {
          setProModelActive((data as any).pro_model_active);
        }
      } catch {}
    })();
  }, []);

  // Trigger animation after menu mounts
  useEffect(() => {
    if (showFormatoMenu) {
      requestAnimationFrame(() => setFormatoMenuAnimated(true));
    }
  }, [showFormatoMenu]);

  useEffect(() => {
    if (showEscenaMenu) {
      requestAnimationFrame(() => setEscenaMenuAnimated(true));
      fetchEscenaHistorial();
      setEscenaVisibleCount(4);
      setEscenaGeneratedIdeas([]);
    }
  }, [showEscenaMenu]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showBibliotecaMenu) {
      requestAnimationFrame(() => setBibliotecaMenuAnimated(true));
    }
  }, [showBibliotecaMenu]);

  useEffect(() => {
    if (showEscenasGuardadasMenu) {
      requestAnimationFrame(() => setEscenasGuardadasMenuAnimated(true));
    }
  }, [showEscenasGuardadasMenu]);

  useEffect(() => {
    if (showTextoMenu) {
      requestAnimationFrame(() => setTextoMenuAnimated(true));
    }
  }, [showTextoMenu]);

  useEffect(() => {
    if (showBrandAnalysisMenu) {
      requestAnimationFrame(() => setBrandAnalysisMenuAnimated(true));
    }
  }, [showBrandAnalysisMenu]);

  useEffect(() => {
    if (showCreativoIdeasMenu) {
      requestAnimationFrame(() => setCreativoIdeasMenuAnimated(true));
    }
  }, [showCreativoIdeasMenu]);

  useEffect(() => {
    if (showCreativoHistorialMenu) {
      requestAnimationFrame(() => setCreativoHistorialMenuAnimated(true));
      fetchCreativoHistorial();
    }
  }, [showCreativoHistorialMenu]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showInstruccionMenu) {
      requestAnimationFrame(() => setInstruccionMenuAnimated(true));
    }
  }, [showInstruccionMenu]);

  useEffect(() => {
    if (showModeloPersonalizadoMenu) {
      requestAnimationFrame(() => setModeloPersonalizadoMenuAnimated(true));
    }
  }, [showModeloPersonalizadoMenu]);

  useEffect(() => {
    if (showModeloGuardadoMenu) {
      requestAnimationFrame(() => setModeloGuardadoMenuAnimated(true));
    }
  }, [showModeloGuardadoMenu]);

  useEffect(() => {
    const fetchPoses = async () => {
      try {
        // Filtramos por cantidad_personas = 1 porque esta pantalla es
        // single-modelo. Las poses multi-persona viven en la Interfaz V2
        // (modo varios modelos).
        const { data, error } = await supabase
          .from("poses")
          .select(
            "id, nombre, filename, categoria, areas_principales, areas_secundarias",
          )
          .eq("cantidad_personas", 1)
          .order("created_at");
        if (error || !data) return;
        const mapped: PoseOption[] = data.map((p: any) => ({
          id: p.id,
          name: p.nombre,
          image: p.filename
            ? `${POSE_BASE_URL}/${encodeURIComponent(p.filename)}`
            : "",
          areas_principales: Array.isArray(p.areas_principales)
            ? p.areas_principales
            : [],
          areas_secundarias: Array.isArray(p.areas_secundarias)
            ? p.areas_secundarias
            : [],
          categoria: p.categoria || "",
        }));
        setPoseOptions(mapped);
        // Set default pose if none selected yet
        if (selectedPoses.length === 0 && mapped.length > 0) {
          setSelectedPoses([mapped[0].id]);
        }
      } catch (err) {
        console.error("Error fetching poses:", err);
      }
    };
    fetchPoses();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showPoseMenu) {
      requestAnimationFrame(() => setPoseMenuAnimated(true));
    }
  }, [showPoseMenu]);

  useEffect(() => {
    if (showVistaProductoMenu) {
      requestAnimationFrame(() => setVistaProductoMenuAnimated(true));
    }
  }, [showVistaProductoMenu]);

  const tabOrder: Array<"automatico" | "personalizado" | "creativo"> = [
    "automatico",
    "personalizado",
    "creativo",
  ];
  const switchTab = (newTab: "automatico" | "personalizado" | "creativo") => {
    if (newTab === activeTab) return;
    const oldIdx = tabOrder.indexOf(activeTab);
    const newIdx = tabOrder.indexOf(newTab);
    setTabSlideDir(newIdx > oldIdx ? "left" : "right");
    prevTabRef.current = activeTab;
    setActiveTab(newTab);
    // Clear direction after animation completes (350ms = Apple iOS transition duration)
    setTimeout(() => setTabSlideDir(null), 350);
  };

  // Close menu with slide-out animation
  const closeFormatoMenu = () => {
    setFormatoMenuAnimated(false);
    setTimeout(() => setShowFormatoMenu(false), 300);
  };

  const closeEscenaMenu = () => {
    setEscenaMenuAnimated(false);
    setTimeout(() => setShowEscenaMenu(false), 300);
  };

  const closeBibliotecaMenu = () => {
    // Restore snapshot (discard unsaved changes)
    setBibliotecaSelectedFondoId(bibliotecaSnapshot.current.fondoId);
    setBibliotecaSelectedFondoUrl(bibliotecaSnapshot.current.fondoUrl);
    setBibliotecaMenuAnimated(false);
    setTimeout(() => setShowBibliotecaMenu(false), 300);
  };

  const closeEscenasGuardadasMenu = () => {
    setSelectedSavedSceneId(escenaGuardadaSnapshot.current);
    setShowAddSceneForm(false);
    resetAddSceneState();
    setEscenasGuardadasMenuAnimated(false);
    setTimeout(() => setShowEscenasGuardadasMenu(false), 300);
  };

  const closeTextoMenu = () => {
    setTextoMenuAnimated(false);
    setTimeout(() => setShowTextoMenu(false), 300);
  };

  const closeBrandAnalysisMenu = () => {
    setBrandAnalysisMenuAnimated(false);
    setTimeout(() => setShowBrandAnalysisMenu(false), 300);
  };

  const closeCreativoIdeasMenu = () => {
    setCreativoIdeasMenuAnimated(false);
    setTimeout(() => setShowCreativoIdeasMenu(false), 300);
  };

  const closeCreativoHistorialMenu = () => {
    setCreativoHistorialMenuAnimated(false);
    setTimeout(() => setShowCreativoHistorialMenu(false), 300);
  };

  const closeInstruccionMenu = () => {
    setInstruccionAdicional(instruccionSnapshot.current);
    setInstruccionMenuAnimated(false);
    setTimeout(() => setShowInstruccionMenu(false), 300);
  };

  const closeModeloPersonalizadoMenu = () => {
    // Restore snapshot (discard unsaved changes)
    const snap = modeloPersonalizadoSnapshot.current;
    setModeloGenero(snap.genero);
    setModeloEdad(snap.edad);
    setModeloTipoCuerpo(snap.tipoCuerpo);
    setModeloTonoPiel(snap.tonoPiel);
    setModeloColorPelo(snap.colorPelo);
    setModeloLargoPelo(snap.largoPelo);
    setModeloDescripcion(snap.descripcion);
    setModeloPersonalizadoMenuAnimated(false);
    setTimeout(() => setShowModeloPersonalizadoMenu(false), 300);
  };

  const closeModeloGuardadoMenu = () => {
    // Restore snapshot (discard unsaved changes)
    setSelectedSavedModelId(modeloGuardadoSnapshot.current);
    setModeloGuardadoMenuAnimated(false);
    setTimeout(() => setShowModeloGuardadoMenu(false), 300);
  };

  const handleModeloPersonalizadoTouchStart = (e: React.TouchEvent) => {
    modeloPersonalizadoDragStartY.current = e.touches[0].clientY;
  };
  const handleModeloPersonalizadoTouchMove = (e: React.TouchEvent) => {
    if (modeloPersonalizadoDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - modeloPersonalizadoDragStartY.current;
    if (deltaY > 0) setModeloPersonalizadoMenuDragY(deltaY);
  };
  const handleModeloPersonalizadoTouchEnd = () => {
    if (modeloPersonalizadoMenuDragY > 100) {
      closeModeloPersonalizadoMenu();
    }
    setModeloPersonalizadoMenuDragY(0);
    modeloPersonalizadoDragStartY.current = null;
  };

  const handleModeloGuardadoTouchStart = (e: React.TouchEvent) => {
    modeloGuardadoDragStartY.current = e.touches[0].clientY;
  };
  const handleModeloGuardadoTouchMove = (e: React.TouchEvent) => {
    if (modeloGuardadoDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - modeloGuardadoDragStartY.current;
    if (deltaY > 0) setModeloGuardadoMenuDragY(deltaY);
  };
  const handleModeloGuardadoTouchEnd = () => {
    if (modeloGuardadoMenuDragY > 100) {
      closeModeloGuardadoMenu();
    }
    setModeloGuardadoMenuDragY(0);
    modeloGuardadoDragStartY.current = null;
  };

  const closePoseMenu = () => {
    // Restore snapshot (discard unsaved changes)
    setSelectedPoses(poseSnapshot.current);
    setPoseActiveTab("recomendado");
    setPoseMenuAnimated(false);
    setTimeout(() => setShowPoseMenu(false), 300);
  };

  const closeVistaProductoMenu = () => {
    // Restore snapshot (discard unsaved changes)
    setSelectedVistaProducto(vistaProductoSnapshot.current);
    setVistaProductoMenuAnimated(false);
    setTimeout(() => setShowVistaProductoMenu(false), 300);
  };

  const handleVistaProductoTouchStart = (e: React.TouchEvent) => {
    vistaProductoDragStartY.current = e.touches[0].clientY;
  };
  const handleVistaProductoTouchMove = (e: React.TouchEvent) => {
    if (vistaProductoDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - vistaProductoDragStartY.current;
    if (deltaY > 0) setVistaProductoMenuDragY(deltaY);
  };
  const handleVistaProductoTouchEnd = () => {
    if (vistaProductoMenuDragY > 100) {
      closeVistaProductoMenu();
    }
    setVistaProductoMenuDragY(0);
    vistaProductoDragStartY.current = null;
  };

  const handleVistaProductoSelect = (vistaId: string) => {
    setSelectedVistaProducto(vistaId);
  };

  const handleVistaProductoSubmit = () => {
    // Multi-vista: guardar el array como CSV en URL
    if (multiVistaActive) {
      if (selectedVistas.length === 0) return;
      const qp = new URLSearchParams(location.search);
      qp.set("vista_producto", selectedVistas.join(","));
      navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
      setVistaProductoMenuAnimated(false);
      setTimeout(() => setShowVistaProductoMenu(false), 300);
      return;
    }
    // Single: comportamiento original
    if (!selectedVistaProducto) return;
    const qp = new URLSearchParams(location.search);
    qp.set("vista_producto", selectedVistaProducto);
    navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
    setVistaProductoMenuAnimated(false);
    setTimeout(() => setShowVistaProductoMenu(false), 300);
  };

  const handlePoseTouchStart = (e: React.TouchEvent) => {
    poseDragStartY.current = e.touches[0].clientY;
  };
  const handlePoseTouchMove = (e: React.TouchEvent) => {
    if (poseDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - poseDragStartY.current;
    if (deltaY > 0) setPoseMenuDragY(deltaY);
  };
  const handlePoseTouchEnd = () => {
    if (poseMenuDragY > 100) {
      closePoseMenu();
    }
    setPoseMenuDragY(0);
    poseDragStartY.current = null;
  };

  const handlePoseSubmit = () => {
    if (selectedPoses.length === 0) return;
    const qp = new URLSearchParams(location.search);
    if (selectedPoses.length > 0) {
      qp.set("pose", selectedPoses.join(","));
    } else {
      qp.delete("pose");
    }
    navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
    setPoseActiveTab("recomendado");
    setPoseMenuAnimated(false);
    setTimeout(() => setShowPoseMenu(false), 300);
  };

  const multiPoseActive =
    multiPoseToggle &&
    selectedPoses.length > 1 &&
    activeTab === "personalizado" &&
    modeloEnabled;

  const handlePoseClick = (poseId: string) => {
    setSelectedPoses((prev) => {
      const isSelected = prev.includes(poseId);
      let next: string[];
      if (multiPoseToggle) {
        next = isSelected
          ? prev.filter((p) => p !== poseId)
          : [...prev, poseId];
      } else {
        next = isSelected ? [] : [poseId];
      }
      return next;
    });
  };

  const handleMultiPoseToggle = (enabled: boolean) => {
    setMultiPoseToggle(enabled);
    if (!enabled && selectedPoses.length > 1) {
      // Keep only the first selected pose
      setSelectedPoses((prev) => [prev[0]]);
    }
  };
  // Multi-vista activo si: toggle ON, hay más de 1 vista (cualquier producto)
  const multiVistaActive = multiVistaToggle && selectedVistas.length > 1;

  const handleVistaProductoClick = (vistaId: string) => {
    if (multiVistaToggle) {
      setSelectedVistas((prev) => {
        const isSelected = prev.includes(vistaId);
        return isSelected
          ? prev.filter((v) => v !== vistaId)
          : [...prev, vistaId];
      });
    } else {
      setSelectedVistaProducto(vistaId);
      setSelectedVistas([vistaId]);
    }
  };

  const handleMultiVistaToggle = (enabled: boolean) => {
    setMultiVistaToggle(enabled);
    if (enabled) {
      // Al activar multi-vista: descartar "automatica" si estaba seleccionada
      if (selectedVistaProducto === "automatica") {
        setSelectedVistaProducto("");
      }
      setSelectedVistas((prev) => prev.filter((v) => v !== "automatica"));
    } else if (selectedVistas.length > 1) {
      // Al desactivar: mantener solo la primera vista seleccionada
      setSelectedVistas((prev) => [prev[0]]);
      setSelectedVistaProducto(selectedVistas[0] || "");
    }
  };

  const effectiveImageCount = multiVistaActive
    ? selectedVistas.length
    : multiPoseActive
      ? selectedPoses.length
      : imageCount;

  // Pose category tabs and filtering
  const POSE_TABS = [
    { key: "recomendado", label: "Recomendado" },
    { key: "cuerpo_completo", label: "Cuerpo completo" },
    { key: "plano_medio", label: "Plano medio" },
    { key: "detalle_close-up", label: "Detalle" },
  ];

  // 'cuerpo_completo' maps to requiring both 'torso' and 'piernas' in areas_principales
  const expandModelView = (mv: string): string[] => {
    if (mv === "cuerpo_completo") return ["torso", "piernas"];
    return [mv];
  };

  const recommendedPoses = useMemo(() => {
    if (productModelViews.length === 0) return poseOptions;
    if (productModelViews.length === 1) {
      const expandedMvs = expandModelView(productModelViews[0]);
      const rec = poseOptions.filter((pose) =>
        expandedMvs.every((mv) => pose.areas_principales.includes(mv)),
      );
      return rec.length > 0 ? rec : poseOptions;
    }
    const rec = poseOptions.filter((pose) => {
      const allAreas = [...pose.areas_principales, ...pose.areas_secundarias];
      return productModelViews.every((mv) =>
        expandModelView(mv).every((part) => allAreas.includes(part)),
      );
    });
    return rec.length > 0 ? rec : poseOptions;
  }, [poseOptions, productModelViews]);

  const filteredPoses = useMemo(() => {
    if (poseActiveTab === "recomendado") return recommendedPoses;
    return poseOptions.filter((p) => p.categoria === poseActiveTab);
  }, [poseActiveTab, recommendedPoses, poseOptions]);

  // Track which tabs have at least one selected pose (for dot indicator)
  const poseTabsWithSelection = useMemo(() => {
    const set = new Set<string>();
    const recIds = new Set(recommendedPoses.map((p) => p.id));
    for (const id of selectedPoses) {
      if (recIds.has(id)) set.add("recomendado");
      const pose = poseOptions.find((p) => p.id === id);
      if (pose?.categoria) set.add(pose.categoria);
    }
    return set;
  }, [selectedPoses, recommendedPoses, poseOptions]);

  // Skin tone and hair color options for modelo personalizado
  const skinToneOptions = [
    { label: "Muy clara", color: "#FFDFC4" },
    { label: "Clara", color: "#F0C8A0" },
    { label: "Beige", color: "#DEB887" },
    { label: "Trigueña", color: "#CD853F" },
    { label: "Oscura", color: "#8B4513" },
    { label: "Muy oscura", color: "#3C2414" },
  ];
  const hairColorOptions = [
    { label: "Negro", color: "#232323" },
    { label: "Castaño oscuro", color: "#4B2E09" },
    { label: "Castaño claro", color: "#A0522D" },
    { label: "Rubio", color: "#F8E49B" },
    { label: "Colorado", color: "#B55239" },
  ];

  // Fetch saved models
  const fetchSavedModels = async () => {
    try {
      setSavedModelsLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      const idCompany = (profile as any)?.id_company;
      if (!idCompany) return;

      const { data, error } = await supabase
        .from("modelos")
        .select("id, nombre, foto_file_name")
        .eq("id_company", idCompany)
        .order("nombre");
      if (error) throw error;

      const rows = (data as any[]) || [];
      const withSigned = await Promise.all(
        rows.map(async (m) => {
          let signedUrl: string | undefined;
          const fileName = m?.foto_file_name;
          if (fileName) {
            try {
              const { data: signed } = await supabase.storage
                .from("modelos")
                .createSignedUrl(`${idCompany}/full/${fileName}`, 3600);
              if (signed?.signedUrl) signedUrl = signed.signedUrl;
            } catch {}
          }
          return { id: m.id as number, nombre: m.nombre as string, signedUrl };
        }),
      );
      setSavedModels(withSigned);
    } catch (e) {
      console.error(e);
    } finally {
      setSavedModelsLoading(false);
    }
  };

  const handleModeloPersonalizadoSubmit = () => {
    const qp = new URLSearchParams(location.search);
    qp.set("modelo_mode", "personalizado");
    if (modeloGenero.trim()) qp.set("genero", modeloGenero.trim());
    if (modeloEdad.trim()) qp.set("edad", modeloEdad.trim());
    if (modeloTipoCuerpo.trim()) qp.set("tipo_cuerpo", modeloTipoCuerpo.trim());
    if (modeloTonoPiel.trim()) qp.set("tono_piel", modeloTonoPiel.trim());
    if (modeloColorPelo.trim()) qp.set("color_pelo", modeloColorPelo.trim());
    if (modeloLargoPelo.trim()) qp.set("largo_pelo", modeloLargoPelo.trim());
    if (modeloDescripcion.trim())
      qp.set("desc_modelo", modeloDescripcion.trim());
    navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
    setSelectedModeloOption("personalizado");
    setModeloPersonalizadoMenuAnimated(false);
    setTimeout(() => setShowModeloPersonalizadoMenu(false), 300);
  };

  const handleModeloGuardadoSelect = (modelId: number | null) => {
    setSelectedSavedModelId(modelId);
  };

  const handleModeloGuardadoSubmit = () => {
    if (selectedSavedModelId === null) return;
    const qp = new URLSearchParams(location.search);
    const selected = savedModels.find((m) => m.id === selectedSavedModelId);
    qp.set("modelo_mode", "guardados");
    qp.set("modelo_guardado_id", String(selectedSavedModelId));
    if (selected?.nombre) qp.set("modelo_guardado_nombre", selected.nombre);
    navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
    setSelectedModeloOption("anteriores");
    setModeloGuardadoMenuAnimated(false);
    setTimeout(() => setShowModeloGuardadoMenu(false), 300);
  };

  const handleModelFileSelect = async (file: File) => {
    // Formatos bloqueados (RAW/TIFF/GIF/SVG — ADR-590)
    if (isUnsupportedUploadFile(file)) {
      setAddModelError(UNSUPPORTED_FORMAT_MESSAGE);
      return;
    }
    if (isHeicFile(file) || isWebpFile(file)) {
      try {
        setConvertingHeic(true);
        const { file: converted, url } = await convertHeicToJpeg(file, 0.85);
        setAddModelFile(converted);
        setAddModelPreviewUrl(url);
        setAddModelError(null);
      } catch {
        setAddModelError(
          "No pudimos procesar la imagen. Probá con una imagen en formato PNG o JPG.",
        );
      } finally {
        setConvertingHeic(false);
      }
    } else {
      setAddModelFile(file);
      setAddModelPreviewUrl(URL.createObjectURL(file));
      setAddModelError(null);
    }
  };

  const resetAddModelState = () => {
    setAddModelName("");
    setAddModelFile(null);
    setAddModelPreviewUrl(null);
    setAddModelConsentA(false);
    setAddModelConsentB(false);
    setAddModelSaving(false);
    setAddModelError(null);
    setAiModelName("");
    setAiModelGenero("");
    setAiModelEdad("");
    setAiModelTipoCuerpo("");
    setAiModelTonoPiel("");
    setAiModelColorPelo("");
    setAiModelLargoPelo("");
    setAiModelDescripcion("");
    setAiModelGenerating(false);
    setAiModelResultB64(null);
    setAiModelSaving(false);
    setAiModelError(null);
    setSelectedGenImage(null);
    setSavingModelFromImage(false);
    setHistorialSuccess(false);
  };

  const loadGeneratedImages = async (isMore = false) => {
    if (genLoadInFlightRef.current) return;
    genLoadInFlightRef.current = true;
    try {
      setHistorialError(null);
      if (isMore) setGenLoadingMore(true);
      else setGenLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      const idCompany = (profile as any)?.id_company as string | undefined;
      if (!idCompany) throw new Error("No se encontró la empresa");
      const startIndex = isMore ? genPage * GEN_PAGE_SIZE : 0;
      const endIndex = startIndex + GEN_PAGE_SIZE - 1;
      const { data: imagesData, error } = await supabase
        .from("imagenes_generadas")
        .select("id, file_name, created_at, active, id_company")
        .eq("active", true)
        .eq("id_company", idCompany)
        .order("created_at", { ascending: false })
        .range(startIndex, endIndex);
      if (error) throw error;
      const processed: GenImageItem[] = await Promise.all(
        ((imagesData as any[]) || []).map(async (img) => {
          const optPath = `${idCompany}/opt/${img.file_name}`;
          const fullPath = `${idCompany}/full/${img.file_name}`;
          let thumbUrl = "";
          let fullUrl = "";
          try {
            const { data: optSigned } = await supabase.storage
              .from("generadas")
              .createSignedUrl(optPath, 3600);
            if (optSigned?.signedUrl) thumbUrl = optSigned.signedUrl;
            const { data: fullSigned } = await supabase.storage
              .from("generadas")
              .createSignedUrl(fullPath, 3600);
            if (fullSigned?.signedUrl) fullUrl = fullSigned.signedUrl;
            if (!thumbUrl && fullUrl) thumbUrl = fullUrl;
            if (!fullUrl && thumbUrl) fullUrl = thumbUrl;
          } catch {}
          return {
            id: String(img.id),
            fileName: img.file_name as string,
            thumbUrl,
            fullUrl,
          };
        }),
      );
      if (isMore) {
        setGenImages((prev) => {
          const merged = [...prev, ...processed];
          const seen = new Set<string>();
          const deduped: GenImageItem[] = [];
          for (const item of merged) {
            if (!seen.has(item.id)) {
              seen.add(item.id);
              deduped.push(item);
            }
          }
          return deduped;
        });
        setGenPage((prev) => prev + 1);
      } else {
        setGenImages(() => {
          const seen = new Set<string>();
          const deduped: GenImageItem[] = [];
          for (const item of processed) {
            if (!seen.has(item.id)) {
              seen.add(item.id);
              deduped.push(item);
            }
          }
          return deduped;
        });
        setGenPage(1);
      }
      setGenHasMore(((imagesData as any[]) || []).length === GEN_PAGE_SIZE);
    } catch (e: any) {
      console.error("Error cargando imágenes generadas:", e);
      setHistorialError(
        e?.message ||
          "Error al cargar imágenes. Por favor, volvé a intentarlo.",
      );
    } finally {
      setGenLoading(false);
      setGenLoadingMore(false);
      genLoadInFlightRef.current = false;
    }
  };

  const handleSaveFromGenerated = async () => {
    if (!selectedGenImage) return;
    try {
      setHistorialSuccess(false);
      setSavingModelFromImage(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");
      await postSaveModelFromGenerated({
        user_id: user.id,
        imagen_id: selectedGenImage.id,
      });
      await fetchSavedModels();
      setSavingModelFromImage(false);
      setHistorialSuccess(true);
      setTimeout(() => {
        setShowAddModelForm(false);
        resetAddModelState();
      }, 1500);
    } catch (e: any) {
      console.error("Error al añadir modelo desde imagen generada:", e);
      setHistorialError(e?.message || "Error al añadir el modelo");
      setSavingModelFromImage(false);
    }
  };

  // Lazy load generated images when entering historial view
  useEffect(() => {
    if (
      showAddModelForm === "historial" &&
      genImages.length === 0 &&
      !genLoading &&
      !genLoadInFlightRef.current
    ) {
      loadGeneratedImages(false);
    }
  }, [showAddModelForm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll observer for generated images grid
  useEffect(() => {
    if (showAddModelForm !== "historial") return;
    const sentinel = genLoadMoreRef.current;
    if (!sentinel) return;
    const root =
      genContainerRefDesktop.current || genContainerRefMobile.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            genHasMore &&
            !genLoading &&
            !genLoadingMore &&
            !genLoadInFlightRef.current
          ) {
            loadGeneratedImages(true);
          }
        });
      },
      { root, rootMargin: "200px 0px", threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [showAddModelForm, genHasMore, genLoading, genLoadingMore]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddModelSave = async () => {
    try {
      setAddModelError(null);
      if (!addModelName.trim())
        throw new Error("Ingresá un nombre para el modelo");
      if (!addModelFile) throw new Error("Subí una imagen del rostro");
      if (!addModelConsentA || !addModelConsentB)
        throw new Error("Debés aceptar ambas confirmaciones");
      setAddModelSaving(true);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      const idCompany = (profile as any)?.id_company;
      if (!idCompany) throw new Error("No se encontró la empresa");

      const ext = (addModelFile.name.split(".").pop() || "jpg").toLowerCase();
      const base = addModelName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      const fileName = `${Date.now()}-${base || "modelo"}.${ext}`;
      const targetPath = `${idCompany}/full/${fileName}`;

      const { error: upErr } = await supabase.storage
        .from("modelos")
        .upload(targetPath, addModelFile, {
          cacheControl: "3600",
          upsert: false,
          contentType: addModelFile.type || "image/jpeg",
        });
      if (upErr) throw upErr;

      const { error: insErr } = await supabase.from("modelos").insert({
        id_company: idCompany,
        nombre: addModelName.trim(),
        foto_file_name: fileName,
      });
      if (insErr) throw insErr;

      await fetchSavedModels();
      setShowAddModelForm(false);
      resetAddModelState();
    } catch (e: any) {
      console.error("Error subiendo modelo:", e);
      setAddModelError(e?.message || "No se pudo guardar el modelo");
    } finally {
      setAddModelSaving(false);
    }
  };

  const handleAiModelGenerate = async () => {
    try {
      setAiModelError(null);
      setAiModelGenerating(true);
      setAiModelResultB64(null);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");

      const image = await postGenerateModelFace({
        user_id: user.id,
        nombre: aiModelName.trim(),
        genero: aiModelGenero,
        edad: aiModelEdad,
        tipo_cuerpo: aiModelTipoCuerpo,
        tono_piel: aiModelTonoPiel,
        color_pelo: aiModelColorPelo,
        largo_pelo: aiModelLargoPelo,
        descripcion: aiModelDescripcion,
      });
      setAiModelResultB64(image);
    } catch (e: any) {
      console.error("Error generando modelo con IA:", e);
      setAiModelError(e?.message || "No se pudo generar el modelo");
    } finally {
      setAiModelGenerating(false);
    }
  };

  const handleAiModelSave = async () => {
    try {
      if (!aiModelResultB64) return;
      setAiModelError(null);
      setAiModelSaving(true);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      const idCompany = (profile as any)?.id_company;
      if (!idCompany) throw new Error("No se encontró la empresa");

      // Convert b64 to Blob
      const byteChars = atob(aiModelResultB64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++)
        byteNumbers[i] = byteChars.charCodeAt(i);
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "image/png" });

      const base = aiModelName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      const fileName = `${Date.now()}-${base || "modelo-ia"}.png`;
      const targetPath = `${idCompany}/full/${fileName}`;

      const { error: upErr } = await supabase.storage
        .from("modelos")
        .upload(targetPath, blob, {
          cacheControl: "3600",
          upsert: false,
          contentType: "image/png",
        });
      if (upErr) throw upErr;

      const { error: insErr } = await supabase.from("modelos").insert({
        id_company: idCompany,
        nombre: aiModelName.trim(),
        foto_file_name: fileName,
        // Se persiste para que la GENERACIÓN pueda afirmar la contextura (este
        // flujo guarda SOLO la cara, así que el cuerpo no se puede deducir de la
        // imagen). Ver ADR-2026-08-06-tipo-cuerpo-declarado-en-modelos.
        tipo_cuerpo: aiModelTipoCuerpo || null,
      });
      if (insErr) throw insErr;

      await fetchSavedModels();
      setShowAddModelForm(false);
      resetAddModelState();
    } catch (e: any) {
      console.error("Error guardando modelo IA:", e);
      setAiModelError(e?.message || "No se pudo guardar el modelo");
    } finally {
      setAiModelSaving(false);
    }
  };

  const handleCreativoIdeasTouchStart = (e: React.TouchEvent) => {
    creativoIdeasDragStartY.current = e.touches[0].clientY;
  };
  const handleCreativoIdeasTouchMove = (e: React.TouchEvent) => {
    if (creativoIdeasDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - creativoIdeasDragStartY.current;
    if (deltaY > 0) setCreativoIdeasMenuDragY(deltaY);
  };
  const handleCreativoIdeasTouchEnd = () => {
    if (creativoIdeasMenuDragY > 100) {
      closeCreativoIdeasMenu();
    }
    setCreativoIdeasMenuDragY(0);
    creativoIdeasDragStartY.current = null;
  };

  const handleCreativoHistorialTouchStart = (e: React.TouchEvent) => {
    creativoHistorialDragStartY.current = e.touches[0].clientY;
  };
  const handleCreativoHistorialTouchMove = (e: React.TouchEvent) => {
    if (creativoHistorialDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - creativoHistorialDragStartY.current;
    if (deltaY > 0) setCreativoHistorialMenuDragY(deltaY);
  };
  const handleCreativoHistorialTouchEnd = () => {
    if (creativoHistorialMenuDragY > 100) {
      closeCreativoHistorialMenu();
    }
    setCreativoHistorialMenuDragY(0);
    creativoHistorialDragStartY.current = null;
  };

  const handleBrandAnalysisTouchStart = (e: React.TouchEvent) => {
    brandAnalysisDragStartY.current = e.touches[0].clientY;
  };
  const handleBrandAnalysisTouchMove = (e: React.TouchEvent) => {
    if (brandAnalysisDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - brandAnalysisDragStartY.current;
    if (deltaY > 0) setBrandAnalysisMenuDragY(deltaY);
  };
  const handleBrandAnalysisTouchEnd = () => {
    if (brandAnalysisMenuDragY > 100) {
      closeBrandAnalysisMenu();
    }
    setBrandAnalysisMenuDragY(0);
    brandAnalysisDragStartY.current = null;
  };

  // Simple markdown renderer for brand analysis
  const renderMarkdown = (text: string) => {
    const lines = text.split("\n");
    return lines.map((line, i) => {
      // Headers
      if (line.startsWith("### ")) {
        return (
          <h4
            key={i}
            className="text-[15px] font-semibold text-neutral-900 mt-4 mb-2"
          >
            {line.slice(4)}
          </h4>
        );
      }
      if (line.startsWith("## ")) {
        return (
          <h3
            key={i}
            className="text-[15px] font-semibold text-neutral-900 mt-4 mb-2"
          >
            {line.slice(3)}
          </h3>
        );
      }
      if (line.startsWith("# ")) {
        return (
          <h2
            key={i}
            className="text-[17px] font-bold text-neutral-900 mt-4 mb-2"
          >
            {line.slice(2)}
          </h2>
        );
      }
      // List items
      if (line.startsWith("- ") || line.startsWith("* ")) {
        const content = line.slice(2);
        return (
          <li key={i} className="text-[15px] text-neutral-700 ml-4 list-disc">
            {renderInlineMarkdown(content)}
          </li>
        );
      }
      // Numbered list
      const numberedMatch = line.match(/^\d+\.\s/);
      if (numberedMatch) {
        const content = line.slice(numberedMatch[0].length);
        return (
          <li
            key={i}
            className="text-[15px] text-neutral-700 ml-4 list-decimal"
          >
            {renderInlineMarkdown(content)}
          </li>
        );
      }
      // Empty line
      if (line.trim() === "") {
        return <div key={i} className="h-2" />;
      }
      // Regular paragraph
      return (
        <p key={i} className="text-[15px] text-neutral-700 mb-2">
          {renderInlineMarkdown(line)}
        </p>
      );
    });
  };

  const renderInlineMarkdown = (text: string) => {
    // Bold **text** or __text__
    const parts = text.split(/(\*\*[^*]+\*\*|__[^_]+__)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={i} className="font-semibold">
            {part.slice(2, -2)}
          </strong>
        );
      }
      if (part.startsWith("__") && part.endsWith("__")) {
        return (
          <strong key={i} className="font-semibold">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return part;
    });
  };

  useEffect(() => {
    if (defaultsInitializedRef.current) return;
    defaultsInitializedRef.current = true;

    const qp = new URLSearchParams(location.search);
    let changed = false;

    // Load last used options from localStorage
    const lastState = loadLastCrearAnuncioState();

    // Apply last used activeTab (mode)
    if (lastState?.activeTab) {
      setActiveTab(lastState.activeTab);
    }

    // Apply last used formato if not already set
    if (!qp.get("formato")) {
      if (lastState?.formato) {
        qp.set("formato", lastState.formato);
        setFormat(lastState.formato);
        changed = true;
      } else if (format) {
        qp.set("formato", format);
        changed = true;
      }
    }

    // Apply last used cantidad_imagenes if not already set
    if (!qp.get("cantidad_imagenes")) {
      if (lastState?.cantidadImagenes && lastState.cantidadImagenes > 0) {
        qp.set("cantidad_imagenes", String(lastState.cantidadImagenes));
        setImageCount(lastState.cantidadImagenes);
        changed = true;
      } else if (imageCount > 0) {
        qp.set("cantidad_imagenes", String(imageCount));
        changed = true;
      }
    }

    // Apply last used background/escenario settings
    if (!qp.get("escenario") && lastState?.background?.mode) {
      const bgMode = lastState.background.mode;
      if (bgMode === "ia") {
        qp.set("escenario", "sugerido");
        setSelectedScenarios({
          sugerido: true,
          fondo_blanco: false,
          personalizado: false,
          biblioteca: false,
          escenas_guardadas: false,
        });
      } else if (bgMode === "blanco") {
        qp.set("escenario", "fondo_blanco");
        setSelectedScenarios({
          sugerido: false,
          fondo_blanco: true,
          personalizado: false,
          biblioteca: false,
          escenas_guardadas: false,
        });
      } else if (bgMode === "descripcion" && lastState.background.text) {
        qp.set("escenario", "personalizado");
        qp.set("escenario_desc", lastState.background.text);
        setEscenaPrompt(lastState.background.text);
        setSelectedScenarios({
          sugerido: false,
          fondo_blanco: false,
          personalizado: true,
          biblioteca: false,
          escenas_guardadas: false,
        });
      } else if (bgMode === "elegido") {
        qp.set("escenario", "biblioteca");
        if (lastState.background.id)
          qp.set("escenario_fondo_id", String(lastState.background.id));
        if (lastState.background.url)
          qp.set("escenario_fondo_url", lastState.background.url);
        setSelectedScenarios({
          sugerido: false,
          fondo_blanco: false,
          personalizado: false,
          biblioteca: true,
          escenas_guardadas: false,
        });
      } else if (bgMode === "guardado" && lastState.background.fileName) {
        qp.set("escenario", "escenas_guardadas");
        qp.set("escena_guardada_nombre_archivo", lastState.background.fileName);
        setSelectedScenarios({
          sugerido: false,
          fondo_blanco: false,
          personalizado: false,
          biblioteca: false,
          escenas_guardadas: true,
        });
      }
      changed = true;
    } else if (!qp.get("escenario")) {
      qp.set("escenario", "sugerido");
      changed = true;
    }

    // Apply last used modelo settings (only if modelo was enabled)
    if (
      !qp.get("modelo_mode") &&
      lastState?.modelo?.mode &&
      lastState?.modeloEnabled !== false
    ) {
      qp.set("modelo_mode", lastState.modelo.mode);
      setSelectedModeloOption(
        lastState.modelo.mode === "ia"
          ? "ia"
          : lastState.modelo.mode === "personalizado"
            ? "personalizado"
            : lastState.modelo.mode === "guardados"
              ? "anteriores"
              : null,
      );
      if (lastState.modelo.guardadoId) {
        qp.set("modelo_guardado_id", lastState.modelo.guardadoId);
        setSelectedSavedModelId(Number(lastState.modelo.guardadoId));
      }
      if (lastState.modelo.guardadoNombre) {
        qp.set("modelo_guardado_nombre", lastState.modelo.guardadoNombre);
      }
      if (lastState.modelo.genero) {
        qp.set("genero", lastState.modelo.genero);
        setModeloGenero(lastState.modelo.genero);
      }
      if (lastState.modelo.edad) {
        qp.set("edad", lastState.modelo.edad);
        setModeloEdad(lastState.modelo.edad);
      }
      if (lastState.modelo.tipoCuerpo) {
        qp.set("tipo_cuerpo", lastState.modelo.tipoCuerpo);
        setModeloTipoCuerpo(lastState.modelo.tipoCuerpo);
      }
      if (lastState.modelo.tonoPiel) {
        qp.set("tono_piel", lastState.modelo.tonoPiel);
        setModeloTonoPiel(lastState.modelo.tonoPiel);
      }
      if (lastState.modelo.colorPelo) {
        qp.set("color_pelo", lastState.modelo.colorPelo);
        setModeloColorPelo(lastState.modelo.colorPelo);
      }
      if (lastState.modelo.largoPelo) {
        qp.set("largo_pelo", lastState.modelo.largoPelo);
        setModeloLargoPelo(lastState.modelo.largoPelo);
      }
      if (lastState.modelo.descripcion) {
        qp.set("desc_modelo", lastState.modelo.descripcion);
        setModeloDescripcion(lastState.modelo.descripcion);
      }
      changed = true;
    }

    // Apply last used pose (default handled by fetchPoses useEffect)
    if (!qp.get("pose")) {
      if (lastState?.pose) {
        qp.set("pose", lastState.pose);
        setSelectedPoses(lastState.pose.split(",").filter(Boolean));
        changed = true;
      }
    }

    // Apply last used vista_producto
    if (!qp.get("vista_producto") && lastState?.vistaProducto) {
      qp.set("vista_producto", lastState.vistaProducto);
      setSelectedVistaProducto(lastState.vistaProducto);
      changed = true;
    }

    // Apply last used modeloEnabled toggle state
    if (typeof lastState?.modeloEnabled === "boolean") {
      setModeloEnabled(lastState.modeloEnabled);
    }

    // Apply last used multiPoseToggle state
    if (typeof lastState?.multiPoseToggle === "boolean") {
      setMultiPoseToggle(lastState.multiPoseToggle);
    }

    // Apply last used creativoPrompt for creative mode
    if (lastState?.creativoPrompt && !creativoPrompt) {
      setCreativoPrompt(lastState.creativoPrompt);
    }

    // Apply last used composition/texto settings
    if (!qp.get("texto_enabled") && lastState?.composition?.enabled) {
      qp.set("texto_enabled", "1");
      if (lastState.composition.selectedCompId)
        qp.set("texto_comp_id", String(lastState.composition.selectedCompId));
      if (lastState.composition.selectedCompUrl)
        qp.set("texto_comp_url", lastState.composition.selectedCompUrl);
      if (lastState.composition.compositionText)
        qp.set("texto_texto", lastState.composition.compositionText);
      changed = true;
    }

    if (changed) {
      // Use setTimeout to avoid calling navigate during render
      setTimeout(() => {
        navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
      }, 0);
    }
  }, [location.pathname, location.search, navigate, format, imageCount]);

  const formatOptions: Array<{ id: FormatoKey; name: string; image: string }> =
    [
      {
        id: "post",
        name: "Posteo (4:5)",
        image:
          "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Posteo.jpeg",
      },
      {
        id: "story",
        name: "Historias/Reels (9:16)",
        image:
          "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Historia.jpeg",
      },
      {
        id: "square",
        name: "Cuadrado (1:1)",
        image:
          "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Cuadrado.jpeg",
      },
      {
        id: "portrait",
        name: "Retrato (2:3)",
        image:
          "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Retrato.jpeg",
      },
      {
        id: "banner",
        name: "Banner Web (16:9)",
        image:
          "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_BannerWeb.png",
      },
      {
        id: "editorial",
        name: "Editorial (3:2)",
        image:
          "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Editorial.png",
      },
      {
        id: "billboard",
        name: "Billboard (21:9)",
        image:
          "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Billboard.png",
      },
    ];

  /**
   * Créditos que va a costar la generación que está configurada ahora mismo. Espeja lo
   * que cobra el backend, con las MISMAS condiciones con que se arma el payload más
   * abajo: las poses sólo viajan si hay modelo y el rubro es moda, y las vistas sólo si
   * no hay modelo y hay un único producto.
   * Ver ADR-2026-08-03-creditos-insuficientes-muestran-planes.
   */
  const costoDeEstaGeneracion = (): number => {
    const mandaPoses = modeloEnabled && isFashionRubro && multiPoseActive;
    const mandaVistas = !modeloEnabled && multiVistaActive && ids.length === 1;
    return costoDeGeneracion({
      composicion: modeloEnabled ? "modelo" : "producto",
      cantidadImagenes: multiPoseActive || multiVistaActive ? 1 : imageCount,
      poses: mandaPoses ? selectedPoses : [],
      vistas: mandaVistas ? selectedVistas : [],
    });
  };

  /**
   * ¿Alcanzan los créditos para generar? Si no, abre el catálogo de planes.
   *
   * `costo` es lo que va a cobrar el backend. Antes esta función sólo miraba
   * `remaining <= 0` —disponibilidad, no suficiencia—, así que un usuario con 2
   * créditos pidiendo 3 imágenes pasaba el chequeo, el backend rechazaba el request y
   * el usuario veía un error genérico en vez del catálogo de planes. Es el mismo bug
   * que ADR-518 arregló en Interfaz V2; esta pantalla no lo había recibido.
   * Ver ADR-2026-08-03-creditos-insuficientes-muestran-planes.
   */
  const checkSubscriptionLimits = async (costo: number) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();

      if (!profile?.id_company)
        throw new Error("No company associated with user");

      const { data: companyProfile } = await supabase
        .from("company_profiles")
        .select("plan, imagenes_restantes:imagenes_disponibles, paid_plan_start_date")
        .eq("id_company", profile.id_company)
        .single();

      if (!companyProfile) throw new Error("No company profile found");

      // `needed` cae a 1 si no llega un costo válido: nada es gratis.
      const needed = Number.isFinite(costo) && costo > 0 ? Math.ceil(costo) : 1;
      const remaining = (companyProfile as any).imagenes_restantes as
        | number
        | null
        | undefined;
      if (typeof remaining === "number" && remaining < needed) {
        const isFree = (companyProfile as any).plan === "gratis";
        setSubscriptionLimitType(
          isFree ? "free-trial-ended" : "plan-limit-reached",
        );
        setShowSubscriptionLimitModal(true);
        return false;
      }

      return true;
    } catch (err) {
      console.error("Error checking subscription limits:", err);
      return true;
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) {
          if (mounted) {
            setVariants([]);
            setLoading(false);
          }
          return;
        }
        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        const idCompany: string | null = (profile as any)?.id_company ?? null;
        if (!idCompany || ids.length === 0) {
          if (mounted) {
            setVariants([]);
            setLoading(false);
          }
          return;
        }
        if (mounted) setCompanyId(idCompany);

        // Fetch company rubro to set default modeloEnabled for non-fashion rubros and control Pose visibility
        try {
          const { data: companyData } = await supabase
            .from("company_profiles")
            .select("rubro, plan")
            .eq("id_company", idCompany)
            .single();
          const isFashion = companyData?.rubro === "Moda e Indumentaria";
          const plan = (companyData as any)?.plan as string | null;
          if (mounted && plan) {
            setUserPlan(plan);
          }
          if (mounted) {
            setIsFashionRubro(isFashion);
            if (companyData?.rubro) setUserRubro(companyData.rubro as string);
            const lastState = loadLastCrearAnuncioState();
            // Only set default modeloEnabled if no saved preference
            if (typeof lastState?.modeloEnabled !== "boolean" && !isFashion) {
              setModeloEnabled(false);
            }
            // For Fashion rubro with no saved generation data, set defaults for personalized mode
            if (isFashion && !lastState?.modelo?.mode) {
              // Default to 'ia' (sugerido por IA)
              if (!params.get("modelo_mode")) {
                setSelectedModeloOption("ia");
                const qp = new URLSearchParams(location.search);
                qp.set("modelo_mode", "ia");
                navigate(`${location.pathname}?${qp.toString()}`, {
                  replace: true,
                });
              }
            }
          }
        } catch {
          // Ignore error, rubro check is optional
        }

        // 1) Fetch basic variants with product model_view.
        // En lotes (fetchInChunks) para no exceder el largo de URL del gateway
        // REST con selecciones grandes — un `.in("id", ids)` crudo con cientos
        // de ids tira HTTP 400. Ver utils/supabaseChunkedIn.
        let vd: any[] = [];
        try {
          vd = await fetchInChunks<any>(ids, (chunk) =>
            supabase
              .from("variantes")
              .select("id, nombre, product_id, productos!inner(id, model_view)")
              .eq("id_company", idCompany)
              .in("id", chunk),
          );
        } catch (variantsError) {
          console.log(
            "[PruebaNuevaInterfaz] variantes fetch error:",
            variantsError,
          );
        }

        console.log("[PruebaNuevaInterfaz] variantsData:", vd);

        // Collect all products' model_view values for pose recommendations
        if (mounted) {
          const rawModelViews = vd.map((v: any) => ({
            variante_id: v?.id,
            model_view: v?.productos?.model_view,
          }));
          console.log("[Poses] Raw model_view por variante:", rawModelViews);
          const allModelViews = vd
            .map((v: any) => (v?.productos?.model_view || "").trim())
            .filter((mv: string) => mv && mv !== "na" && mv !== "producto");
          console.log(
            "[Poses] model_views filtrados para recomendaciones:",
            allModelViews,
          );
          setProductModelViews(allModelViews);
        }

        // If single product, get its model_view for vista del producto options
        if (mounted && vd.length === 1) {
          const modelView = vd[0]?.productos?.model_view;
          console.log(
            "[PruebaNuevaInterfaz] model_view found:",
            modelView,
            "full variant:",
            vd[0],
          );
          if (modelView) setProductModelView(modelView);
          // Set default vista based on product type (only if not already set from URL/localStorage)
          const currentVista = params.get("vista_producto");
          if (!currentVista) {
            // Clothing products default to 'frente', others default to 'automatica'
            const isClothing =
              modelView === "torso" ||
              modelView === "piernas" ||
              modelView === "cuerpo-completo" ||
              modelView === "cuerpo_completo";
            const isCalzadoOrCabeza =
              modelView === "calzado" || modelView === "cabeza";
            if (!modelView || isClothing || isCalzadoOrCabeza) {
              // Missing model_view → fallback to 'frente' (frente/posterior options)
              setSelectedVistaProducto("frente");
            } else {
              setSelectedVistaProducto("automatica");
            }
          }
        }
        // 2) Fetch product images for these variants (en lotes para no
        // exceder el largo de URL del gateway con selecciones grandes → 400).
        const variantIds = vd.map((v) => String(v.id));
        let imagesByVariantId: Record<string, ProductImage[]> = {};
        if (variantIds.length > 0) {
          let imagesData: any[] = [];
          try {
            imagesData = await fetchInChunks<any>(variantIds, (chunk) =>
              supabase
                .from("productos_imagenes")
                .select("id, url, principal, orden, variante_id")
                .eq("id_company", idCompany)
                .in("variante_id", chunk),
            );
          } catch (imagesError) {
            console.log(
              "[PruebaNuevaInterfaz] imágenes fetch error:",
              imagesError,
            );
          }
          imagesByVariantId = {};
          (imagesData || []).forEach((img: any) => {
            const entry: ProductImage = {
              id: String(img.id),
              url: String(img.url || ""),
              principal: !!img.principal,
              orden: Number(img.orden || 0),
              variante_id: String(img.variante_id || ""),
            };
            if (!imagesByVariantId[entry.variante_id])
              imagesByVariantId[entry.variante_id] = [];
            imagesByVariantId[entry.variante_id].push(entry);
          });
        }
        const processed: Variant[] = vd.map((v) => ({
          id: String(v.id),
          nombre: String(v.nombre || "Producto"),
          images: imagesByVariantId[String(v.id)] || [],
        }));
        if (mounted) setVariants(processed);

        // Fetch brand analysis — prioriza el análisis de la marca ACTIVA del usuario;
        // fallback a config.media_agent_analysis si la marca no tiene context o no hay marca.
        try {
          const {
            data: { user },
          } = await supabase.auth.getUser();

          let usedBrandAnalysis = false;

          if (user) {
            const { data: profData } = await supabase
              .from("profiles")
              .select("current_brand_id")
              .eq("id", user.id)
              .maybeSingle();
            const currentBrandId = (profData as any)?.current_brand_id as
              | string
              | null
              | undefined;

            if (currentBrandId) {
              const { data: brandData } = await supabase
                .from("brand_profiles")
                .select("name, context")
                .eq("id", currentBrandId)
                .maybeSingle();
              if (mounted && brandData) {
                setActiveBrandName((brandData as any).name ?? null);
                if ((brandData as any).context) {
                  setBrandAnalysis((brandData as any).context);
                  usedBrandAnalysis = true;
                }
              }
            }
          }

          if (!usedBrandAnalysis) {
            const { data: configData } = await supabase
              .from("config")
              .select("media_agent_analysis")
              .eq("id_company", idCompany)
              .maybeSingle();
            if (mounted && configData?.media_agent_analysis) {
              setBrandAnalysis(configData.media_agent_analysis);
            }
          }
        } catch {
          // Ignore error, brand analysis is optional
        }
      } catch {
        if (mounted) setVariants([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [ids]);

  // Refrescar el análisis cuando se cambia de marca desde sidebar/pildora
  useEffect(() => {
    const refetchAnalysis = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profData } = await supabase
          .from("profiles")
          .select("current_brand_id, id_company")
          .eq("id", user.id)
          .maybeSingle();
        const brandId = (profData as any)?.current_brand_id as
          | string
          | null
          | undefined;
        const idCompany = (profData as any)?.id_company as string | undefined;
        if (brandId) {
          const { data: brandData } = await supabase
            .from("brand_profiles")
            .select("name, context")
            .eq("id", brandId)
            .maybeSingle();
          if (brandData) {
            setActiveBrandName((brandData as any).name ?? null);
            setBrandAnalysis((brandData as any).context ?? null);
            return;
          }
        }
        // Fallback legacy
        if (idCompany) {
          const { data: configData } = await supabase
            .from("config")
            .select("media_agent_analysis")
            .eq("id_company", idCompany)
            .maybeSingle();
          setBrandAnalysis(configData?.media_agent_analysis ?? null);
          setActiveBrandName(null);
        }
      } catch {
        // silencioso
      }
    };
    const handler = () => refetchAnalysis();
    window.addEventListener("atlas:brand-changed", handler);
    return () => window.removeEventListener("atlas:brand-changed", handler);
  }, []);

  const principalImageFor = (v: Variant): ProductImage | undefined => {
    const imgs = v.images || [];
    return imgs.find((i) => i.principal) || imgs[0];
  };

  const principalImageUrl = (v: Variant): string | null => {
    const img = principalImageFor(v);
    if (!img?.url) return null;
    const key = img.url;
    const signed = signedUrls[key];
    if (signed?.thumbnail) return signed.thumbnail;
    if (key.startsWith("http")) return key; // fallback while signing not ready
    return null;
  };

  const ensurePrincipalImageSigned = async (v: Variant) => {
    const img = principalImageFor(v);
    if (!img?.url || !companyId) return;
    const key = img.url as string;
    if (key.startsWith("http")) {
      // Try to derive filename and sign opt/full paths from storage
      const fileNameWithQuery = key.split("/").pop() || "";
      const fileName = fileNameWithQuery.split("?")[0];
      if (!fileName) {
        if (!signedUrls[key])
          setSignedUrls((prev) => ({
            ...prev,
            [key]: { full: key, thumbnail: key },
          }));
        return;
      }
      const pathVariantId = (img as any)?.variante_id ?? v.id;
      const optFilePath = `${companyId}/${pathVariantId}/opt/${fileName}`;
      const fullFilePath = `${companyId}/${pathVariantId}/full/${fileName}`;
      try {
        let thumbSigned: string | null = null;
        let fullSigned: string | null = null;
        const { data: thumb } = await supabase.storage
          .from("productos")
          .createSignedUrl(optFilePath, 3600);
        if (thumb?.signedUrl) {
          thumbSigned = thumb.signedUrl;
          const { data: full } = await supabase.storage
            .from("productos")
            .createSignedUrl(fullFilePath, 3600);
          fullSigned = full?.signedUrl || thumbSigned;
        } else {
          const { data: full } = await supabase.storage
            .from("productos")
            .createSignedUrl(fullFilePath, 3600);
          if (full?.signedUrl) {
            thumbSigned = full.signedUrl;
            fullSigned = full.signedUrl;
          }
        }
        setSignedUrls((prev) => ({
          ...prev,
          [key]: {
            full: fullSigned || key,
            thumbnail: thumbSigned || fullSigned || key,
          },
        }));
      } catch {
        if (!signedUrls[key])
          setSignedUrls((prev) => ({
            ...prev,
            [key]: { full: key, thumbnail: key },
          }));
      }
      return;
    }
    if (signedUrls[key] || loadingImageKeysRef.current.has(key)) return;
    loadingImageKeysRef.current.add(key);
    try {
      const pathVariantId = (img as any)?.variante_id ?? v.id;
      const optFilePath = `${companyId}/${pathVariantId}/opt/${key}`;
      const fullFilePath = `${companyId}/${pathVariantId}/full/${key}`;
      let thumbSigned: string | null = null;
      let fullSigned: string | null = null;
      const { data: thumb } = await supabase.storage
        .from("productos")
        .createSignedUrl(optFilePath, 3600);
      if (thumb?.signedUrl) {
        thumbSigned = thumb.signedUrl;
        const { data: full } = await supabase.storage
          .from("productos")
          .createSignedUrl(fullFilePath, 3600);
        fullSigned = full?.signedUrl || thumbSigned;
      } else {
        const { data: full } = await supabase.storage
          .from("productos")
          .createSignedUrl(fullFilePath, 3600);
        if (full?.signedUrl) {
          thumbSigned = full.signedUrl;
          fullSigned = full.signedUrl;
        }
      }
      setSignedUrls((prev) => ({
        ...prev,
        [key]: {
          full: fullSigned || key,
          thumbnail: thumbSigned || fullSigned || key,
        },
      }));
    } catch {
      setSignedUrls((prev) => ({
        ...prev,
        [key]: { full: "", thumbnail: "" },
      }));
    } finally {
      loadingImageKeysRef.current.delete(key);
    }
  };

  useEffect(() => {
    // Pre-sign principal images for loaded variants
    if (!companyId || variants.length === 0) return;
    variants.forEach((v) => {
      void ensurePrincipalImageSigned(v);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, variants.map((v) => v.id).join(",")]);

  const toggleScenario = (key: ScenarioKey) => {
    const wasKeySelected = selectedScenarios[key];

    setSelectedScenarios((prev) => {
      const isSelected = prev[key];
      if (isSelected) {
        return {
          sugerido: false,
          fondo_blanco: false,
          personalizado: false,
          biblioteca: false,
          escenas_guardadas: false,
        };
      }
      return {
        sugerido: key === "sugerido",
        fondo_blanco: key === "fondo_blanco",
        personalizado: key === "personalizado",
        biblioteca: key === "biblioteca",
        escenas_guardadas: key === "escenas_guardadas",
      };
    });

    const qp = new URLSearchParams(location.search);

    if (wasKeySelected) {
      qp.delete("escenario");
      navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
      return;
    }

    qp.set("escenario", key);

    navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
  };

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const esc = p.get("escenario") as ScenarioKey | null;
    if (!esc) return;
    setSelectedScenarios({
      sugerido: esc === "sugerido",
      fondo_blanco: esc === "fondo_blanco",
      personalizado: esc === "personalizado",
      biblioteca: esc === "biblioteca",
      escenas_guardadas: esc === "escenas_guardadas",
    });
  }, [location.search]);

  const addOptions = useMemo(
    () => [
      { key: "texto", title: "Texto", Icon: Type },
      { key: "colores", title: "Colores", Icon: Palette },
      { key: "logo", title: "Logo", Icon: Badge },
    ],
    [],
  );

  const handleAddOptionClick = (key: string) => {
    const qp = new URLSearchParams(location.search);
    if (ids.length > 0) qp.set("ids", ids.join(","));
    else qp.delete("ids");
    qp.set("estudio_virtual", "1");
    const query = qp.toString();

    if (key === "modelo") {
      navigate(
        `/redes-sociales/crear-anuncio/personalizar-modelo-estudio-virtual?${query}`,
      );
      return;
    }
    if (key === "texto") {
      closeFormatoMenu();
      closeEscenaMenu();
      closeBibliotecaMenu();
      fetchTextoComposiciones();
      setShowTextoMenu(true);
      return;
    }
    if (key === "colores" || key === "logo") {
      setSoonFeatureKey(key);
    }
  };

  const handleRemoveAddOption = (key: string) => {
    const qp = new URLSearchParams(location.search);

    if (key === "modelo") {
      // Remove all model-related params
      qp.delete("modelo_mode");
      qp.delete("modelo_guardado_id");
      qp.delete("modelo_guardado_nombre");
      qp.delete("genero");
      qp.delete("edad");
      qp.delete("tipo_cuerpo");
      qp.delete("tono_piel");
      qp.delete("color_pelo");
      qp.delete("largo_pelo");
      qp.delete("desc_modelo");
      qp.delete("pose");
    }
    if (key === "texto") {
      qp.delete("texto_enabled");
      qp.delete("texto_comp_id");
    }

    navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
  };

  const handleGenerate = async () => {
    // Immediate lock to prevent rapid clicks
    if (generatingLockRef.current) return;
    generatingLockRef.current = true;

    if (ids.length === 0) {
      generatingLockRef.current = false;
      return;
    }

    // ─── Comprehensive validation ───
    const missingFields: string[] = [];

    if (activeTab === "personalizado") {
      // Escenario validation
      const escenarioParam = params.get("escenario") as string | null;
      const isBiblioteca =
        selectedScenarios.biblioteca || escenarioParam === "biblioteca";
      const isPersonalizado =
        selectedScenarios.personalizado || escenarioParam === "personalizado";

      if (isBiblioteca) {
        const fondoId = params.get("escenario_fondo_id");
        if (!fondoId) missingFields.push("fondo de biblioteca");
      }
      if (isPersonalizado) {
        const escDesc = params.get("escenario_desc");
        if (!escDesc?.trim()) missingFields.push("descripción de escena");
      }

      if (modeloEnabled) {
        // Modelo validation
        const modeloModeCheck = params.get("modelo_mode") as string | null;
        if (selectedPoses.length === 0 && isFashionRubro)
          missingFields.push("pose");
        if (!modeloModeCheck) missingFields.push("tipo de modelo");
        if (
          modeloModeCheck === "guardados" &&
          !params.get("modelo_guardado_id")
        )
          missingFields.push("modelo guardado");
      } else if (ids.length === 1) {
        // Vista producto validation (single product, no modelo)
        // Multi-vista: chequear selectedVistas. Single: chequear selectedVistaProducto.
        const hasVista = multiVistaToggle
          ? selectedVistas.length > 0
          : !!selectedVistaProducto;
        if (!hasVista) missingFields.push("vista del producto");
      }
    }

    if (activeTab === "creativo") {
      if (!creativoPrompt.trim()) missingFields.push("instrucción creativa");
    }

    if (missingFields.length > 0) {
      setGenError(`Para generar, completá: ${missingFields.join(", ")}`);
      generatingLockRef.current = false;
      return;
    }

    try {
      setGenError(null);
      setGenerating(true);
      setImagesReady(false);
      setShowLoadingScreen(true);
      setLoadingExiting(false);
      const canGenerate = await checkSubscriptionLimits(costoDeEstaGeneracion());
      if (!canGenerate) {
        setGenerating(false);
        setShowLoadingScreen(false);
        generatingLockRef.current = false;
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();

      if (!profile?.id_company)
        throw new Error("No se encontró la empresa del usuario");

      let formatName = "";
      switch (format) {
        case "story":
          formatName = "9:16";
          break;
        case "square":
          formatName = "1:1";
          break;
        case "portrait":
          formatName = "2:3";
          break;
        case "banner":
          formatName = "16:9";
          break;
        case "editorial":
          formatName = "3:2";
          break;
        case "billboard":
          formatName = "21:9";
          break;
        case "post":
        default:
          formatName = "4:5";
      }

      const textoEnabledFlag = params.get("texto_enabled") === "1";
      const textoCompIdParam = params.get("texto_comp_id") || "";
      const textoCompUrl = params.get("texto_comp_url") || "";
      const textoTextParam = params.get("texto_texto") || "";
      const textoCompId = textoCompIdParam ? Number(textoCompIdParam) : NaN;

      const poseParam = params.get("pose") || "";
      const poseArray = poseParam ? poseParam.split(",").filter(Boolean) : [];
      const rawModeloMode = params.get("modelo_mode") as
        | "ia"
        | "guardados"
        | "personalizado"
        | null;
      const modeloModeParam =
        rawModeloMode === "ia" ||
        rawModeloMode === "guardados" ||
        rawModeloMode === "personalizado"
          ? rawModeloMode
          : null;
      const modeloGuardadoId = params.get("modelo_guardado_id") || "";
      const modeloGuardadoNombre = params.get("modelo_guardado_nombre") || "";
      const genero = params.get("genero") || "";
      const edad = params.get("edad") || "";
      const tipoCuerpo = params.get("tipo_cuerpo") || "";
      const tonoPiel = params.get("tono_piel") || "";
      const colorPelo = params.get("color_pelo") || "";
      const largoPelo = params.get("largo_pelo") || "";
      const descModelo = params.get("desc_modelo") || "";

      const escenarioDescParam = params.get("escenario_desc") || "";
      const escenarioFondoIdParam = params.get("escenario_fondo_id") || "";
      const escenarioFondoUrlParam = params.get("escenario_fondo_url") || "";
      const escenaGuardadaNombreArchivo =
        params.get("escena_guardada_nombre_archivo") || "";
      const escenarioParam = params.get("escenario") as ScenarioKey | null;

      let scenario: ScenarioKey = "sugerido";
      if (selectedScenarios.sugerido) scenario = "sugerido";
      else if (selectedScenarios.fondo_blanco) scenario = "fondo_blanco";
      else if (selectedScenarios.personalizado) scenario = "personalizado";
      else if (selectedScenarios.biblioteca) scenario = "biblioteca";
      else if (selectedScenarios.escenas_guardadas)
        scenario = "escenas_guardadas";
      else if (escenarioParam) scenario = escenarioParam;

      // Loading duration: automatico tab (rapido mode) takes longer; pro quality takes even longer
      const isAutomaticMode = activeTab === "automatico";
      const isPro = calidad === "pro";
      setLoadingDurationMs(
        multiPoseActive
          ? isPro
            ? 100000
            : 70000
          : isAutomaticMode
            ? isPro
              ? 160000
              : 80000
            : isPro
              ? 50000
              : 35000,
      );

      let backgroundMode:
        | "ia"
        | "blanco"
        | "elegido"
        | "descripcion"
        | "guardado" = "ia";
      let backgroundId: number | undefined;
      let backgroundUrl: string | undefined;
      let backgroundText: string | undefined;

      // In automatico mode, always use 'ia' for background
      // In personalizado/creativo modes, use the selected scenario
      if (activeTab === "automatico") {
        backgroundMode = "ia";
      } else {
        switch (scenario) {
          case "fondo_blanco":
            backgroundMode = "blanco";
            break;
          case "biblioteca": {
            backgroundMode = "elegido";
            const n = Number(escenarioFondoIdParam);
            if (Number.isFinite(n)) backgroundId = n;
            if (escenarioFondoUrlParam) backgroundUrl = escenarioFondoUrlParam;
            break;
          }
          case "personalizado":
            backgroundMode = "descripcion";
            if (escenarioDescParam.trim())
              backgroundText = escenarioDescParam.trim();
            break;
          case "escenas_guardadas":
            backgroundMode = "guardado";
            break;
          case "sugerido":
          default:
            backgroundMode = "ia";
        }
      }

      const variantIds = ids
        .map((s) => String(s))
        .map((s) => s.trim())
        .filter((s) => !!s && s !== "0");
      if (variantIds.length === 0)
        throw new Error("No hay productos válidos seleccionados");
      const mode = variantIds.length > 1 ? "multiple" : "single";

      // Determine API mode based on activeTab:
      // - 'automatico' tab -> 'rapido' (AI decides everything)
      // - 'personalizado' tab -> 'personalizado' (user customizes options)
      // - 'creativo' tab -> 'creativo' (user provides custom prompt)
      const modoForApi: "rapido" | "personalizado" | "creativo" =
        activeTab === "automatico"
          ? "rapido"
          : activeTab === "creativo"
            ? "creativo"
            : "personalizado";

      let composicion: "modelo" | "producto" = "producto";
      if (
        modeloEnabled &&
        (modeloModeParam === "ia" ||
          modeloModeParam === "guardados" ||
          modeloModeParam === "personalizado")
      ) {
        composicion = "modelo";
      }

      // Build request data - creativo mode doesn't need composicion or background_mode
      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds,
        mode,
        modo: modoForApi,
        cantidad_imagenes:
          multiPoseActive || multiVistaActive ? 1 : imageCount,
        Format: formatName,
        new_ui: true,
      };

      // Only add composicion and background for non-creativo modes
      if (activeTab !== "creativo") {
        requestData.composicion = composicion;
        requestData.background_mode = backgroundMode;

        if (backgroundMode === "guardado" && escenaGuardadaNombreArchivo) {
          requestData.background_file_name = escenaGuardadaNombreArchivo;
        } else if (backgroundMode === "elegido") {
          if (
            typeof backgroundId === "number" &&
            Number.isFinite(backgroundId)
          ) {
            requestData.background_id = backgroundId;
          }
          if (!requestData.background_id && backgroundUrl) {
            requestData.background_url = backgroundUrl;
          }
        } else if (backgroundMode === "blanco") {
          requestData.background_id = 5;
        } else if (backgroundMode === "descripcion" && backgroundText) {
          requestData.background_text = backgroundText;
        }
      }

      if (textoEnabledFlag) {
        requestData.composition_mode = true;
        if (Number.isFinite(textoCompId)) {
          requestData.composition_id = textoCompId;
        } else if (textoCompUrl) {
          requestData.composition_url = textoCompUrl;
        }
        if (textoTextParam.trim()) {
          requestData.composition_text = textoTextParam.trim();
        }
      }

      if (modeloEnabled && poseArray.length > 0 && isFashionRubro)
        requestData.pose = poseArray;

      // Add vista_producto when modelo is off and single product
      const vistaProducto = params.get("vista_producto") || "";
      if (vistaProducto && !modeloEnabled && ids.length === 1) {
        // Multi-vista: mandar array. Single: mandar string.
        if (multiVistaActive) {
          requestData.vista_producto = selectedVistas;
        } else {
          requestData.vista_producto = vistaProducto;
        }
      }
      if (modeloEnabled && modeloModeParam) {
        requestData.modelo_mode = modeloModeParam;
        if (modeloModeParam === "guardados" && modeloGuardadoId) {
          requestData.modelo_guardado_id = modeloGuardadoId;
          if (modeloGuardadoNombre)
            requestData.modelo_guardado_nombre = modeloGuardadoNombre;
        } else if (modeloModeParam === "personalizado") {
          if (genero) requestData.genero = genero;
          if (edad) requestData.edad = edad;
          if (tipoCuerpo) requestData.tipo_cuerpo = tipoCuerpo;
          if (tonoPiel) requestData.tono_piel = tonoPiel;
          if (colorPelo) requestData.color_pelo = colorPelo;
          if (largoPelo) requestData.largo_pelo = largoPelo;
          if (descModelo.trim()) requestData.desc_modelo = descModelo.trim();
        }
      }

      requestData.modo_pro = calidad === "pro";

      // Add creative prompt if in creativo tab
      if (activeTab === "creativo" && creativoPrompt.trim()) {
        requestData.prompt = creativoPrompt.trim();
      }

      // Add instruccion adicional if provided (personalizado tab)
      if (activeTab === "personalizado" && instruccionAdicional.trim()) {
        requestData.instruccion_adicional = instruccionAdicional.trim();
      }

      try {
        console.log(
          "[PruebaNuevaInterfaz] handleGenerate payload",
          requestData,
        );
      } catch {}


      // Carrera respuesta-vs-poll: el backend nuevo puede responder 202 sin
      // ids (generación lenta); el poll por execution_id las recupera. Se
      // sintetiza `data` con la misma shape para no tocar el flujo de abajo.
      const genResult = await generateClassicImages({
        payload: requestData,
        companyId: profile.id_company,
        expectedCount:
          requestData.tipo === "imagen"
            ? (requestData.cantidad_imagenes as number | undefined)
            : undefined,
      });
      const data: any = {
        imagenes_generadas_ids: genResult.imageIds,
        id_company: genResult.companyId,
      };
      try {
        console.log("[PruebaNuevaInterfaz] handleGenerate response", data);
      } catch {}

      if (
        Array.isArray(data?.imagenes_generadas_ids) &&
        data.imagenes_generadas_ids.length > 0
      ) {
        const imageIds = data.imagenes_generadas_ids as number[];
        const companyIdResult =
          (data.id_company as string) || profile.id_company;
        setGeneratedImageIds(imageIds);
        setCompanyIdForImages(companyIdResult);
        setGenError(null);
        consecutiveErrorCountRef.current = 0; // Reset on success
        try {
          window.sessionStorage.setItem(
            "atlas:crear-anuncio:has-generated",
            "1",
          );
        } catch {}

        // Save last used options to localStorage for next time
        try {
          mergeLastCrearAnuncioState({
            activeTab: activeTab,
            formato: format,
            cantidadImagenes: imageCount,
            modoPro: calidad === "pro",
            modeloEnabled: modeloEnabled,
            multiPoseToggle: multiPoseToggle,
            background:
              activeTab !== "creativo"
                ? {
                    mode: backgroundMode,
                    id: backgroundId,
                    url: backgroundUrl,
                    text: backgroundText,
                    fileName:
                      backgroundMode === "guardado"
                        ? escenaGuardadaNombreArchivo || undefined
                        : undefined,
                  }
                : undefined,
            modelo: modeloModeParam
              ? {
                  mode: modeloModeParam,
                  guardadoId: modeloGuardadoId || undefined,
                  guardadoNombre: modeloGuardadoNombre || undefined,
                  genero: genero || undefined,
                  edad: edad || undefined,
                  tipoCuerpo: tipoCuerpo || undefined,
                  tonoPiel: tonoPiel || undefined,
                  colorPelo: colorPelo || undefined,
                  largoPelo: largoPelo || undefined,
                  descripcion: descModelo || undefined,
                }
              : undefined,
            pose: poseArray.length > 0 ? poseParam : undefined,
            vistaProducto: vistaProducto || undefined,
            composition: textoEnabledFlag
              ? {
                  enabled: true,
                  selectedCompId: Number.isFinite(textoCompId)
                    ? textoCompId
                    : null,
                  selectedCompUrl: textoCompUrl || undefined,
                  compositionText: textoTextParam || undefined,
                }
              : undefined,
            creativoPrompt:
              activeTab === "creativo" ? creativoPrompt : undefined,
          });
        } catch (saveErr) {
          console.warn(
            "[PruebaNuevaInterfaz] Failed to save last state:",
            saveErr,
          );
        }

        // Build lastPayload without user_id for reuse in "generate more"
        const { user_id: _uid, ...lastPayload } = requestData;

        // Preload images before navigating to ensure smooth animation
        let preloadedImages: PreloadedImage[] = [];
        try {
          preloadedImages = await preloadGeneratedImages(
            imageIds,
            companyIdResult,
          );
        } catch (preloadErr) {
          console.warn(
            "[PruebaNuevaInterfaz] Image preload failed, continuing anyway:",
            preloadErr,
          );
        }

        // Play completion sound before navigating away
        try {
          const audio = new Audio("/images/clicksound.m4a");
          audio.play();
        } catch {}

        // Navigate to dedicated generated images page with preloaded data
        navigate("/redes-sociales/imagenes-generadas", {
          state: {
            imageIds,
            companyId: companyIdResult,
            lastPayload,
            preloadedImages,
          },
        });
      } else {
        throw new Error("No se recibieron imágenes generadas.");
      }
    } catch (e: any) {
      console.error("[PruebaNuevaInterfaz] handleGenerate error", e);
      if (e instanceof GenerateAdError && e.isActionable) {
        setGenError(e.message);
      } else {
        consecutiveErrorCountRef.current += 1;
        setGenError(
          "Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.",
        );
      }
      setShowLoadingScreen(false);
      generatingLockRef.current = false;
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateMore = async (): Promise<{
    imageIds?: number[];
    companyId?: string;
  } | void> => {
    // Immediate lock to prevent rapid clicks
    if (generatingLockRef.current) return;
    generatingLockRef.current = true;

    if (ids.length === 0) {
      generatingLockRef.current = false;
      return;
    }
    try {
      setGenError(null);
      setGenerating(true);
      setImagesReady(false);
      // Don't show loading screen — child component shows inline placeholder cards
      setLoadingExiting(false);
      // "Generar más" siempre pide 2 imágenes (`cantidad_imagenes: 2` más abajo).
      const canGenerate = await checkSubscriptionLimits(2);
      if (!canGenerate) {
        setGenerating(false);
        setShowLoadingScreen(false);
        generatingLockRef.current = false;
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();

      if (!profile?.id_company)
        throw new Error("No se encontró la empresa del usuario");

      let formatName = "";
      switch (format) {
        case "story":
          formatName = "9:16";
          break;
        case "square":
          formatName = "1:1";
          break;
        case "portrait":
          formatName = "2:3";
          break;
        case "banner":
          formatName = "16:9";
          break;
        case "editorial":
          formatName = "3:2";
          break;
        case "billboard":
          formatName = "21:9";
          break;
        case "post":
        default:
          formatName = "4:5";
      }

      const textoEnabledFlag = params.get("texto_enabled") === "1";
      const textoCompIdParam = params.get("texto_comp_id") || "";
      const textoCompUrl = params.get("texto_comp_url") || "";
      const textoTextParam = params.get("texto_texto") || "";
      const textoCompId = textoCompIdParam ? Number(textoCompIdParam) : NaN;

      const pose = params.get("pose") || "";
      const rawModeloModeMore = params.get("modelo_mode") as
        | "ia"
        | "guardados"
        | "personalizado"
        | null;
      const modeloModeParam =
        rawModeloModeMore === "ia" ||
        rawModeloModeMore === "guardados" ||
        rawModeloModeMore === "personalizado"
          ? rawModeloModeMore
          : null;
      const modeloGuardadoId = params.get("modelo_guardado_id") || "";
      const modeloGuardadoNombre = params.get("modelo_guardado_nombre") || "";
      const genero = params.get("genero") || "";
      const edad = params.get("edad") || "";
      const tipoCuerpo = params.get("tipo_cuerpo") || "";
      const tonoPiel = params.get("tono_piel") || "";
      const colorPelo = params.get("color_pelo") || "";
      const largoPelo = params.get("largo_pelo") || "";
      const descModelo = params.get("desc_modelo") || "";

      const escenarioDescParam = params.get("escenario_desc") || "";
      const escenarioFondoIdParam = params.get("escenario_fondo_id") || "";
      const escenarioFondoUrlParam = params.get("escenario_fondo_url") || "";
      const escenaGuardadaNombreArchivo =
        params.get("escena_guardada_nombre_archivo") || "";
      const escenarioParam = params.get("escenario") as ScenarioKey | null;

      let scenario: ScenarioKey = "sugerido";
      if (selectedScenarios.sugerido) scenario = "sugerido";
      else if (selectedScenarios.fondo_blanco) scenario = "fondo_blanco";
      else if (selectedScenarios.personalizado) scenario = "personalizado";
      else if (selectedScenarios.biblioteca) scenario = "biblioteca";
      else if (selectedScenarios.escenas_guardadas)
        scenario = "escenas_guardadas";
      else if (escenarioParam) scenario = escenarioParam;

      let backgroundMode:
        | "ia"
        | "blanco"
        | "elegido"
        | "descripcion"
        | "guardado" = "ia";
      let backgroundId: number | undefined;
      let backgroundUrl: string | undefined;
      let backgroundText: string | undefined;

      // In automatico mode, always use 'ia' for background (same logic as handleGenerate)
      if (activeTab === "automatico") {
        backgroundMode = "ia";
      } else {
        switch (scenario) {
          case "fondo_blanco":
            backgroundMode = "blanco";
            break;
          case "biblioteca": {
            backgroundMode = "elegido";
            const n = Number(escenarioFondoIdParam);
            if (Number.isFinite(n)) backgroundId = n;
            if (escenarioFondoUrlParam) backgroundUrl = escenarioFondoUrlParam;
            break;
          }
          case "personalizado":
            backgroundMode = "descripcion";
            if (escenarioDescParam.trim())
              backgroundText = escenarioDescParam.trim();
            break;
          case "escenas_guardadas":
            backgroundMode = "guardado";
            break;
          case "sugerido":
          default:
            backgroundMode = "ia";
        }
      }

      const variantIds = ids
        .map((s) => String(s))
        .map((s) => s.trim())
        .filter((s) => !!s && s !== "0");
      if (variantIds.length === 0)
        throw new Error("No hay productos válidos seleccionados");
      const mode = variantIds.length > 1 ? "multiple" : "single";

      // Determine API mode based on activeTab (same logic as handleGenerate)
      const modoForApi: "rapido" | "personalizado" | "creativo" =
        activeTab === "automatico"
          ? "rapido"
          : activeTab === "creativo"
            ? "creativo"
            : "personalizado";

      let composicion: "modelo" | "producto" = "producto";
      if (
        modeloEnabled &&
        (modeloModeParam === "ia" ||
          modeloModeParam === "guardados" ||
          modeloModeParam === "personalizado")
      ) {
        composicion = "modelo";
      }

      // Build request data - creativo mode doesn't need composicion or background_mode
      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds,
        mode,
        modo: modoForApi,
        cantidad_imagenes: 2,
        Format: formatName,
        new_ui: true,
      };

      // Only add composicion and background for non-creativo modes
      if (activeTab !== "creativo") {
        requestData.composicion = composicion;
        requestData.background_mode = backgroundMode;

        if (backgroundMode === "guardado" && escenaGuardadaNombreArchivo) {
          requestData.background_file_name = escenaGuardadaNombreArchivo;
        } else if (backgroundMode === "elegido") {
          if (
            typeof backgroundId === "number" &&
            Number.isFinite(backgroundId)
          ) {
            requestData.background_id = backgroundId;
          }
          if (!requestData.background_id && backgroundUrl) {
            requestData.background_url = backgroundUrl;
          }
        } else if (backgroundMode === "blanco") {
          requestData.background_id = 5;
        } else if (backgroundMode === "descripcion" && backgroundText) {
          requestData.background_text = backgroundText;
        }
      }

      if (textoEnabledFlag) {
        requestData.composition_mode = true;
        if (Number.isFinite(textoCompId)) {
          requestData.composition_id = textoCompId;
        } else if (textoCompUrl) {
          requestData.composition_url = textoCompUrl;
        }
        if (textoTextParam.trim()) {
          requestData.composition_text = textoTextParam.trim();
        }
      }

      if (modeloEnabled && pose && isFashionRubro) requestData.pose = pose;

      // Add vista_producto when modelo is off and single product
      const vistaProductoMore = params.get("vista_producto") || "";
      if (vistaProductoMore && !modeloEnabled && ids.length === 1) {
        // Multi-vista: mandar array. Single: mandar string.
        if (multiVistaActive) {
          requestData.vista_producto = selectedVistas;
        } else {
          requestData.vista_producto = vistaProductoMore;
        }
      }
      if (modeloEnabled && modeloModeParam) {
        requestData.modelo_mode = modeloModeParam;
        if (modeloModeParam === "guardados" && modeloGuardadoId) {
          requestData.modelo_guardado_id = modeloGuardadoId;
          if (modeloGuardadoNombre)
            requestData.modelo_guardado_nombre = modeloGuardadoNombre;
        } else if (modeloModeParam === "personalizado") {
          if (genero) requestData.genero = genero;
          if (edad) requestData.edad = edad;
          if (tipoCuerpo) requestData.tipo_cuerpo = tipoCuerpo;
          if (tonoPiel) requestData.tono_piel = tonoPiel;
          if (colorPelo) requestData.color_pelo = colorPelo;
          if (largoPelo) requestData.largo_pelo = largoPelo;
          if (descModelo.trim()) requestData.desc_modelo = descModelo.trim();
        }
      }

      requestData.modo_pro = calidad === "pro";

      // Add creative prompt if in creativo tab
      if (activeTab === "creativo" && creativoPrompt.trim()) {
        requestData.prompt = creativoPrompt.trim();
      }

      // Add instruccion adicional if provided (personalizado tab)
      if (activeTab === "personalizado" && instruccionAdicional.trim()) {
        requestData.instruccion_adicional = instruccionAdicional.trim();
      }

      try {
        console.log(
          "[PruebaNuevaInterfaz] handleGenerateMore payload",
          requestData,
        );
      } catch {}

      const genResult = await generateClassicImages({
        payload: requestData,
        companyId: profile.id_company,
        expectedCount:
          requestData.tipo === "imagen"
            ? (requestData.cantidad_imagenes as number | undefined)
            : undefined,
      });
      const data: any = {
        imagenes_generadas_ids: genResult.imageIds,
        id_company: genResult.companyId,
      };
      try {
        console.log("[PruebaNuevaInterfaz] handleGenerateMore response", data);
      } catch {}

      if (
        Array.isArray(data?.imagenes_generadas_ids) &&
        data.imagenes_generadas_ids.length > 0
      ) {
        const newImageIds = data.imagenes_generadas_ids as number[];
        const companyIdResult =
          (data.id_company as string) || profile.id_company;

        setGenError(null);
        consecutiveErrorCountRef.current = 0; // Reset on success
        try {
          window.sessionStorage.setItem(
            "atlas:crear-anuncio:has-generated",
            "1",
          );
        } catch {}
        setShowLoadingScreen(false);
        generatingLockRef.current = false;
        // Return data so the child component can handle display inline
        // Do NOT update generatedImageIds here — that would change the imageIds prop
        // and trigger a full re-fetch in GeneratedImagesInline's useEffect
        return { imageIds: newImageIds, companyId: companyIdResult };
      } else {
        throw new Error("No se recibieron imágenes generadas.");
      }
    } catch (e: any) {
      console.error("[PruebaNuevaInterfaz] handleGenerateMore error", e);
      if (e instanceof GenerateAdError && e.isActionable) {
        setGenError(e.message);
      } else {
        consecutiveErrorCountRef.current += 1;
        setGenError(
          "Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.",
        );
      }
      setShowLoadingScreen(false);
      generatingLockRef.current = false;
    } finally {
      setGenerating(false);
    }
  };

  const handleReturnToConfig = () => {
    setShowResults(false);
  };

  const showSidePanel =
    showFormatoMenu ||
    showEscenaMenu ||
    showBibliotecaMenu ||
    showEscenasGuardadasMenu ||
    showTextoMenu ||
    showBrandAnalysisMenu ||
    showCreativoIdeasMenu ||
    showCreativoHistorialMenu ||
    showInstruccionMenu ||
    showModeloPersonalizadoMenu ||
    showModeloGuardadoMenu ||
    showPoseMenu ||
    showVistaProductoMenu;

  // Drag handlers for mobile menus
  const handleFormatoTouchStart = (e: React.TouchEvent) => {
    formatoDragStartY.current = e.touches[0].clientY;
  };
  const handleFormatoTouchMove = (e: React.TouchEvent) => {
    if (formatoDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - formatoDragStartY.current;
    if (deltaY > 0) setFormatoMenuDragY(deltaY);
  };
  const handleFormatoTouchEnd = () => {
    if (formatoMenuDragY > 100) {
      closeFormatoMenu();
    }
    setFormatoMenuDragY(0);
    formatoDragStartY.current = null;
  };

  const handleEscenaTouchStart = (e: React.TouchEvent) => {
    escenaDragStartY.current = e.touches[0].clientY;
  };
  const handleEscenaTouchMove = (e: React.TouchEvent) => {
    if (escenaDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - escenaDragStartY.current;
    if (deltaY > 0) setEscenaMenuDragY(deltaY);
  };
  const handleEscenaTouchEnd = () => {
    if (escenaMenuDragY > 100) {
      closeEscenaMenu();
    }
    setEscenaMenuDragY(0);
    escenaDragStartY.current = null;
  };

  const handleInstruccionTouchStart = (e: React.TouchEvent) => {
    instruccionDragStartY.current = e.touches[0].clientY;
  };
  const handleInstruccionTouchMove = (e: React.TouchEvent) => {
    if (instruccionDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - instruccionDragStartY.current;
    if (deltaY > 0) setInstruccionMenuDragY(deltaY);
  };
  const handleInstruccionTouchEnd = () => {
    if (instruccionMenuDragY > 100) {
      closeInstruccionMenu();
    }
    setInstruccionMenuDragY(0);
    instruccionDragStartY.current = null;
  };

  const handleInstruccionSubmit = () => {
    setInstruccionMenuAnimated(false);
    setTimeout(() => setShowInstruccionMenu(false), 300);
  };

  const handleGenerateCreativoIdeas = async () => {
    if (creativoIdeasLoading) return;
    try {
      setCreativoIdeasLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const variantIds = ids.filter(Boolean);
      if (variantIds.length === 0) return;

      const data = await postPromptInstrucciones({
        user_id: user.id,
        variante_id: variantIds,
        modo: "generar_ideas",
        tipo_de_prompt: "creativo",
      });
      // Handle new format with posts array containing titulo and prompt
      if (Array.isArray(data?.posts)) {
        const newIdeas = data.posts
          .filter((p: any) => p?.titulo && p?.prompt)
          .map((p: any) => ({ titulo: p.titulo, prompt: p.prompt }));
        if (newIdeas.length > 0) {
          setCreativoIdeas((prev) => [...prev, ...newIdeas]);
          setShowCreativoIdeasMenu(true);
        }
      } else if (Array.isArray(data?.ideas)) {
        // Fallback for old format (string array)
        const newIdeas = data.ideas.map((idea: string) => ({
          titulo: idea.slice(0, 50) + (idea.length > 50 ? "..." : ""),
          prompt: idea,
        }));
        setCreativoIdeas((prev) => [...prev, ...newIdeas]);
        setShowCreativoIdeasMenu(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCreativoIdeasLoading(false);
    }
  };

  const [mejorandoInstruccion, setMejorandoInstruccion] =
    useState<boolean>(false);

  const handleMejorarInstruccion = async () => {
    if (mejorandoInstruccion || !creativoPrompt.trim()) return;
    try {
      setMejorandoInstruccion(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const variantIds = ids.filter(Boolean);
      if (variantIds.length === 0) return;

      const data = await postPromptInstrucciones({
        user_id: user.id,
        variante_id: variantIds,
        modo: "mejorar",
        prompt: creativoPrompt.trim(),
        tipo_de_prompt: "creativo",
      });
      if (data?.prompt_mejorado) {
        setCreativoPrompt(data.prompt_mejorado);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setMejorandoInstruccion(false);
    }
  };

  // Rubro-based example scenes
  const escenaExamplesMap: Record<string, string[]> = {
    "Moda e Indumentaria": [
      "En una calle empedrada de una ciudad europea al atardecer",
      "En un estudio fotográfico con fondo de tela beige",
      "En una cafetería moderna con luz natural entrando por ventanales",
      "En un parque con vegetación verde y luz suave de la mañana",
    ],
    "Hogar y Decoración": [
      "En un living minimalista con luz natural y paredes blancas",
      "En una habitación con estilo nórdico y plantas decorativas",
      "En una terraza con vista al jardín durante la hora dorada",
      "En una cocina moderna con mesada de mármol blanco",
    ],
    "Belleza y Cuidado Personal": [
      "Sobre una superficie de mármol con pétalos de flores alrededor",
      "En un baño elegante con toallas blancas y velas aromáticas",
      "Sobre una mesa de madera clara con plantas verdes de fondo",
      "En un tocador con espejo dorado y luz cálida",
    ],
    Tecnología: [
      "En un escritorio minimalista con fondo oscuro y luces LED",
      "En un espacio de coworking moderno con buena iluminación",
      "Sobre una superficie reflectante con fondo degradado azul",
      "En una mesa blanca con setup tecnológico de fondo",
    ],
    "Salud y Bienestar": [
      "En un espacio zen con bambú y piedras naturales",
      "En un ambiente claro y sereno con plantas medicinales",
      "Sobre una superficie de madera con ingredientes naturales alrededor",
      "En un jardín tranquilo con luz filtrada entre las hojas",
    ],
    Automotriz: [
      "En una ruta panorámica al atardecer con montañas de fondo",
      "En un garaje moderno con piso de concreto pulido",
      "En una calle urbana de noche con reflejos de luces de neón",
      "En un paisaje costero con el mar de fondo",
    ],
    Gastronomía: [
      "En una mesa rústica de madera con ingredientes frescos alrededor",
      "En una cocina profesional con fondo de acero inoxidable",
      "En un restaurante elegante con luz de velas",
      "En una tabla de madera con hierbas frescas y especias",
    ],
    Juguetería: [
      "En una habitación infantil colorida con estantes de fondo",
      "En un parque de juegos con césped verde y cielo azul",
      "Sobre una alfombra suave con cubos de colores alrededor",
      "En un espacio luminoso con globos y decoraciones festivas",
    ],
    "Arte y Manualidades": [
      "En un taller de arte con pinturas y pinceles de fondo",
      "Sobre una mesa de madera con materiales creativos alrededor",
      "En un espacio bohemio con telas coloridas y buena luz natural",
      "En un estudio con paredes de ladrillo y obras de arte",
    ],
    Librería: [
      "En una biblioteca acogedora con estantes llenos de libros",
      "Junto a una ventana con luz natural y una taza de café",
      "En un rincón de lectura con sillón y lámpara cálida",
      "Sobre una mesa de madera oscura con hojas otoñales",
    ],
    "Creación de Contenido": [
      "En un set de filmación con luces profesionales de fondo",
      "En un escritorio de creador con micrófono y pantallas",
      "En un espacio moderno con neón y decoración urbana",
      "En un estudio con fondo degradado y ring light",
    ],
  };
  const fallbackExamples = [
    "En un estudio fotográfico con fondo blanco y luz suave",
    "Sobre una mesa de mármol con plantas verdes de fondo",
    "En un espacio moderno con buena iluminación natural",
    "En una superficie de madera con fondo desenfocado elegante",
  ];
  const escenaExamples = escenaExamplesMap[userRubro] || fallbackExamples;

  // Fetch escena historial when menu opens
  const fetchEscenaHistorial = async () => {
    if (!companyId) return;
    try {
      const { data, error } = await supabase
        .from("historial_prompts")
        .select("prompt")
        .eq("id_company", companyId)
        .eq("tipo", "escena")
        .order("created_at", { ascending: false })
        .limit(10);
      if (!error && data) {
        const unique = [...new Set(data.map((d: any) => d.prompt as string))];
        setEscenaHistorial(unique);
      }
    } catch {}
  };

  // Fetch creativo historial when menu opens
  const fetchCreativoHistorial = async () => {
    if (!companyId) return;
    try {
      const { data, error } = await supabase
        .from("historial_prompts")
        .select("prompt")
        .eq("id_company", companyId)
        .eq("tipo", "creativo")
        .order("created_at", { ascending: false })
        .limit(15);
      if (!error && data) {
        const unique = [...new Set(data.map((d: any) => d.prompt as string))];
        setCreativoHistorial(unique);
      }
    } catch {}
  };

  // Mejorar escena prompt (same API as modo creativo)
  const handleMejorarEscena = async () => {
    if (mejorandoEscena || !escenaPrompt.trim()) return;
    try {
      setMejorandoEscena(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const variantIds = ids.filter(Boolean);
      if (variantIds.length === 0) return;
      const data = await postPromptInstrucciones({
        user_id: user.id,
        variante_id: variantIds,
        modo: "mejorar",
        prompt: escenaPrompt.trim(),
        tipo_de_prompt: "escena",
      });
      if (data?.prompt_mejorado) {
        setEscenaPrompt(data.prompt_mejorado);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setMejorandoEscena(false);
    }
  };

  const handleGenerateEscenaIdeas = async () => {
    if (escenaIdeasLoading) return;
    try {
      setEscenaIdeasLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const variantIds = ids.filter(Boolean);
      if (variantIds.length === 0) return;
      const data = await postPromptInstrucciones({
        user_id: user.id,
        variante_id: variantIds,
        modo: "generar_ideas",
        tipo_de_prompt: "escena",
      });
      if (Array.isArray(data?.posts)) {
        const newIdeas = data.posts
          .filter((p: any) => p?.titulo || p?.prompt)
          .map((p: any) => p.prompt || p.titulo);
        if (newIdeas.length > 0) {
          setEscenaGeneratedIdeas((prev) => [...prev, ...newIdeas]);
        }
      } else if (Array.isArray(data?.ideas)) {
        setEscenaGeneratedIdeas((prev) => [...prev, ...data.ideas]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setEscenaIdeasLoading(false);
    }
  };

  const handleEscenaSubmit = () => {
    const trimmed = escenaPrompt.trim();
    if (!trimmed) return;
    const qp = new URLSearchParams(location.search);
    qp.set("escenario", "personalizado");
    qp.set("escenario_desc", trimmed);
    navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
    setSelectedScenarios({
      sugerido: false,
      fondo_blanco: false,
      personalizado: true,
      biblioteca: false,
      escenas_guardadas: false,
    });
    closeEscenaMenu();
  };

  const handleBibliotecaTouchStart = (e: React.TouchEvent) => {
    bibliotecaDragStartY.current = e.touches[0].clientY;
  };
  const handleBibliotecaTouchMove = (e: React.TouchEvent) => {
    if (bibliotecaDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - bibliotecaDragStartY.current;
    if (deltaY > 0) setBibliotecaMenuDragY(deltaY);
  };
  const handleBibliotecaTouchEnd = () => {
    if (bibliotecaMenuDragY > 100) {
      closeBibliotecaMenu();
    }
    setBibliotecaMenuDragY(0);
    bibliotecaDragStartY.current = null;
  };

  const fetchBibliotecaFondos = async () => {
    if (bibliotecaFondos.length > 0 || bibliotecaLoading) return;
    try {
      setBibliotecaLoading(true);
      const { data, error } = await supabase
        .from("fondos")
        .select("id, link_img, categoria, nombre, rubros");
      if (error) throw error;
      const filtered = ((data as any) || []).filter((f: any) => {
        const arr = (f?.rubros as string[] | undefined) || [];
        if (arr.length === 1 && arr[0] === "all") return true;
        return true;
      });
      setBibliotecaFondos(filtered);
      if (filtered.length > 0 && !bibliotecaActiveCategoria) {
        const cats = [
          ...new Set(filtered.map((f: any) => f.categoria || "Otros")),
        ].sort();
        setBibliotecaActiveCategoria(cats[0] as string);
      }
    } catch (e) {
      console.error("Error cargando fondos", e);
    } finally {
      setBibliotecaLoading(false);
    }
  };

  const bibliotecaCategorias = useMemo(() => {
    const cats = [
      ...new Set(bibliotecaFondos.map((f) => f.categoria || "Otros")),
    ];
    return cats.sort();
  }, [bibliotecaFondos]);

  const bibliotecaItemsActuales = useMemo(() => {
    return bibliotecaFondos.filter(
      (f) => (f.categoria || "Otros") === bibliotecaActiveCategoria,
    );
  }, [bibliotecaFondos, bibliotecaActiveCategoria]);

  const handleBibliotecaSubmit = () => {
    if (!bibliotecaSelectedFondoId || !bibliotecaSelectedFondoUrl) return;
    const qp = new URLSearchParams(location.search);
    qp.set("escenario", "biblioteca");
    qp.set("escenario_fondo_id", String(bibliotecaSelectedFondoId));
    qp.set("escenario_fondo_url", bibliotecaSelectedFondoUrl);
    navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
    setSelectedScenarios({
      sugerido: false,
      fondo_blanco: false,
      personalizado: false,
      biblioteca: true,
      escenas_guardadas: false,
    });
    setBibliotecaMenuAnimated(false);
    setTimeout(() => setShowBibliotecaMenu(false), 300);
  };

  // --- Escenas Guardadas handlers ---
  const resetAddSceneState = () => {
    setAddSceneName("");
    setAddSceneFile(null);
    setAddScenePreviewUrl(null);
    setAddSceneSaving(false);
    setAddSceneError(null);
  };

  const handleDeleteScene = async (sceneId: string) => {
    const scene = savedScenes.find((s) => s.id === sceneId);
    if (!scene) return;
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      const idCompany = (profile as any)?.id_company;
      if (!idCompany) return;
      await supabase.storage
        .from("fondos_personalizados")
        .remove([`${idCompany}/full/${scene.nombre_archivo}`]);
      await supabase.from("fondos_personalizados").delete().eq("id", sceneId);
      if (selectedSavedSceneId === sceneId) setSelectedSavedSceneId(null);
      setSavedScenes((prev) => prev.filter((s) => s.id !== sceneId));
    } catch (e) {
      console.error("Error deleting scene:", e);
    }
    setOpenSceneMenuId(null);
  };

  const handleDeleteModel = async (modelId: number) => {
    const model = savedModels.find((m) => m.id === modelId);
    if (!model) return;
    setOpenModelMenuId(null);
    setDeletingModelId(modelId);
    // Wait for fade-out animation
    await new Promise((r) => setTimeout(r, 300));
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      const idCompany = (profile as any)?.id_company;
      if (!idCompany) return;
      const { data: modelRow } = await supabase
        .from("modelos")
        .select("foto_file_name")
        .eq("id", modelId)
        .single();
      const fileName = (modelRow as any)?.foto_file_name;
      if (fileName)
        await supabase.storage
          .from("modelos")
          .remove([`${idCompany}/full/${fileName}`]);
      await supabase.from("modelos").delete().eq("id", modelId);
      if (selectedSavedModelId === modelId) setSelectedSavedModelId(null);
      setSavedModels((prev) => prev.filter((m) => m.id !== modelId));
    } catch (e) {
      console.error("Error deleting model:", e);
    }
    setDeletingModelId(null);
  };

  const handleSceneFileSelect = async (file: File) => {
    // Formatos bloqueados (RAW/TIFF/GIF/SVG — ADR-590)
    if (isUnsupportedUploadFile(file)) {
      setAddSceneError(UNSUPPORTED_FORMAT_MESSAGE);
      return;
    }
    if (isHeicFile(file) || isWebpFile(file)) {
      try {
        setConvertingHeic(true);
        const { file: converted, url } = await convertHeicToJpeg(file, 0.85);
        setAddSceneFile(converted);
        setAddScenePreviewUrl(url);
        setAddSceneError(null);
      } catch {
        setAddSceneError(
          "No pudimos procesar la imagen. Probá con una imagen en formato PNG o JPG.",
        );
      } finally {
        setConvertingHeic(false);
      }
    } else {
      setAddSceneFile(file);
      setAddScenePreviewUrl(URL.createObjectURL(file));
      setAddSceneError(null);
    }
  };

  const handleEscenasGuardadasTouchStart = (e: React.TouchEvent) => {
    escenasGuardadasDragStartY.current = e.touches[0].clientY;
  };
  const handleEscenasGuardadasTouchMove = (e: React.TouchEvent) => {
    if (escenasGuardadasDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - escenasGuardadasDragStartY.current;
    if (deltaY > 0) setEscenasGuardadasMenuDragY(deltaY);
  };
  const handleEscenasGuardadasTouchEnd = () => {
    if (escenasGuardadasMenuDragY > 100) {
      closeEscenasGuardadasMenu();
    }
    setEscenasGuardadasMenuDragY(0);
    escenasGuardadasDragStartY.current = null;
  };

  const fetchSavedScenes = async () => {
    try {
      setSavedScenesLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      const idCompany = (profile as any)?.id_company;
      if (!idCompany) return;

      const { data, error } = await supabase
        .from("fondos_personalizados")
        .select("id, nombre, nombre_archivo")
        .eq("id_company", idCompany)
        .order("created_at", { ascending: false });
      if (error) throw error;

      const rows = (data as any[]) || [];
      const withSigned = await Promise.all(
        rows.map(async (s) => {
          let signedUrl: string | undefined;
          const fileName = s?.nombre_archivo;
          if (fileName) {
            try {
              const { data: fullSigned } = await supabase.storage
                .from("fondos_personalizados")
                .createSignedUrl(`${idCompany}/full/${fileName}`, 3600);
              if (fullSigned?.signedUrl) signedUrl = fullSigned.signedUrl;
            } catch {}
          }
          return {
            id: s.id as string,
            nombre: s.nombre as string | null,
            nombre_archivo: s.nombre_archivo as string,
            signedUrl,
          };
        }),
      );
      setSavedScenes(withSigned);
    } catch (e) {
      console.error(e);
    } finally {
      setSavedScenesLoading(false);
    }
  };

  const handleAddSceneSave = async () => {
    try {
      setAddSceneError(null);
      if (!addSceneFile) throw new Error("Subí una imagen de la escena");
      setAddSceneSaving(true);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");
      const { data: profile } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", user.id)
        .single();
      const idCompany = (profile as any)?.id_company;
      if (!idCompany) throw new Error("No se encontró la empresa");

      const ext = (addSceneFile.name.split(".").pop() || "jpg").toLowerCase();
      const base = addSceneName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      const fileName = `${Date.now()}-${base || "escena"}.${ext}`;
      const targetPath = `${idCompany}/full/${fileName}`;

      const { error: upErr } = await supabase.storage
        .from("fondos_personalizados")
        .upload(targetPath, addSceneFile, {
          cacheControl: "3600",
          upsert: false,
          contentType: addSceneFile.type || "image/jpeg",
        });
      if (upErr) throw upErr;

      const { error: insErr } = await supabase
        .from("fondos_personalizados")
        .insert({
          id_company: idCompany,
          nombre: addSceneName.trim() || null,
          nombre_archivo: fileName,
        });
      if (insErr) throw insErr;

      await fetchSavedScenes();
      setShowAddSceneForm(false);
      resetAddSceneState();
    } catch (e: any) {
      console.error("Error subiendo escena:", e);
      setAddSceneError(e?.message || "No se pudo guardar la escena");
    } finally {
      setAddSceneSaving(false);
    }
  };

  const handleEscenaGuardadaSelect = (sceneId: string | null) => {
    setSelectedSavedSceneId(sceneId);
  };

  const handleEscenasGuardadasSubmit = () => {
    if (!selectedSavedSceneId) return;
    const scene = savedScenes.find((s) => s.id === selectedSavedSceneId);
    if (!scene) return;
    const qp = new URLSearchParams(location.search);
    qp.set("escenario", "escenas_guardadas");
    qp.set("escena_guardada_id", scene.id);
    qp.set("escena_guardada_nombre_archivo", scene.nombre_archivo);
    if (scene.signedUrl) qp.set("escena_guardada_url", scene.signedUrl);
    navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
    setSelectedScenarios({
      sugerido: false,
      fondo_blanco: false,
      personalizado: false,
      biblioteca: false,
      escenas_guardadas: true,
    });
    setEscenasGuardadasMenuAnimated(false);
    setTimeout(() => setShowEscenasGuardadasMenu(false), 300);
  };

  const handleTextoTouchStart = (e: React.TouchEvent) => {
    textoDragStartY.current = e.touches[0].clientY;
  };
  const handleTextoTouchMove = (e: React.TouchEvent) => {
    if (textoDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - textoDragStartY.current;
    if (deltaY > 0) setTextoMenuDragY(deltaY);
  };
  const handleTextoTouchEnd = () => {
    if (textoMenuDragY > 100) {
      closeTextoMenu();
    }
    setTextoMenuDragY(0);
    textoDragStartY.current = null;
  };

  const fetchTextoComposiciones = async () => {
    if (textoComposiciones.length > 0 || textoLoading) return;
    try {
      setTextoLoading(true);
      const { data, error } = await supabase
        .from("composiciones")
        .select("id, link_img, categoria, nombre");
      if (error) throw error;
      setTextoComposiciones((data as any) || []);
      if (data && data.length > 0 && !textoActiveCategoria) {
        const cats = [
          ...new Set((data as any[]).map((c: any) => c.categoria || "Otros")),
        ].sort((a, b) => {
          if (a === "Moderno" && b !== "Moderno") return -1;
          if (b === "Moderno" && a !== "Moderno") return 1;
          return a.localeCompare(b);
        });
        setTextoActiveCategoria(cats[0] as string);
      }
    } catch (e) {
      console.error("Error cargando composiciones", e);
    } finally {
      setTextoLoading(false);
    }
  };

  const textoCategorias = useMemo(() => {
    const cats = [
      ...new Set(textoComposiciones.map((c) => c.categoria || "Otros")),
    ];
    return cats.sort((a, b) => {
      if (a === "Moderno" && b !== "Moderno") return -1;
      if (b === "Moderno" && a !== "Moderno") return 1;
      return a.localeCompare(b);
    });
  }, [textoComposiciones]);

  const textoItemsActuales = useMemo(() => {
    return textoComposiciones.filter(
      (c) => (c.categoria || "Otros") === textoActiveCategoria,
    );
  }, [textoComposiciones, textoActiveCategoria]);

  const textoCanSave =
    textoSelectedCompId !== null && textoText.trim().length > 0;

  const handleTextoSubmit = () => {
    if (!textoCanSave) return;
    const qp = new URLSearchParams(location.search);
    qp.set("texto_enabled", "1");
    qp.set("texto_comp_id", String(textoSelectedCompId));
    if (textoSelectedCompUrl) {
      qp.set("texto_comp_url", textoSelectedCompUrl);
    }
    qp.set("texto_texto", textoText.trim());
    navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
    closeTextoMenu();
  };

  // Block scroll when side panel/menu is open
  useEffect(() => {
    if (showSidePanel) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [showSidePanel]);

  return (
    <div className="pt-1 px-4 sm:p-8 min-h-[100dvh] md:min-h-screen bg-surface-mobile sm:bg-transparent pb-0 transition-all duration-300 max-w-4xl mx-auto">
      {/* Desktop sticky header — Apple frosted glass nav bar */}
      <div
        className="hidden sm:block fixed top-0 right-0 z-40"
        style={{ left: sidebarWidth }}
      >
        <div className="bg-white border-b border-black/[0.06] sm:rounded-bl-lg">
          <div className="max-w-4xl mx-auto px-8">
            <div className="flex items-center justify-between h-14">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center gap-1 text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span>Atrás</span>
                </button>
              </div>
              <h1 className="absolute left-1/2 -translate-x-1/2 text-[17px] font-semibold text-brand-black/90">
                Diseña tus imágenes
              </h1>
              <div className="flex items-center gap-2">
                {!multiPoseActive && !multiVistaActive && (
                  <div className="flex items-center bg-black/[0.05] rounded-[10px] overflow-hidden">
                    <button
                      type="button"
                      aria-label="Disminuir cantidad"
                      onClick={() => {
                        setImageCount((c) => {
                          const next = Math.max(1, c - 1);
                          const qp = new URLSearchParams(location.search);
                          qp.set("cantidad_imagenes", String(next));
                          navigate(`${location.pathname}?${qp.toString()}`, {
                            replace: true,
                          });
                          return next;
                        });
                        setCountAnimating(true);
                        setTimeout(() => setCountAnimating(false), 150);
                      }}
                      disabled={imageCount <= 1 || generating}
                      className="w-9 h-9 flex items-center justify-center text-[#1644EB] hover:bg-black/[0.05] active:bg-black/[0.08] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span
                      className={`text-[15px] font-medium text-brand-black/90 w-6 text-center tabular-nums transition-transform duration-150 ${countAnimating ? "scale-110" : "scale-100"}`}
                    >
                      {effectiveImageCount}
                    </span>
                    <button
                      type="button"
                      aria-label="Aumentar cantidad"
                      onClick={() => {
                        setImageCount((c) => {
                          const next = Math.min(8, c + 1);
                          const qp = new URLSearchParams(location.search);
                          qp.set("cantidad_imagenes", String(next));
                          navigate(`${location.pathname}?${qp.toString()}`, {
                            replace: true,
                          });
                          return next;
                        });
                        setCountAnimating(true);
                        setTimeout(() => setCountAnimating(false), 150);
                      }}
                      disabled={imageCount >= 8 || generating}
                      className="w-9 h-9 flex items-center justify-center text-[#1644EB] hover:bg-black/[0.05] active:bg-black/[0.08] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={generating || ids.length === 0}
                  className={`inline-flex items-center justify-center gap-2 px-5 h-9 rounded-[10px] text-[15px] font-medium transition-all ${
                    generating || ids.length === 0
                      ? "bg-[#1644EB]/40 text-white/60 cursor-not-allowed"
                      : "bg-[#1644EB] hover:bg-[#1238C8] active:bg-[#0F2FA8] text-white"
                  }`}
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    "Generar"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!showResults ? (
        <div className="sm:bg-transparent p-0 mt-3 sm:mt-6 relative">
          {/* Floating error toast — overlay */}
          {genError && (
            <div
              className="fixed bottom-6 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-6 z-[9999] pointer-events-none"
              style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
            >
              <div className="pointer-events-auto mx-4 sm:mx-0 w-[calc(100vw-2rem)] sm:w-[380px] bg-white rounded-[13px] shadow-[0_8px_32px_rgba(0,0,0,0.18),0_0_0_0.5px_rgba(0,0,0,0.06)] p-4">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-rounded text-[22px] text-[#FF3B30] shrink-0">
                    error
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-semibold text-brand-black/90">
                      Error al generar
                    </p>
                    <p className="text-[13px] text-[#3C3C43]/60 mt-0.5">
                      {genError}
                    </p>
                    {consecutiveErrorCountRef.current >= 2 && (
                      <p className="text-[13px] text-[#3C3C43]/60 mt-1">
                        Si el problema persiste,{" "}
                        <a
                          href="https://wa.me/5491122635392?text=Hola%2C%20tuve%20un%20error%20al%20generar%20im%C3%A1genes%20en%20Atlas"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#1644EB] underline sm:hidden"
                        >
                          contactanos
                        </a>
                        <button
                          type="button"
                          onClick={() => setShowContactQR(true)}
                          className="text-[#1644EB] underline hidden sm:inline"
                        >
                          contactanos
                        </button>
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setGenError(null)}
                    className="text-[#3C3C43]/30 hover:text-[#3C3C43]/60 transition-colors shrink-0 -mt-0.5"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Product selection — Apple grouped list style */}
          <div className="mt-0 sm:mt-2">
            <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">
              {variants.length === 1
                ? "Producto seleccionado"
                : `${variants.length || 0} productos seleccionados`}
            </p>
            {loading ? (
              <div className="bg-white rounded-[13px] overflow-hidden divide-y divide-black/[0.08]">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 px-4 py-[11px]"
                  >
                    <div className="shrink-0 w-10 h-10 rounded-[8px] bg-neutral-100 animate-pulse" />
                    <div className="h-3.5 w-1/3 rounded bg-neutral-100 animate-pulse" />
                  </div>
                ))}
              </div>
            ) : variants.length === 0 ? (
              <div className="bg-white rounded-[13px] px-4 py-[11px]">
                <span className="text-[15px] text-brand-black/40">
                  No hay productos seleccionados
                </span>
              </div>
            ) : (
              <div className="bg-white rounded-[13px] overflow-hidden">
                {variants.map((v, i) => {
                  const url = principalImageUrl(v);
                  const dx = swipeX[v.id] || 0;
                  const isDismissing = swipeDismissing === v.id;
                  return (
                    <React.Fragment key={v.id}>
                      {i > 0 && (
                        <div
                          className="ml-[60px] h-px bg-black/[0.08]"
                          style={{
                            opacity: isDismissing ? 0 : 1,
                            transition: "opacity 0.3s",
                          }}
                        />
                      )}
                      <div
                        className="relative overflow-hidden"
                        style={{
                          maxHeight: isDismissing ? 0 : 200,
                          opacity: isDismissing ? 0 : 1,
                          transition: isDismissing
                            ? "max-height 0.3s ease-out, opacity 0.2s"
                            : "none",
                        }}
                      >
                        {/* Red delete action behind */}
                        <div
                          className="absolute inset-y-0 right-0 flex items-center justify-center bg-[#FF3B30]"
                          style={{
                            width: Math.abs(dx) > 0 ? "100%" : 0,
                            transition:
                              swipeStartX.current[v.id] !== undefined
                                ? "none"
                                : "width 0.3s ease-out",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => handleRemoveProduct(v.id)}
                            className="absolute right-0 h-full flex items-center justify-center text-white text-[15px] font-medium"
                            style={{
                              width: Math.max(Math.abs(dx), 90),
                              minWidth: 90,
                            }}
                          >
                            Eliminar
                          </button>
                        </div>
                        {/* Swipeable row */}
                        <div
                          className="flex items-center gap-3 px-4 py-[11px] bg-white relative"
                          style={{
                            transform: `translateX(${dx}px)`,
                            transition:
                              swipeStartX.current[v.id] !== undefined
                                ? "none"
                                : "transform 0.35s cubic-bezier(0.25, 0.1, 0.25, 1)",
                          }}
                          onTouchStart={(e) => {
                            swipeStartX.current[v.id] = e.touches[0].clientX;
                            swipeStartY.current[v.id] = e.touches[0].clientY;
                            swipeLocked.current[v.id] = false;
                            (swipeStartX.current as any)[`${v.id}_base`] =
                              swipeX[v.id] || 0;
                          }}
                          onTouchMove={(e) => {
                            const startX = swipeStartX.current[v.id];
                            const startY = swipeStartY.current[v.id];
                            if (startX === undefined) return;
                            const deltaX = e.touches[0].clientX - startX;
                            const deltaY = e.touches[0].clientY - (startY ?? 0);
                            if (
                              !swipeLocked.current[v.id] &&
                              (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8)
                            ) {
                              swipeLocked.current[v.id] = true;
                              if (Math.abs(deltaY) > Math.abs(deltaX)) {
                                delete swipeStartX.current[v.id];
                                return;
                              }
                            }
                            const baseDx =
                              (swipeStartX.current as any)[`${v.id}_base`] || 0;
                            const newDx = Math.min(0, baseDx + deltaX);
                            e.preventDefault();
                            setSwipeX((prev) => ({ ...prev, [v.id]: newDx }));
                          }}
                          onTouchEnd={() => {
                            delete swipeStartX.current[v.id];
                            delete swipeStartY.current[v.id];
                            const currentDx = swipeX[v.id] || 0;
                            if (currentDx < -180) {
                              // Full swipe — slide off screen then dismiss
                              setSwipeX((prev) => ({
                                ...prev,
                                [v.id]: -window.innerWidth,
                              }));
                              setTimeout(() => handleRemoveProduct(v.id), 350);
                            } else if (currentDx < -45) {
                              // Partial — snap to show delete button
                              setSwipeX((prev) => ({ ...prev, [v.id]: -90 }));
                            } else {
                              // Light — snap back
                              setSwipeX((prev) => ({ ...prev, [v.id]: 0 }));
                            }
                          }}
                        >
                          <div className="shrink-0 w-10 h-10 rounded-[8px] overflow-hidden bg-black/[0.03]">
                            {url ? (
                              <img
                                src={url}
                                alt={v.nombre}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-brand-black/20">
                                <ImageIcon className="w-4 h-4" />
                              </div>
                            )}
                          </div>
                          <span className="flex-1 min-w-0 text-[15px] text-brand-black/90 truncate">
                            {v.nombre}
                          </span>
                          {/* Mobile: toggle swipe delete */}
                          <button
                            type="button"
                            className="md:hidden shrink-0 w-8 h-8 flex items-center justify-center rounded-full active:bg-black/[0.05] transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSwipeX((prev) => ({
                                ...prev,
                                [v.id]: (prev[v.id] || 0) < 0 ? 0 : -90,
                              }));
                            }}
                          >
                            <MoreHorizontal className="w-[18px] h-[18px] text-[#8E8E93]" />
                          </button>
                          {/* Desktop: ellipsis menu */}
                          <div className="hidden md:block shrink-0">
                            <button
                              type="button"
                              data-product-menu={v.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setProductMenuOpen((prev) =>
                                  prev === v.id ? null : v.id,
                                );
                              }}
                              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/[0.05] active:bg-black/[0.08] transition-colors"
                            >
                              <MoreHorizontal className="w-[18px] h-[18px] text-[#8E8E93]" />
                            </button>
                            {productMenuOpen === v.id &&
                              ReactDOM.createPortal(
                                <>
                                  <div
                                    className="fixed inset-0 z-[9998]"
                                    onClick={() => setProductMenuOpen(null)}
                                  />
                                  <div
                                    className="fixed z-[9999] bg-white rounded-[10px] shadow-[0_2px_12px_rgba(0,0,0,0.12)] overflow-hidden min-w-[160px]"
                                    style={(() => {
                                      const btn = document.querySelector(
                                        `[data-product-menu="${v.id}"]`,
                                      );
                                      if (!btn) return { top: 0, left: 0 };
                                      const r = btn.getBoundingClientRect();
                                      return {
                                        top: r.bottom + 4,
                                        left: r.right - 160,
                                      };
                                    })()}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setProductMenuOpen(null);
                                        handleRemoveProduct(v.id);
                                      }}
                                      className="w-full flex items-center gap-2.5 px-4 py-[10px] text-left text-[15px] text-[#FF3B30] hover:bg-black/[0.04] active:bg-black/[0.06] transition-colors"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      Eliminar
                                    </button>
                                  </div>
                                </>,
                                document.body,
                              )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
            <div className="bg-white rounded-[13px] overflow-hidden mt-3">
              <button
                type="button"
                onClick={() => {
                  const qp = new URLSearchParams();
                  if (ids.length > 0) qp.set("ids", ids.join(","));
                  const query = qp.toString();
                  navigate(
                    query
                      ? `/redes-sociales/crear-anuncio/seleccionar-productos?${query}`
                      : "/redes-sociales/crear-anuncio/seleccionar-productos",
                  );
                }}
                onClickCapture={tapFlash}
                className="w-full flex items-center gap-3 px-4 py-[11px] min-h-[54px] text-left transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] text-[#1644EB]">
                    Cambiar productos
                  </p>
                </div>
                <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />
              </button>
            </div>
          </div>

          {/* iOS Segmented Control — Mode selector */}
          <div className="mt-6 sm:mt-10">
            <div className="relative flex items-center p-[3px] bg-black/[0.06] rounded-[10px]">
              <div
                className="absolute top-[3px] bottom-[3px] rounded-[8px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
                style={{
                  width: "calc(33.333% - 2px)",
                  left:
                    activeTab === "automatico"
                      ? "3px"
                      : activeTab === "personalizado"
                        ? "calc(33.333% + 1px)"
                        : "calc(66.666% - 1px)",
                }}
              />
              <button
                type="button"
                onClick={() => switchTab("automatico")}
                className={`relative z-10 flex-1 h-8 inline-flex items-center justify-center text-[13px] font-medium rounded-[8px] text-center transition-colors duration-200 ${
                  activeTab === "automatico"
                    ? "text-brand-black/90"
                    : "text-brand-black/50 active:text-brand-black/70"
                }`}
              >
                Automático
              </button>
              <button
                type="button"
                onClick={() => switchTab("personalizado")}
                className={`relative z-10 flex-1 h-8 inline-flex items-center justify-center text-[13px] font-medium rounded-[8px] text-center transition-colors duration-200 ${
                  activeTab === "personalizado"
                    ? "text-brand-black/90"
                    : "text-brand-black/50 active:text-brand-black/70"
                }`}
              >
                Personalizado
              </button>
              <button
                type="button"
                onClick={() => switchTab("creativo")}
                className={`relative z-10 flex-1 h-8 inline-flex items-center justify-center text-[13px] font-medium rounded-[8px] text-center transition-colors duration-200 ${
                  activeTab === "creativo"
                    ? "text-brand-black/90"
                    : "text-brand-black/50 active:text-brand-black/70"
                }`}
              >
                Creativo
              </button>
            </div>
          </div>

          {/* Tab Content Container */}
          <div className="mt-4 pb-10 sm:pb-4">
            {/* Mode Description — iOS secondary text */}
            <div
              className="text-[15px] text-[#3C3C43]/60 mb-5"
              style={
                tabSlideDir
                  ? {
                      animation: `${tabSlideDir === "left" ? "slideInFromRight" : "slideInFromLeft"} 350ms cubic-bezier(0.28,0.11,0.32,1) both`,
                    }
                  : undefined
              }
            >
              {activeTab === "automatico" && (
                <p>La IA creará imágenes adaptadas al estilo de tu marca.</p>
              )}
              {activeTab === "personalizado" && (
                <p>Personalizá las imágenes como quieras.</p>
              )}
              {activeTab === "creativo" && <p>Creá una imagen a tu medida.</p>}
            </div>

            {/* Tab Content */}
            {activeTab === "automatico" && (
              <div
                key="tab-automatico"
                className="pt-0"
                style={
                  tabSlideDir
                    ? {
                        animation: `${tabSlideDir === "left" ? "slideInFromRight" : "slideInFromLeft"} 350ms cubic-bezier(0.28,0.11,0.32,1) both`,
                      }
                    : undefined
                }
              >
                {/* AI Explanation — iOS card */}
                <div className="bg-white rounded-[13px] p-4 mb-5 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-9 h-9 rounded-[8px] bg-[#1644EB]/10 flex items-center justify-center">
                      <Sparkles className="w-[18px] h-[18px] text-[#1644EB]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[15px] font-semibold text-brand-black/90 mb-0.5">
                        Generación automática
                      </h3>
                      {brandAnalysis ? (
                        <>
                          <p className="text-[13px] text-[#3C3C43]/60 leading-[1.4]">
                            La IA analizará tu marca y creará imágenes adaptadas
                            a su estilo.
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              closeFormatoMenu();
                              closeEscenaMenu();
                              closeBibliotecaMenu();
                              closeTextoMenu();
                              setShowBrandAnalysisMenu(true);
                            }}
                            className="mt-2 inline-flex items-center gap-1 text-[13px] text-[#1644EB] hover:opacity-70 font-medium"
                          >
                            <Info className="w-3.5 h-3.5" />
                            Ver análisis de {activeBrandName || "marca"}
                          </button>
                        </>
                      ) : (
                        <p className="text-[13px] text-[#3C3C43]/60 leading-[1.4]">
                          La IA creará imágenes profesionales de forma
                          automática para tus productos.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Calidad and Formato — iOS grouped table view */}
                <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                  <button
                    type="button"
                    onClick={() => {
                      closeEscenaMenu();
                      closeBibliotecaMenu();
                      closeTextoMenu();
                      setShowFormatoMenu(true);
                    }}
                    onClickCapture={tapFlash}
                    className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
                  >
                    <span className="flex items-center gap-3">
                      <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                        grid_view
                      </span>
                      Formato
                    </span>
                    <span className="flex items-center gap-1 text-[15px] text-[#3C3C43]/30">
                      {formatOptions
                        .find((o) => o.id === format)
                        ?.name.split(" (")[0] || "Posteo"}
                      <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
                    </span>
                  </button>
                </div>
              </div>
            )}

            {activeTab === "creativo" && (
              <div
                key="tab-creativo"
                className="pt-0"
                style={
                  tabSlideDir
                    ? {
                        animation: `${tabSlideDir === "left" ? "slideInFromRight" : "slideInFromLeft"} 350ms cubic-bezier(0.28,0.11,0.32,1) both`,
                      }
                    : undefined
                }
              >
                {/* Prompt text field — iOS card */}
                <div className="bg-white rounded-[13px] p-4 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.04)]">
                  <textarea
                    value={creativoPrompt}
                    onChange={(e) => setCreativoPrompt(e.target.value)}
                    placeholder="Describí la imagen que querés crear..."
                    className="w-full text-[15px] text-brand-black/90 placeholder:text-brand-black/25 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[180px]"
                  />
                  <div className="mt-3 pt-3 border-t border-black/[0.06]">
                    <div className="relative">
                      {/* Generar ideas + Historial — visible when empty */}
                      <div
                        className="flex items-center justify-between transition-all duration-300 ease-out"
                        style={{
                          opacity: creativoPrompt.trim() ? 0 : 1,
                          maxHeight: creativoPrompt.trim() ? 0 : 48,
                          overflow: "hidden",
                          pointerEvents: creativoPrompt.trim()
                            ? "none"
                            : "auto",
                        }}
                      >
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                          <div className="relative shrink-0 inline-flex">
                            <button
                              type="button"
                              onClick={handleGenerateCreativoIdeas}
                              disabled={creativoIdeasLoading}
                              className="relative inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-[8px] bg-white border border-neutral-200 text-[15px] text-brand-black/90 active:bg-neutral-50 disabled:cursor-not-allowed overflow-hidden"
                            >
                              <Lightbulb
                                className={`w-4 h-4 ${creativoIdeasLoading ? "text-[#1644EB] animate-pulse" : "text-[#1644EB]"}`}
                              />
                              <span>
                                {creativoIdeasLoading
                                  ? "Generando..."
                                  : "Generar ideas"}
                              </span>
                              {creativoIdeasLoading && (
                                <>
                                  <span
                                    className="absolute bottom-0 left-0 h-0.5 bg-[#1644EB]"
                                    style={{
                                      animation:
                                        "widthExpand 30s linear forwards",
                                    }}
                                  />
                                  <style>{`@keyframes widthExpand { from { width: 0%; } to { width: 100%; } }`}</style>
                                </>
                              )}
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowCreativoHistorialMenu(true)}
                            className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-[8px] bg-white border border-neutral-200 text-[15px] text-brand-black/90 active:bg-neutral-50"
                          >
                            <span className="material-symbols-rounded text-[16px] text-[#1644EB]">
                              history
                            </span>
                            <span>Historial</span>
                          </button>
                        </div>
                        {creativoIdeas.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowCreativoIdeasMenu(true)}
                            className="text-[15px] text-[#1644EB] hover:opacity-70 font-medium shrink-0 ml-2"
                          >
                            Ver ideas ({creativoIdeas.length})
                          </button>
                        )}
                      </div>
                      {/* Mejorar / Borrar — visible when has text */}
                      <div
                        className="flex items-center justify-between transition-all duration-300 ease-out"
                        style={{
                          opacity: creativoPrompt.trim() ? 1 : 0,
                          maxHeight: creativoPrompt.trim() ? 48 : 0,
                          overflow: "hidden",
                          pointerEvents: creativoPrompt.trim()
                            ? "auto"
                            : "none",
                        }}
                      >
                        {creativoPromptFromIdea ? (
                          <button
                            type="button"
                            onClick={() => {
                              setCreativoPrompt("");
                              setCreativoPromptFromIdea(false);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] bg-black/[0.05] text-[15px] text-brand-black/90 active:bg-black/[0.08]"
                          >
                            <X className="w-4 h-4 text-brand-black/40" />
                            <span>Borrar</span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={handleMejorarInstruccion}
                            disabled={mejorandoInstruccion}
                            className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] bg-white border border-neutral-200 text-[15px] text-brand-black/90 active:bg-neutral-50 disabled:cursor-not-allowed overflow-hidden"
                          >
                            <Sparkles
                              className={`w-4 h-4 text-[#1644EB] ${mejorandoInstruccion ? "animate-pulse" : ""}`}
                            />
                            <span>
                              {mejorandoInstruccion
                                ? "Mejorando..."
                                : "Mejorar"}
                            </span>
                            {mejorandoInstruccion && (
                              <span
                                className="absolute bottom-0 left-0 h-0.5 bg-[#1644EB]"
                                style={{
                                  animation: "widthExpand 25s linear forwards",
                                }}
                              />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Calidad and Formato — iOS grouped table view */}
                <div className="mt-5 rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                  <button
                    type="button"
                    onClick={() => {
                      closeEscenaMenu();
                      closeBibliotecaMenu();
                      closeTextoMenu();
                      closeBrandAnalysisMenu();
                      setShowFormatoMenu(true);
                    }}
                    onClickCapture={tapFlash}
                    className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
                  >
                    <span className="flex items-center gap-3">
                      <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                        grid_view
                      </span>
                      Formato
                    </span>
                    <span className="flex items-center gap-1 text-[15px] text-[#3C3C43]/30">
                      {formatOptions
                        .find((o) => o.id === format)
                        ?.name.split(" (")[0] || "Posteo"}
                      <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
                    </span>
                  </button>
                </div>
              </div>
            )}

            {activeTab === "personalizado" && (
              <div
                key="tab-personalizado"
                className="pt-0"
                style={
                  tabSlideDir
                    ? {
                        animation: `${tabSlideDir === "left" ? "slideInFromRight" : "slideInFromLeft"} 350ms cubic-bezier(0.28,0.11,0.32,1) both`,
                      }
                    : undefined
                }
              >
                {/* Tipo de imagen — iOS segmented control */}
                <div className="mt-2">
                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">
                    Tipo de imagen
                  </p>
                  <div className="relative flex items-center p-[3px] bg-black/[0.06] rounded-[10px]">
                    <div
                      className="absolute top-[3px] bottom-[3px] rounded-[8px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
                      style={{
                        width: "calc(50% - 2px)",
                        left: modeloEnabled ? "3px" : "calc(50% - 1px)",
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setModeloEnabled(true);
                        if (selectedPoses.length > 1) {
                          setMultiPoseToggle(true);
                        }
                      }}
                      className={`relative z-10 flex-1 h-8 inline-flex items-center justify-center text-[13px] font-medium rounded-[8px] text-center transition-colors duration-200 ${
                        modeloEnabled
                          ? "text-brand-black/90"
                          : "text-brand-black/50 active:text-brand-black/70"
                      }`}
                    >
                      Con modelo
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setModeloEnabled(false);
                        setSelectedModeloOption(null);
                        setMultiPoseToggle(false);
                        if (selectedPoses.length > 1) {
                          setSelectedPoses((prev) => [prev[0]]);
                        }
                        const qp = new URLSearchParams(location.search);
                        qp.delete("modelo_mode");
                        qp.delete("modelo_guardado_id");
                        qp.delete("modelo_guardado_nombre");
                        qp.delete("genero");
                        qp.delete("edad");
                        qp.delete("tipo_cuerpo");
                        qp.delete("tono_piel");
                        qp.delete("color_pelo");
                        qp.delete("largo_pelo");
                        qp.delete("desc_modelo");
                        qp.delete("pose");
                        navigate(`${location.pathname}?${qp.toString()}`, {
                          replace: true,
                        });
                      }}
                      className={`relative z-10 flex-1 h-8 inline-flex items-center justify-center text-[13px] font-medium rounded-[8px] text-center transition-colors duration-200 ${
                        !modeloEnabled
                          ? "text-brand-black/90"
                          : "text-brand-black/50 active:text-brand-black/70"
                      }`}
                    >
                      Solo producto
                    </button>
                  </div>
                </div>

                {/* Vista del producto — iOS grouped table */}
                {ids.length === 1 && (
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      !modeloEnabled
                        ? "max-h-[200px] opacity-100"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    <div
                      className={`transition-transform duration-300 ease-in-out ${!modeloEnabled ? "translate-y-0" : "-translate-y-4"}`}
                    >
                      <div className="mt-5">
                        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">
                          Vista del producto
                        </p>
                        <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                          <button
                            type="button"
                            onClick={() => {
                              vistaProductoSnapshot.current =
                                selectedVistaProducto;
                              setShowVistaProductoMenu(true);
                            }}
                            onClickCapture={tapFlash}
                            className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
                          >
                            <span className="flex items-center gap-3">
                              <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                                visibility
                              </span>
                              Vista
                            </span>
                            <span className="flex items-center gap-1 text-[15px] text-[#3C3C43]/30">
                              {multiVistaToggle && selectedVistas.length > 1
                                ? `${selectedVistas.length} vistas`
                                : getVistaOptions().find(
                                    (o) => o.id === selectedVistaProducto,
                                  )?.name || "Frente"}
                              <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tipo de modelo y Plano/Pose — iOS grouped tables */}
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    modeloEnabled
                      ? "max-h-[600px] opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <div
                    className={`transition-transform duration-300 ease-in-out ${modeloEnabled ? "translate-y-0" : "-translate-y-4"}`}
                  >
                    {/* Tipo de modelo */}
                    <div className="mt-5">
                      <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">
                        Tipo de modelo
                      </p>
                      <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedModeloOption("ia");
                            const qp = new URLSearchParams(location.search);
                            qp.set("modelo_mode", "ia");
                            navigate(`${location.pathname}?${qp.toString()}`, {
                              replace: true,
                            });
                          }}
                          onClickCapture={tapFlash}
                          className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
                        >
                          <span className="flex items-center gap-3">
                            <span
                              className={`material-symbols-rounded text-[20px] shrink-0 ${selectedModeloOption === "ia" ? "text-[#1644EB]" : "text-neutral-400"}`}
                            >
                              auto_awesome
                            </span>
                            Sugerido por IA
                          </span>
                          {selectedModeloOption === "ia" && (
                            <Check className="w-5 h-5 text-[#1644EB] shrink-0" />
                          )}
                        </button>
                        <div className="ml-[46px] h-px bg-black/[0.08]" />
                        <button
                          type="button"
                          onClick={() => {
                            modeloGuardadoSnapshot.current =
                              selectedSavedModelId;
                            setShowModeloGuardadoMenu(true);
                            if (
                              savedModels.length === 0 &&
                              !savedModelsLoading
                            ) {
                              fetchSavedModels();
                            }
                          }}
                          onClickCapture={tapFlash}
                          className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
                        >
                          <span className="flex items-center gap-3">
                            <span
                              className={`material-symbols-rounded text-[20px] shrink-0 ${selectedModeloOption === "anteriores" ? "text-[#1644EB]" : "text-neutral-400"}`}
                            >
                              person
                            </span>
                            Modelo guardado
                          </span>
                          <span className="flex items-center gap-1 text-[15px] text-[#3C3C43]/30 min-w-0">
                            <span className="truncate max-w-[120px]">
                              {selectedModeloOption === "anteriores"
                                ? savedModels.find(
                                    (m) => m.id === selectedSavedModelId,
                                  )?.nombre ||
                                  params.get("modelo_guardado_nombre") ||
                                  "Seleccionado"
                                : selectedSavedModelId
                                  ? savedModels.find(
                                      (m) => m.id === selectedSavedModelId,
                                    )?.nombre ||
                                    params.get("modelo_guardado_nombre") ||
                                    "Seleccionado"
                                  : "Seleccionar"}
                            </span>
                            {selectedModeloOption === "anteriores" && (
                              <Check className="w-5 h-5 text-[#1644EB] shrink-0" />
                            )}
                            {selectedModeloOption !== "anteriores" && (
                              <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />
                            )}
                          </span>
                        </button>
                        <div className="ml-[46px] h-px bg-black/[0.08]" />
                        <button
                          type="button"
                          onClick={() => {
                            modeloPersonalizadoSnapshot.current = {
                              genero: modeloGenero,
                              edad: modeloEdad,
                              tipoCuerpo: modeloTipoCuerpo,
                              tonoPiel: modeloTonoPiel,
                              colorPelo: modeloColorPelo,
                              largoPelo: modeloLargoPelo,
                              descripcion: modeloDescripcion,
                            };
                            setShowModeloPersonalizadoMenu(true);
                          }}
                          onClickCapture={tapFlash}
                          className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
                        >
                          <span className="flex items-center gap-3">
                            <span
                              className={`material-symbols-rounded text-[20px] shrink-0 ${selectedModeloOption === "personalizado" ? "text-[#1644EB]" : "text-neutral-400"}`}
                            >
                              edit
                            </span>
                            Personalizado
                          </span>
                          <span className="flex items-center gap-1">
                            {selectedModeloOption === "personalizado" && (
                              <Check className="w-5 h-5 text-[#1644EB] shrink-0" />
                            )}
                            <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* Pose */}
                    {isFashionRubro && (
                      <div className="mt-5">
                        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">
                          Pose
                        </p>
                        <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                          <button
                            type="button"
                            onClick={() => {
                              poseSnapshot.current = [...selectedPoses];
                              setShowPoseMenu(true);
                            }}
                            onClickCapture={tapFlash}
                            className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
                          >
                            <span className="flex items-center gap-3 shrink-0">
                              <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                                accessibility_new
                              </span>
                              Pose
                            </span>
                            <span className="flex items-center gap-1 text-[15px] text-[#3C3C43]/30 overflow-hidden">
                              <span className="truncate max-w-[160px] sm:max-w-none">
                                {selectedPoses.length > 1
                                  ? `${selectedPoses.length} seleccionadas`
                                  : selectedPoses.length === 1
                                    ? poseOptions.find(
                                        (p) => p.id === selectedPoses[0],
                                      )?.name || "Seleccionar"
                                    : "Seleccionar"}
                              </span>
                              <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />
                            </span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Escena — iOS grouped list */}
                <div className="mt-8">
                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">
                    Escena
                  </p>
                  <div className="bg-white rounded-[13px] overflow-hidden">
                    {/* Automático */}
                    <button
                      type="button"
                      onClick={() => toggleScenario("sugerido")}
                      onClickCapture={tapFlash}
                      className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
                    >
                      <span className="flex items-center gap-3">
                        <span
                          className={`material-symbols-rounded text-[20px] shrink-0 ${selectedScenarios.sugerido ? "text-[#1644EB]" : "text-neutral-400"}`}
                        >
                          auto_awesome
                        </span>
                        Automática
                      </span>
                      {selectedScenarios.sugerido && (
                        <Check className="w-5 h-5 text-[#1644EB] shrink-0" />
                      )}
                    </button>
                    <div className="ml-[46px] h-px bg-black/[0.08]" />
                    {/* Fondo blanco */}
                    <button
                      type="button"
                      onClick={() => toggleScenario("fondo_blanco")}
                      onClickCapture={tapFlash}
                      className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
                    >
                      <span className="flex items-center gap-3">
                        <span
                          className={`material-symbols-rounded text-[20px] shrink-0 ${selectedScenarios.fondo_blanco ? "text-[#1644EB]" : "text-neutral-400"}`}
                          style={{
                            fontVariationSettings:
                              "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20",
                          }}
                        >
                          square
                        </span>
                        Fondo blanco
                      </span>
                      {selectedScenarios.fondo_blanco && (
                        <Check className="w-5 h-5 text-[#1644EB] shrink-0" />
                      )}
                    </button>
                    <div className="ml-[46px] h-px bg-black/[0.08]" />
                    {/* Biblioteca */}
                    <button
                      type="button"
                      onClick={() => {
                        bibliotecaSnapshot.current = {
                          fondoId: bibliotecaSelectedFondoId,
                          fondoUrl: bibliotecaSelectedFondoUrl,
                        };
                        closeFormatoMenu();
                        closeEscenaMenu();
                        fetchBibliotecaFondos();
                        setShowBibliotecaMenu(true);
                      }}
                      onClickCapture={tapFlash}
                      className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
                    >
                      <span className="flex items-center gap-3">
                        <span
                          className={`material-symbols-rounded text-[20px] shrink-0 ${selectedScenarios.biblioteca ? "text-[#1644EB]" : "text-neutral-400"}`}
                        >
                          photo_library
                        </span>
                        Biblioteca
                      </span>
                      <span className="flex items-center gap-1 text-[15px] text-[#3C3C43]/30 min-w-0">
                        <span className="truncate max-w-[120px]">
                          {selectedScenarios.biblioteca
                            ? bibliotecaFondos.find(
                                (f) => f.id === bibliotecaSelectedFondoId,
                              )?.nombre ||
                              bibliotecaFondos.find(
                                (f) => f.id === bibliotecaSelectedFondoId,
                              )?.categoria ||
                              "Seleccionado"
                            : bibliotecaSelectedFondoId
                              ? bibliotecaFondos.find(
                                  (f) => f.id === bibliotecaSelectedFondoId,
                                )?.nombre ||
                                bibliotecaFondos.find(
                                  (f) => f.id === bibliotecaSelectedFondoId,
                                )?.categoria ||
                                "Seleccionado"
                              : "Seleccionar"}
                        </span>
                        {selectedScenarios.biblioteca && (
                          <Check className="w-5 h-5 text-[#1644EB] shrink-0" />
                        )}
                        {!selectedScenarios.biblioteca && (
                          <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />
                        )}
                      </span>
                    </button>
                    <div className="ml-[46px] h-px bg-black/[0.08]" />
                    {/* Escenas guardadas */}
                    <button
                      type="button"
                      onClick={() => {
                        escenaGuardadaSnapshot.current = selectedSavedSceneId;
                        closeFormatoMenu();
                        closeEscenaMenu();
                        closeBibliotecaMenu();
                        fetchSavedScenes();
                        setShowEscenasGuardadasMenu(true);
                      }}
                      onClickCapture={tapFlash}
                      className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
                    >
                      <span className="flex items-center gap-3">
                        <span
                          className={`material-symbols-rounded text-[20px] shrink-0 ${selectedScenarios.escenas_guardadas ? "text-[#1644EB]" : "text-neutral-400"}`}
                        >
                          bookmark
                        </span>
                        Escenas guardadas
                      </span>
                      <span className="flex items-center gap-1 text-[15px] text-[#3C3C43]/30 min-w-0">
                        <span className="truncate max-w-[120px]">
                          {selectedScenarios.escenas_guardadas
                            ? savedScenes.find(
                                (s) => s.id === selectedSavedSceneId,
                              )?.nombre || "Seleccionado"
                            : selectedSavedSceneId
                              ? savedScenes.find(
                                  (s) => s.id === selectedSavedSceneId,
                                )?.nombre || "Seleccionado"
                              : "Seleccionar"}
                        </span>
                        {selectedScenarios.escenas_guardadas && (
                          <Check className="w-5 h-5 text-[#1644EB] shrink-0" />
                        )}
                        {!selectedScenarios.escenas_guardadas && (
                          <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />
                        )}
                      </span>
                    </button>
                    <div className="ml-[46px] h-px bg-black/[0.08]" />
                    {/* Personalizado */}
                    <button
                      type="button"
                      onClick={() => {
                        closeFormatoMenu();
                        setShowEscenaMenu(true);
                      }}
                      onClickCapture={tapFlash}
                      className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
                    >
                      <span className="flex items-center gap-3">
                        <span
                          className={`material-symbols-rounded text-[20px] shrink-0 ${selectedScenarios.personalizado ? "text-[#1644EB]" : "text-neutral-400"}`}
                        >
                          brush
                        </span>
                        Personalizada
                      </span>
                      <span className="flex items-center gap-1">
                        {selectedScenarios.personalizado && (
                          <Check className="w-5 h-5 text-[#1644EB] shrink-0" />
                        )}
                        <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
                      </span>
                    </button>
                  </div>
                  <p className="text-[13px] text-[#3C3C43]/60 px-4 mt-[6px]">
                    {selectedScenarios.sugerido
                      ? "La IA elegirá automáticamente una escena adecuada para tus productos."
                      : selectedScenarios.personalizado
                        ? "Describí con texto la escena exacta que querés para tus imágenes."
                        : selectedScenarios.fondo_blanco
                          ? "Tus productos se mostrarán sobre un fondo blanco limpio y minimalista."
                          : selectedScenarios.biblioteca
                            ? "Elegí una escena predefinida de nuestra biblioteca de fondos."
                            : selectedScenarios.escenas_guardadas
                              ? "Usá una escena que ya tengas guardada en tu marca."
                              : "Seleccioná un tipo de escena para tus imágenes."}
                  </p>
                </div>

                {/* Adicional — iOS grouped list */}
                <div className="mt-8">
                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">
                    Adicional
                  </p>
                  <div className="bg-white rounded-[13px] overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        instruccionSnapshot.current = instruccionAdicional;
                        setShowInstruccionMenu(true);
                      }}
                      onClickCapture={tapFlash}
                      className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
                    >
                      <span className="flex items-center gap-3">
                        <span
                          className={`material-symbols-rounded text-[20px] shrink-0 ${instruccionAdicional.trim() ? "text-[#1644EB]" : "text-neutral-400"}`}
                        >
                          edit_note
                        </span>
                        Instrucción adicional
                      </span>
                      <span className="flex items-center gap-1">
                        {instruccionAdicional.trim() && (
                          <Check className="w-5 h-5 text-[#1644EB] shrink-0" />
                        )}
                        <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
                      </span>
                    </button>
                  </div>
                  {instruccionAdicional.trim() ? (
                    <p className="text-[13px] text-[#3C3C43]/60 px-4 mt-[6px] line-clamp-2">
                      {instruccionAdicional}
                    </p>
                  ) : (
                    <p className="text-[13px] text-[#3C3C43]/60 px-4 mt-[6px]">
                      Agregá una instrucción extra para personalizar aún más la
                      generación. Esta opción es opcional.
                    </p>
                  )}
                </div>

                {/* HIDDEN: Elementos para añadir section - temporarily disabled
        <div className="mt-10">
          <h2 className="text-[15px] font-semibold text-neutral-900 mb-3 ml-2">Elementos para añadir</h2>
          <div className="bg-white rounded-md border border-neutral-200 p-3">
            {soonFeatureKey && (
              <div className="mb-3 rounded-md bg-info-soft text-info-text text-[13px] sm:text-[15px] px-3 py-2">
                Disponible muy pronto
              </div>
            )}
            <div className="flex items-stretch gap-0">
            {addOptions.map(({ key, title, Icon }) => {
              const isModelo = key === 'modelo';
              const isTexto = key === 'texto';
              const badgeText = isModelo
                ? modeloBadgeText
                : isTexto && textoSelected
                  ? 'Añadiendo texto'
                  : null;
              const hasBadge = !!badgeText;
              const isInDeleteMode = deleteConfirmKey === key;
              const iconBgClass =
                key === 'colores' || key === 'logo' ? 'bg-slate-50'
                : 'bg-neutral-100';
              const iconColorClass =
                key === 'colores' || key === 'logo' ? 'text-slate-600'
                : 'text-neutral-600';
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    if (hasBadge && isInDeleteMode) {
                      // Second tap: remove the option
                      handleRemoveAddOption(key);
                      setDeleteConfirmKey(null);
                    } else if (hasBadge) {
                      // First tap on selected option: enter delete mode
                      setDeleteConfirmKey(key);
                    } else {
                      // Not selected: navigate to add
                      setDeleteConfirmKey(null);
                      handleAddOptionClick(key);
                    }
                  }}
                  className="flex-1 min-w-0 py-3 flex flex-col items-center rounded-md hover:bg-neutral-50 transition-colors"
                >
                  <div className={`relative w-10 h-10 rounded-sm flex items-center justify-center ${isInDeleteMode ? 'bg-red-100' : iconBgClass}`}>
                    {isInDeleteMode ? (
                      <Trash2 className="w-5 h-5 text-red-600" />
                    ) : (
                      <>
                        <Icon className={`w-5 h-5 ${iconColorClass}`} />
                        {hasBadge && (
                          <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500 text-white border border-white">
                            <Check className="w-3 h-3" />
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <span className="mt-2 text-[13px] font-medium text-neutral-900 text-center">
                    {title}
                  </span>
                  <div className="mt-1 px-1 min-h-[16px]">
                    {isInDeleteMode ? (
                      <span className="text-[11px] leading-snug text-red-600 text-center break-words">
                        Toca para eliminar
                      </span>
                    ) : badgeText ? (
                      <span className="text-[11px] leading-snug text-emerald-600 text-center break-words">
                        {badgeText}
                      </span>
                    ) : null}
                  </div>
                </button>
              );
            })}
            </div>
          </div>
        </div>
        */}

                {/* Calidad y Formato — iOS grouped table */}
                <div className="mt-8">
                  <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">
                    Ajustes
                  </p>
                  <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                    <button
                      type="button"
                      onClick={() => {
                        setShowFormatoMenu(true);
                      }}
                      onClickCapture={tapFlash}
                      className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
                    >
                      <span className="flex items-center gap-3">
                        <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
                          grid_view
                        </span>
                        Formato
                      </span>
                      <span className="flex items-center gap-1 text-[15px] text-[#3C3C43]/30">
                        {formatOptions
                          .find((o) => o.id === format)
                          ?.name.split(" (")[0] || "Posteo"}
                        <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="sm:bg-transparent">
          <GeneratedImagesInline
            imageIds={generatedImageIds}
            companyId={companyIdForImages}
            onReturnToConfig={handleReturnToConfig}
            onGenerateMore={handleGenerateMore}
            onReady={() => {
              setImagesReady(true);
              setLoadingExiting(true);
            }}
          />
        </div>
      )}

      {showLoadingScreen && (
        <LoadingAnimation
          message="Generando imágenes..."
          subMessage="Esto puede tardar unos segundos"
          durationMs={loadingDurationMs}
          exiting={loadingExiting}
          onExitComplete={() => setShowLoadingScreen(false)}
        />
      )}

      {/* Mobile sticky footer */}
      {!showResults && (
        <div
          className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-black/[0.06] z-30"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
          }}
        >
          <div className="flex items-center gap-2 px-4 pt-2.5">
            {!multiPoseActive && !multiVistaActive && (
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  aria-label="Disminuir cantidad"
                  onClick={() => {
                    setImageCount((c) => {
                      const next = Math.max(1, c - 1);
                      const qp = new URLSearchParams(location.search);
                      qp.set("cantidad_imagenes", String(next));
                      navigate(`${location.pathname}?${qp.toString()}`, {
                        replace: true,
                      });
                      return next;
                    });
                    setCountAnimating(true);
                    setTimeout(() => setCountAnimating(false), 150);
                  }}
                  disabled={imageCount <= 1 || generating}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-black/[0.05] text-brand-black/50 active:bg-black/[0.1] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Minus className="w-[18px] h-[18px]" />
                </button>
                <button
                  type="button"
                  aria-label="Aumentar cantidad"
                  onClick={() => {
                    setImageCount((c) => {
                      const next = Math.min(8, c + 1);
                      const qp = new URLSearchParams(location.search);
                      qp.set("cantidad_imagenes", String(next));
                      navigate(`${location.pathname}?${qp.toString()}`, {
                        replace: true,
                      });
                      return next;
                    });
                    setCountAnimating(true);
                    setTimeout(() => setCountAnimating(false), 150);
                  }}
                  disabled={imageCount >= 8 || generating}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-black/[0.05] text-brand-black/50 active:bg-black/[0.1] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus className="w-[18px] h-[18px]" />
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating || ids.length === 0}
              className={`flex-1 inline-flex items-center justify-center gap-2 h-[44px] rounded-[12px] text-[15px] font-semibold transition-all ${
                generating || ids.length === 0
                  ? "bg-[#1644EB]/40 text-white/60 cursor-not-allowed"
                  : "bg-[#1644EB] text-white active:brightness-90"
              }`}
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generando...
                </>
              ) : (
                <span
                  className={`transition-transform duration-150 ${countAnimating ? "scale-105" : "scale-100"}`}
                >
                  Generar {effectiveImageCount}{" "}
                  {effectiveImageCount === 1 ? "imagen" : "imágenes"}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      <SubscriptionLimitModal
        isOpen={showSubscriptionLimitModal}
        onClose={() => setShowSubscriptionLimitModal(false)}
        type={subscriptionLimitType}
      />

      {/* Desktop backdrop overlay when side panel is open */}
      {showSidePanel && (
        <div
          className="hidden sm:block fixed inset-0 bg-black/40 z-[45] cursor-pointer"
          onClick={() => {
            closeFormatoMenu();
            closeEscenaMenu();
            closeBibliotecaMenu();
            closeTextoMenu();
            closeBrandAnalysisMenu();
            closeCreativoIdeasMenu();
            closeCreativoHistorialMenu();
            closeModeloPersonalizadoMenu();
            closeModeloGuardadoMenu();
            closePoseMenu();
            closeVistaProductoMenu();
          }}
        />
      )}

      {/* Formato Menu */}
      {showFormatoMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              formatoMenuAnimated
                ? "translate-x-0"
                : "translate-x-[calc(100%+32px)]"
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">
                Formato
              </h3>
              <button
                type="button"
                onClick={closeFormatoMenu}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-3">
                {formatOptions.map((opt) => {
                  const selected = format === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        setFormat(opt.id);
                        const qp = new URLSearchParams(location.search);
                        qp.set("formato", opt.id);
                        navigate(`${location.pathname}?${qp.toString()}`, {
                          replace: true,
                        });
                      }}
                      className={`w-full border rounded-md p-3 transition-colors focus:outline-none flex items-center gap-4 ${
                        selected
                          ? "border-brand-primary ring-2 ring-brand-primary"
                          : "border-neutral-200 bg-white hover:bg-neutral-50"
                      }`}
                    >
                      <div className="relative overflow-hidden rounded-md w-20 h-20 shrink-0">
                        <CachedImg
                          src={opt.image}
                          alt={opt.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 flex items-center justify-between">
                        <p className="text-[15px] text-neutral-900 font-medium">
                          {opt.name}
                        </p>
                        {selected && (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-primary text-white">
                            <Check className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeFormatoMenu}
                className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={closeFormatoMenu}
                className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors"
              >
                Aceptar
              </button>
            </div>
          </div>
          {/* Mobile Slide - rendered via portal to cover Menu button */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${formatoMenuAnimated ? "opacity-100" : "opacity-0"}`}
                onClick={closeFormatoMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
                style={{
                  height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                  transform:
                    formatoMenuDragY > 0
                      ? `translateY(${formatoMenuDragY}px)`
                      : formatoMenuAnimated
                        ? "translateY(0)"
                        : "translateY(100%)",
                  transition:
                    formatoMenuDragY === 0 ? "transform 0.3s ease-out" : "none",
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleFormatoTouchStart}
                  onTouchMove={handleFormatoTouchMove}
                  onTouchEnd={handleFormatoTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button
                    type="button"
                    onClick={closeFormatoMenu}
                    className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                  >
                    Cancelar
                  </button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Formato
                  </h3>
                  <button
                    type="button"
                    onClick={closeFormatoMenu}
                    className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right"
                  >
                    Aceptar
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  <div className="grid grid-cols-2 gap-3">
                    {formatOptions.map((opt) => {
                      const selected = format === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => {
                            setFormat(opt.id);
                            const qp = new URLSearchParams(location.search);
                            qp.set("formato", opt.id);
                            navigate(`${location.pathname}?${qp.toString()}`, {
                              replace: true,
                            });
                          }}
                          className={`w-full text-left border rounded-md p-3 transition-colors focus:outline-none ${
                            selected
                              ? "border-brand-primary ring-2 ring-brand-primary"
                              : "border-neutral-200 bg-white"
                          }`}
                        >
                          <div className="relative overflow-hidden rounded-sm">
                            <CachedImg
                              src={opt.image}
                              alt={opt.name}
                              className="w-full h-40 object-cover"
                            />
                            {selected && (
                              <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-primary text-white">
                                <Check className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                          <p className="text-[15px] text-neutral-900 mt-2 font-medium text-center">
                            {opt.name}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>,
            document.body,
          )}
        </>
      )}

      {/* Escena Personalizada Menu */}
      {showEscenaMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              escenaMenuAnimated
                ? "translate-x-0"
                : "translate-x-[calc(100%+32px)]"
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">
                Escena personalizada
              </h3>
              <button
                type="button"
                onClick={closeEscenaMenu}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <textarea
                  value={escenaPrompt}
                  onChange={(e) => setEscenaPrompt(e.target.value)}
                  placeholder="Describí la escena que querés para tus imágenes"
                  className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[120px]"
                />
                <div
                  className="-mx-4 mt-3 pt-3 px-4 border-t border-black/[0.06] flex justify-start transition-all duration-300 ease-out"
                  style={{
                    opacity: escenaPrompt.trim() ? 1 : 0,
                    maxHeight: escenaPrompt.trim() ? 48 : 0,
                    overflow: "hidden",
                    pointerEvents: escenaPrompt.trim() ? "auto" : "none",
                  }}
                >
                  <button
                    type="button"
                    onClick={handleMejorarEscena}
                    disabled={mejorandoEscena}
                    className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] bg-white border border-neutral-200 text-[15px] text-brand-black/90 active:bg-neutral-50 disabled:cursor-not-allowed overflow-hidden"
                  >
                    <Sparkles
                      className={`w-4 h-4 text-[#1644EB] ${mejorandoEscena ? "animate-pulse" : ""}`}
                    />
                    <span>{mejorandoEscena ? "Mejorando..." : "Mejorar"}</span>
                    {mejorandoEscena && (
                      <span
                        className="absolute bottom-0 left-0 h-0.5 bg-[#1644EB]"
                        style={{ animation: "widthExpand 25s linear forwards" }}
                      />
                    )}
                  </button>
                </div>
              </div>

              <div className="mt-4">
                <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                  {escenaHistorial.length > 0
                    ? "Utilizado anteriormente"
                    : "Ejemplos"}
                </p>
                {(() => {
                  const isHistorial = escenaHistorial.length > 0;
                  const allItems = isHistorial
                    ? escenaHistorial
                    : escenaExamples;
                  const visibleItems = allItems.slice(0, escenaVisibleCount);
                  const hasMore = allItems.length > escenaVisibleCount;
                  return (
                    <>
                      <div className="space-y-0 rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                        {visibleItems.map((item, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setEscenaPrompt(item)}
                            className={`w-full text-left px-4 py-3 text-[15px] text-brand-black/80 hover:bg-neutral-50 active:bg-neutral-100 transition-colors flex items-start gap-2.5 ${i < visibleItems.length - 1 || hasMore ? "border-b border-black/[0.06]" : ""}`}
                          >
                            {isHistorial ? (
                              <Clock className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" />
                            ) : (
                              <Lightbulb className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" />
                            )}
                            <span className="line-clamp-3">{item}</span>
                          </button>
                        ))}
                        {hasMore && (
                          <button
                            type="button"
                            onClick={() =>
                              setEscenaVisibleCount((prev) => prev + 3)
                            }
                            className="w-full text-left px-4 py-3 text-[15px] text-brand-primary/80 hover:bg-neutral-50 active:bg-neutral-100 transition-colors"
                          >
                            Ver más
                          </button>
                        )}
                      </div>
                    </>
                  );
                })()}

                {escenaGeneratedIdeas.length > 0 && (
                  <div className="mt-4">
                    <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                      Ideas generadas
                    </p>
                    <div className="space-y-0 rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                      {escenaGeneratedIdeas.map((item, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setEscenaPrompt(item)}
                          className={`w-full text-left px-4 py-3 text-[15px] text-brand-black/80 hover:bg-neutral-50 active:bg-neutral-100 transition-colors flex items-start gap-2.5 ${i < escenaGeneratedIdeas.length - 1 ? "border-b border-black/[0.06]" : ""}`}
                        >
                          <Lightbulb className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" />
                          <span className="line-clamp-3">{item}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleGenerateEscenaIdeas}
                  disabled={escenaIdeasLoading}
                  className="relative mt-3 w-full text-left px-4 py-3 text-[15px] text-brand-primary rounded-[13px] bg-white border border-neutral-200 hover:bg-neutral-50 active:bg-neutral-100 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
                >
                  <Sparkles
                    className={`w-4 h-4 ${escenaIdeasLoading ? "animate-pulse" : ""}`}
                  />
                  <span>
                    {escenaIdeasLoading ? "Generando..." : "Generar más ideas"}
                  </span>
                  {escenaIdeasLoading && (
                    <>
                      <span
                        className="absolute bottom-0 left-0 h-0.5 bg-[#1644EB]"
                        style={{ animation: "widthExpand 25s linear forwards" }}
                      />
                      <style>{`@keyframes widthExpand { from { width: 0%; } to { width: 100%; } }`}</style>
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeEscenaMenu}
                className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleEscenaSubmit}
                disabled={!escenaPrompt.trim()}
                className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${escenaPrompt.trim() ? "text-white bg-brand-primary hover:bg-brand-primary-hover" : "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed"}`}
              >
                Aceptar
              </button>
            </div>
          </div>
          {/* Mobile Slide - rendered via portal to cover Menu button */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${escenaMenuAnimated ? "opacity-100" : "opacity-0"}`}
                onClick={closeEscenaMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
                style={{
                  height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                  transform:
                    escenaMenuDragY > 0
                      ? `translateY(${escenaMenuDragY}px)`
                      : escenaMenuAnimated
                        ? "translateY(0)"
                        : "translateY(100%)",
                  transition:
                    escenaMenuDragY === 0 ? "transform 0.3s ease-out" : "none",
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleEscenaTouchStart}
                  onTouchMove={handleEscenaTouchMove}
                  onTouchEnd={handleEscenaTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button
                    type="button"
                    onClick={closeEscenaMenu}
                    className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                  >
                    Cancelar
                  </button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Escena personalizada
                  </h3>
                  <button
                    type="button"
                    onClick={handleEscenaSubmit}
                    disabled={!escenaPrompt.trim()}
                    className={`text-[15px] font-semibold w-20 text-right transition-opacity ${escenaPrompt.trim() ? "text-[#1644EB] active:opacity-50" : "text-[#3C3C43]/20 cursor-not-allowed"}`}
                  >
                    Aceptar
                  </button>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
                  <div className="bg-white border border-neutral-200 rounded-lg p-4">
                    <textarea
                      value={escenaPrompt}
                      onChange={(e) => {
                        setEscenaPrompt(e.target.value);
                        e.target.style.height = "auto";
                        e.target.style.height = e.target.scrollHeight + "px";
                      }}
                      placeholder="Describí la escena que querés para tus imágenes"
                      className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[120px]"
                    />
                    <div
                      className="-mx-4 mt-3 pt-3 px-4 border-t border-black/[0.06] flex justify-start transition-all duration-300 ease-out"
                      style={{
                        opacity: escenaPrompt.trim() ? 1 : 0,
                        maxHeight: escenaPrompt.trim() ? 48 : 0,
                        overflow: "hidden",
                        pointerEvents: escenaPrompt.trim() ? "auto" : "none",
                      }}
                    >
                      <button
                        type="button"
                        onClick={handleMejorarEscena}
                        disabled={mejorandoEscena}
                        className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] bg-white border border-neutral-200 text-[15px] text-brand-black/90 active:bg-neutral-50 disabled:cursor-not-allowed overflow-hidden"
                      >
                        <Sparkles
                          className={`w-4 h-4 text-[#1644EB] ${mejorandoEscena ? "animate-pulse" : ""}`}
                        />
                        <span>
                          {mejorandoEscena ? "Mejorando..." : "Mejorar"}
                        </span>
                        {mejorandoEscena && (
                          <span
                            className="absolute bottom-0 left-0 h-0.5 bg-[#1644EB]"
                            style={{
                              animation: "widthExpand 25s linear forwards",
                            }}
                          />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="mt-4">
                    <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                      {escenaHistorial.length > 0
                        ? "Utilizado anteriormente"
                        : "Ejemplos"}
                    </p>
                    {(() => {
                      const isHistorial = escenaHistorial.length > 0;
                      const allItems = isHistorial
                        ? escenaHistorial
                        : escenaExamples;
                      const visibleItems = allItems.slice(
                        0,
                        escenaVisibleCount,
                      );
                      const hasMore = allItems.length > escenaVisibleCount;
                      return (
                        <>
                          <div className="space-y-0 rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                            {visibleItems.map((item, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => setEscenaPrompt(item)}
                                className={`w-full text-left px-4 py-3 text-[15px] text-brand-black/80 active:bg-neutral-100 transition-colors flex items-start gap-2.5 ${i < visibleItems.length - 1 || hasMore ? "border-b border-black/[0.06]" : ""}`}
                              >
                                {isHistorial ? (
                                  <Clock className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" />
                                ) : (
                                  <Lightbulb className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" />
                                )}
                                <span className="line-clamp-3">{item}</span>
                              </button>
                            ))}
                            {hasMore && (
                              <button
                                type="button"
                                onClick={() =>
                                  setEscenaVisibleCount((prev) => prev + 3)
                                }
                                className="w-full text-left px-4 py-3 text-[15px] text-brand-primary/80 active:bg-neutral-100 transition-colors"
                              >
                                Ver más
                              </button>
                            )}
                          </div>
                        </>
                      );
                    })()}

                    {escenaGeneratedIdeas.length > 0 && (
                      <div className="mt-4">
                        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                          Ideas generadas
                        </p>
                        <div className="space-y-0 rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                          {escenaGeneratedIdeas.map((item, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setEscenaPrompt(item)}
                              className={`w-full text-left px-4 py-3 text-[15px] text-brand-black/80 active:bg-neutral-100 transition-colors flex items-start gap-2.5 ${i < escenaGeneratedIdeas.length - 1 ? "border-b border-black/[0.06]" : ""}`}
                            >
                              <Lightbulb className="w-4 h-4 mt-0.5 text-neutral-400 flex-shrink-0" />
                              <span className="line-clamp-3">{item}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleGenerateEscenaIdeas}
                      disabled={escenaIdeasLoading}
                      className="relative mt-3 w-full text-left px-4 py-3 text-[15px] text-brand-primary rounded-[13px] bg-white border border-neutral-200 active:bg-neutral-100 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
                    >
                      <Sparkles
                        className={`w-4 h-4 ${escenaIdeasLoading ? "animate-pulse" : ""}`}
                      />
                      <span>
                        {escenaIdeasLoading
                          ? "Generando..."
                          : "Generar más ideas"}
                      </span>
                      {escenaIdeasLoading && (
                        <>
                          <span
                            className="absolute bottom-0 left-0 h-0.5 bg-[#1644EB]"
                            style={{
                              animation: "widthExpand 25s linear forwards",
                            }}
                          />
                          <style>{`@keyframes widthExpand { from { width: 0%; } to { width: 100%; } }`}</style>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </>,
            document.body,
          )}
        </>
      )}

      {/* Instrucción Adicional Menu */}
      {showInstruccionMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              instruccionMenuAnimated
                ? "translate-x-0"
                : "translate-x-[calc(100%+32px)]"
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">
                Instrucción adicional
              </h3>
              <button
                type="button"
                onClick={closeInstruccionMenu}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <textarea
                  value={instruccionAdicional}
                  onChange={(e) => setInstruccionAdicional(e.target.value)}
                  placeholder="Ej: Que el producto esté levemente inclinado, con sombras suaves y un reflejo sutil en la superficie..."
                  className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[120px]"
                />
              </div>
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeInstruccionMenu}
                className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleInstruccionSubmit}
                className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${instruccionAdicional.trim() ? "text-white bg-brand-primary hover:bg-brand-primary-hover" : "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed"}`}
              >
                Aceptar
              </button>
            </div>
          </div>
          {/* Mobile Slide - rendered via portal */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${instruccionMenuAnimated ? "opacity-100" : "opacity-0"}`}
                onClick={closeInstruccionMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
                style={{
                  height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                  transform:
                    instruccionMenuDragY > 0
                      ? `translateY(${instruccionMenuDragY}px)`
                      : instruccionMenuAnimated
                        ? "translateY(0)"
                        : "translateY(100%)",
                  transition:
                    instruccionMenuDragY === 0
                      ? "transform 0.3s ease-out"
                      : "none",
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleInstruccionTouchStart}
                  onTouchMove={handleInstruccionTouchMove}
                  onTouchEnd={handleInstruccionTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button
                    type="button"
                    onClick={closeInstruccionMenu}
                    className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                  >
                    Cancelar
                  </button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Instrucción adicional
                  </h3>
                  <button
                    type="button"
                    onClick={handleInstruccionSubmit}
                    className={`text-[15px] font-semibold w-20 text-right transition-opacity ${instruccionAdicional.trim() ? "text-[#1644EB] active:opacity-50" : "text-[#3C3C43]/20 cursor-not-allowed"}`}
                  >
                    Aceptar
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  <div className="bg-white border border-neutral-200 rounded-lg p-4">
                    <textarea
                      value={instruccionAdicional}
                      onChange={(e) => setInstruccionAdicional(e.target.value)}
                      placeholder="Ej: Que el producto esté levemente inclinado, con sombras suaves y un reflejo sutil en la superficie..."
                      className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[120px]"
                    />
                  </div>
                </div>
              </div>
            </>,
            document.body,
          )}
        </>
      )}

      {/* Biblioteca de Escenas Menu */}
      {showBibliotecaMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              bibliotecaMenuAnimated
                ? "translate-x-0"
                : "translate-x-[calc(100%+32px)]"
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">
                Biblioteca de escenas
              </h3>
              <button
                type="button"
                onClick={closeBibliotecaMenu}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col">
              {bibliotecaLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
                </div>
              )}

              {!bibliotecaLoading && bibliotecaCategorias.length > 0 && (
                <>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {bibliotecaCategorias.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setBibliotecaActiveCategoria(cat)}
                        className={`px-4 h-9 rounded-full border text-[15px] whitespace-nowrap ${
                          bibliotecaActiveCategoria === cat
                            ? "bg-brand-primary text-white border-brand-primary"
                            : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-3 gap-3 overflow-y-auto flex-1 content-start">
                    {bibliotecaItemsActuales.map((fondo) => {
                      const isSelected = bibliotecaSelectedFondoId === fondo.id;
                      return (
                        <button
                          key={fondo.id}
                          type="button"
                          onClick={() => {
                            setBibliotecaSelectedFondoId(fondo.id);
                            setBibliotecaSelectedFondoUrl(fondo.link_img);
                          }}
                          className={`w-full h-fit text-left border rounded-md p-2 transition-colors ${
                            isSelected
                              ? "border-brand-primary ring-2 ring-brand-primary"
                              : "border-neutral-200 bg-white hover:bg-neutral-50"
                          }`}
                        >
                          <div className="relative overflow-hidden rounded-md h-24 bg-neutral-100">
                            <CachedImg
                              src={fondo.link_img}
                              alt={fondo.nombre || "Fondo"}
                              className="w-full h-full object-cover"
                            />
                            {isSelected && (
                              <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-primary text-white">
                                <Check className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                          <p className="text-[13px] text-neutral-700 mt-2 truncate">
                            {fondo.nombre || "Fondo"}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeBibliotecaMenu}
                className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleBibliotecaSubmit}
                disabled={!bibliotecaSelectedFondoId}
                className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${bibliotecaSelectedFondoId ? "text-white bg-brand-primary hover:bg-brand-primary-hover" : "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed"}`}
              >
                Aceptar
              </button>
            </div>
          </div>
          {/* Mobile Slide - rendered via portal to cover Menu button */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${bibliotecaMenuAnimated ? "opacity-100" : "opacity-0"}`}
                onClick={closeBibliotecaMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
                style={{
                  height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                  transform:
                    bibliotecaMenuDragY > 0
                      ? `translateY(${bibliotecaMenuDragY}px)`
                      : bibliotecaMenuAnimated
                        ? "translateY(0)"
                        : "translateY(100%)",
                  transition:
                    bibliotecaMenuDragY === 0
                      ? "transform 0.3s ease-out"
                      : "none",
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleBibliotecaTouchStart}
                  onTouchMove={handleBibliotecaTouchMove}
                  onTouchEnd={handleBibliotecaTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button
                    type="button"
                    onClick={closeBibliotecaMenu}
                    className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                  >
                    Cancelar
                  </button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Biblioteca de escenas
                  </h3>
                  <button
                    type="button"
                    onClick={handleBibliotecaSubmit}
                    disabled={!bibliotecaSelectedFondoId}
                    className={`text-[15px] font-semibold w-20 text-right transition-opacity ${bibliotecaSelectedFondoId ? "text-[#1644EB] active:opacity-50" : "text-[#3C3C43]/20 cursor-not-allowed"}`}
                  >
                    Aceptar
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col">
                  {bibliotecaLoading && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
                    </div>
                  )}

                  {!bibliotecaLoading && bibliotecaCategorias.length > 0 && (
                    <>
                      <div className="overflow-x-auto mb-4 flex-shrink-0">
                        <div className="inline-flex gap-2">
                          {bibliotecaCategorias.map((cat) => (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => setBibliotecaActiveCategoria(cat)}
                              className={`px-4 h-9 rounded-full border text-[15px] whitespace-nowrap ${
                                bibliotecaActiveCategoria === cat
                                  ? "bg-brand-primary text-white border-brand-primary"
                                  : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                              }`}
                            >
                              {cat}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 overflow-y-auto flex-1 content-start">
                        {bibliotecaItemsActuales.map((fondo) => {
                          const isSelected =
                            bibliotecaSelectedFondoId === fondo.id;
                          return (
                            <button
                              key={fondo.id}
                              type="button"
                              onClick={() => {
                                setBibliotecaSelectedFondoId(fondo.id);
                                setBibliotecaSelectedFondoUrl(fondo.link_img);
                              }}
                              className={`w-full h-fit text-left border rounded-md p-2 transition-colors ${
                                isSelected
                                  ? "border-brand-primary ring-2 ring-brand-primary"
                                  : "border-neutral-200 bg-white hover:bg-neutral-50"
                              }`}
                            >
                              <div className="relative overflow-hidden rounded-md h-28 bg-neutral-100">
                                <CachedImg
                                  src={fondo.link_img}
                                  alt={fondo.nombre || "Fondo"}
                                  className="w-full h-full object-cover"
                                />
                                {isSelected && (
                                  <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-primary text-white">
                                    <Check className="w-3 h-3" />
                                  </span>
                                )}
                              </div>
                              <p className="text-[13px] text-neutral-700 mt-2 truncate">
                                {fondo.nombre || "Fondo"}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>,
            document.body,
          )}
        </>
      )}

      {/* Escenas Guardadas Menu */}
      {showEscenasGuardadasMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              escenasGuardadasMenuAnimated
                ? "translate-x-0"
                : "translate-x-[calc(100%+32px)]"
            }`}
          >
            {showAddSceneForm ? (
              <>
                <div className="flex items-center justify-between px-5 py-4">
                  <h3 className="text-[17px] font-semibold text-brand-black/90">
                    Añadir escena
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddSceneForm(false);
                      resetAddSceneState();
                    }}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="mx-5 h-px bg-black/[0.08]" />
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                  <div>
                    <label className="block text-[15px] font-medium text-neutral-700 mb-1">
                      Nombre de la escena
                    </label>
                    <input
                      type="text"
                      value={addSceneName}
                      onChange={(e) => setAddSceneName(e.target.value)}
                      placeholder="Ej: Playa, Estudio, Jardín..."
                      className="w-full rounded-md border border-neutral-200 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-[15px] font-medium text-neutral-700 mb-1">
                      Imagen de la escena
                    </label>
                    {/* Converting HEIC indicator */}
                    {convertingHeic && !addSceneFile && (
                      <div className="bg-surface-card border border-neutral-200 rounded-lg p-4 mb-3 flex items-center justify-center gap-3">
                        <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
                        <p className="text-[15px] text-neutral-600">
                          Subiendo imagen...
                        </p>
                      </div>
                    )}
                    {addSceneFile && (
                      <div className="bg-surface-card border border-neutral-200 rounded-lg p-3 mb-3">
                        <div className="flex items-center gap-3">
                          {addScenePreviewUrl ? (
                            <img
                              src={addScenePreviewUrl}
                              alt="Preview"
                              className="w-20 h-20 object-cover rounded-md shrink-0"
                            />
                          ) : (
                            <div className="w-20 h-20 rounded-md shrink-0 bg-neutral-100 flex items-center justify-center">
                              <span className="material-symbols-rounded text-[24px] text-neutral-400">
                                image
                              </span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-[15px] text-neutral-700 truncate">
                              {addSceneFile.name}
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                setAddSceneFile(null);
                                setAddScenePreviewUrl(null);
                              }}
                              className="text-[13px] text-error hover:opacity-70 mt-1"
                            >
                              Eliminar
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    <input
                      type="file"
                      id="add-scene-file-desktop"
                      accept="image/*,.heic,.heif"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleSceneFileSelect(file);
                        e.target.value = "";
                      }}
                      className="hidden"
                    />
                    <label
                      htmlFor="add-scene-file-desktop"
                      className={`inline-flex w-full justify-center items-center h-10 px-4 shadow-sm text-[15px] font-medium rounded-md cursor-pointer transition-colors ${convertingHeic ? "pointer-events-none opacity-50 border border-neutral-200 text-neutral-900 bg-surface-card" : !addSceneFile ? "bg-brand-primary text-white hover:bg-brand-primary-hover" : "border border-neutral-200 text-neutral-900 bg-surface-card hover:bg-neutral-100"}`}
                    >
                      <ImagePlus className="w-4 h-4 mr-2" />
                      {convertingHeic
                        ? "Subiendo..."
                        : !addSceneFile
                          ? "Subir imagen"
                          : "Cambiar imagen"}
                    </label>
                  </div>
                  {addSceneError && (
                    <p className="text-[15px] text-error">{addSceneError}</p>
                  )}
                </div>
                <div className="px-5 py-3 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddSceneForm(false);
                      resetAddSceneState();
                    }}
                    className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
                  >
                    Volver
                  </button>
                  <button
                    type="button"
                    onClick={handleAddSceneSave}
                    disabled={addSceneSaving || !addSceneFile}
                    className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors inline-flex items-center gap-1.5 ${addSceneSaving || !addSceneFile ? "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed" : "text-white bg-brand-primary hover:bg-brand-primary-hover"}`}
                  >
                    {addSceneSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      "Guardar escena"
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between px-5 py-4">
                  <h3 className="text-[17px] font-semibold text-brand-black/90">
                    Escenas guardadas
                  </h3>
                  <button
                    type="button"
                    onClick={closeEscenasGuardadasMenu}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="mx-5 h-px bg-black/[0.08]" />
                <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col">
                  <p className="text-[15px] text-neutral-500 mb-4">
                    Cargá una imagen de una escena y utilizala como fondo para
                    tu imagen.
                  </p>
                  {savedScenesLoading && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
                    </div>
                  )}
                  {!savedScenesLoading && savedScenes.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                      <span className="material-symbols-rounded text-[40px] text-neutral-300">
                        bookmark
                      </span>
                      <p className="text-[15px] text-neutral-500 text-center">
                        No tenés escenas guardadas todavía.
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowAddSceneForm(true)}
                        className="inline-flex items-center gap-1.5 px-4 h-10 rounded-md bg-brand-primary text-white text-[15px] font-medium hover:bg-brand-primary-hover shadow-sm"
                      >
                        <ImagePlus className="w-4 h-4" />
                        Añadir escena
                      </button>
                    </div>
                  )}
                  {!savedScenesLoading && savedScenes.length > 0 && (
                    <>
                      <div className="grid grid-cols-3 gap-3 overflow-y-auto flex-1 content-start">
                        <button
                          type="button"
                          onClick={() => setShowAddSceneForm(true)}
                          className="relative rounded-md border border-neutral-200 overflow-hidden transition-colors hover:border-neutral-300 group bg-white"
                        >
                          <div className="w-full h-24 bg-white flex items-center justify-center">
                            <div className="w-12 h-12 rounded-full bg-brand-primary text-white flex items-center justify-center transition-colors group-hover:bg-brand-primary-hover">
                              <Plus className="w-6 h-6" />
                            </div>
                          </div>
                          <p className="text-[15px] text-neutral-900 p-2 font-medium">
                            Añadir escena
                          </p>
                        </button>
                        {savedScenes.map((scene) => {
                          const isSelected = selectedSavedSceneId === scene.id;
                          return (
                            <div
                              key={scene.id}
                              onClick={() =>
                                handleEscenaGuardadaSelect(scene.id)
                              }
                              className={`relative w-full h-fit text-left border rounded-md p-2 transition-colors cursor-pointer ${
                                isSelected
                                  ? "border-brand-primary ring-2 ring-brand-primary"
                                  : "border-neutral-200 bg-white hover:bg-neutral-50"
                              }`}
                            >
                              <div className="relative overflow-hidden rounded-sm h-24 bg-neutral-100">
                                {scene.signedUrl ? (
                                  <img
                                    src={scene.signedUrl}
                                    alt={scene.nombre || "Escena"}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <span className="material-symbols-rounded text-[24px] text-neutral-300">
                                      image
                                    </span>
                                  </div>
                                )}
                                {isSelected && (
                                  <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-primary text-white">
                                    <Check className="w-3 h-3" />
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center mt-2">
                                <p className="text-[13px] text-neutral-700 truncate flex-1">
                                  {scene.nombre || "Escena"}
                                </p>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenSceneMenuId(
                                      openSceneMenuId === scene.id
                                        ? null
                                        : scene.id,
                                    );
                                  }}
                                  className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full hover:bg-neutral-100 transition-colors"
                                >
                                  <MoreHorizontal className="w-3.5 h-3.5 text-neutral-400" />
                                </button>
                              </div>
                              {openSceneMenuId === scene.id && (
                                <div className="absolute bottom-0 right-2 translate-y-full z-20 mt-1 bg-white rounded-md shadow-lg border border-neutral-200 py-1 min-w-[140px]">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteScene(scene.id);
                                    }}
                                    className="w-full text-left px-3 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Eliminar
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
                <div className="px-5 py-3 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeEscenasGuardadasMenu}
                    className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleEscenasGuardadasSubmit}
                    disabled={!selectedSavedSceneId}
                    className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${selectedSavedSceneId ? "text-white bg-brand-primary hover:bg-brand-primary-hover" : "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed"}`}
                  >
                    Aceptar
                  </button>
                </div>
              </>
            )}
          </div>
          {/* Mobile Slide - rendered via portal */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${escenasGuardadasMenuAnimated ? "opacity-100" : "opacity-0"}`}
                onClick={closeEscenasGuardadasMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
                style={{
                  height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                  transform:
                    escenasGuardadasMenuDragY > 0
                      ? `translateY(${escenasGuardadasMenuDragY}px)`
                      : escenasGuardadasMenuAnimated
                        ? "translateY(0)"
                        : "translateY(100%)",
                  transition:
                    escenasGuardadasMenuDragY === 0
                      ? "transform 0.3s ease-out"
                      : "none",
                }}
              >
                {showAddSceneForm ? (
                  <>
                    <div
                      className="py-2 cursor-grab active:cursor-grabbing"
                      onTouchStart={handleEscenasGuardadasTouchStart}
                      onTouchMove={handleEscenasGuardadasTouchMove}
                      onTouchEnd={handleEscenasGuardadasTouchEnd}
                    >
                      <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                    </div>
                    <div className="flex items-center justify-between px-4 pb-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddSceneForm(false);
                          resetAddSceneState();
                        }}
                        className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                      >
                        Volver
                      </button>
                      <h3 className="text-[15px] font-semibold text-brand-black/90">
                        Añadir escena
                      </h3>
                      <button
                        type="button"
                        onClick={handleAddSceneSave}
                        disabled={addSceneSaving || !addSceneFile}
                        className={`text-[15px] font-semibold text-right transition-opacity inline-flex items-center justify-end gap-1 ${addSceneSaving ? "text-[#1644EB]" : addSceneFile ? "text-[#1644EB] active:opacity-50" : "text-[#3C3C43]/20 cursor-not-allowed"}`}
                      >
                        {addSceneSaving ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin text-[#1644EB]" />
                            Guardando...
                          </>
                        ) : (
                          "Guardar"
                        )}
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                      <div>
                        <label className="block text-[15px] font-medium text-neutral-700 mb-1">
                          Nombre de la escena
                        </label>
                        <input
                          type="text"
                          value={addSceneName}
                          onChange={(e) => setAddSceneName(e.target.value)}
                          placeholder="Ej: Playa, Estudio, Jardín..."
                          className="w-full rounded-md border border-neutral-200 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-[15px] font-medium text-neutral-700 mb-1">
                          Imagen de la escena
                        </label>
                        {/* Converting HEIC indicator */}
                        {convertingHeic && !addSceneFile && (
                          <div className="bg-surface-card border border-neutral-200 rounded-lg p-4 mb-3 flex items-center justify-center gap-3">
                            <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
                            <p className="text-[15px] text-neutral-600">
                              Subiendo imagen...
                            </p>
                          </div>
                        )}
                        {addSceneFile && (
                          <div className="bg-surface-card border border-neutral-200 rounded-lg p-3 mb-3">
                            <div className="flex items-center gap-3">
                              {addScenePreviewUrl ? (
                                <img
                                  src={addScenePreviewUrl}
                                  alt="Preview"
                                  className="w-20 h-20 object-cover rounded-md shrink-0"
                                />
                              ) : (
                                <div className="w-20 h-20 rounded-md shrink-0 bg-neutral-100 flex items-center justify-center">
                                  <span className="material-symbols-rounded text-[24px] text-neutral-400">
                                    image
                                  </span>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-[15px] text-neutral-700 truncate">
                                  {addSceneFile.name}
                                </p>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAddSceneFile(null);
                                    setAddScenePreviewUrl(null);
                                  }}
                                  className="text-[13px] text-error hover:opacity-70 mt-1"
                                >
                                  Eliminar
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        <input
                          type="file"
                          id="add-scene-file-mobile"
                          accept="image/*,.heic,.heif"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleSceneFileSelect(file);
                            e.target.value = "";
                          }}
                          className="hidden"
                        />
                        <label
                          htmlFor="add-scene-file-mobile"
                          className={`inline-flex w-full justify-center items-center h-10 px-4 shadow-sm text-[15px] font-medium rounded-md cursor-pointer transition-colors ${convertingHeic ? "pointer-events-none opacity-50 border border-neutral-200 text-neutral-900 bg-surface-card" : !addSceneFile ? "bg-brand-primary text-white hover:bg-brand-primary-hover" : "border border-neutral-200 text-neutral-900 bg-surface-card hover:bg-neutral-100"}`}
                        >
                          <ImagePlus className="w-4 h-4 mr-2" />
                          {convertingHeic
                            ? "Subiendo..."
                            : !addSceneFile
                              ? "Subir imagen"
                              : "Cambiar imagen"}
                        </label>
                      </div>
                      {addSceneError && (
                        <p className="text-[15px] text-error">
                          {addSceneError}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className="py-2 cursor-grab active:cursor-grabbing"
                      onTouchStart={handleEscenasGuardadasTouchStart}
                      onTouchMove={handleEscenasGuardadasTouchMove}
                      onTouchEnd={handleEscenasGuardadasTouchEnd}
                    >
                      <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                    </div>
                    <div className="flex items-center justify-between px-4 pb-3">
                      <button
                        type="button"
                        onClick={closeEscenasGuardadasMenu}
                        className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                      >
                        Cancelar
                      </button>
                      <h3 className="text-[15px] font-semibold text-brand-black/90">
                        Escenas guardadas
                      </h3>
                      <button
                        type="button"
                        onClick={handleEscenasGuardadasSubmit}
                        disabled={!selectedSavedSceneId}
                        className={`text-[15px] font-semibold w-20 text-right transition-opacity ${selectedSavedSceneId ? "text-[#1644EB] active:opacity-50" : "text-[#3C3C43]/20 cursor-not-allowed"}`}
                      >
                        Aceptar
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col">
                      <p className="text-[15px] text-neutral-500 mb-4">
                        Cargá una imagen de una escena y utilizala como fondo
                        para tu imagen.
                      </p>
                      {savedScenesLoading && (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
                        </div>
                      )}
                      {!savedScenesLoading && savedScenes.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-12 gap-3">
                          <span className="material-symbols-rounded text-[40px] text-neutral-300">
                            bookmark
                          </span>
                          <p className="text-[15px] text-neutral-500 text-center">
                            No tenés escenas guardadas todavía.
                          </p>
                          <button
                            type="button"
                            onClick={() => setShowAddSceneForm(true)}
                            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-md bg-brand-primary text-white text-[15px] font-medium hover:bg-brand-primary-hover shadow-sm"
                          >
                            <ImagePlus className="w-4 h-4" />
                            Añadir escena
                          </button>
                        </div>
                      )}
                      {!savedScenesLoading && savedScenes.length > 0 && (
                        <>
                          <div className="grid grid-cols-2 gap-3 overflow-y-auto flex-1 content-start">
                            <button
                              type="button"
                              onClick={() => setShowAddSceneForm(true)}
                              className="relative rounded-md border border-neutral-200 overflow-hidden transition-colors hover:border-neutral-300 group bg-white"
                            >
                              <div className="w-full h-28 bg-white flex items-center justify-center">
                                <div className="w-12 h-12 rounded-full bg-brand-primary text-white flex items-center justify-center transition-colors group-hover:bg-brand-primary-hover">
                                  <Plus className="w-6 h-6" />
                                </div>
                              </div>
                              <p className="text-[15px] text-neutral-900 p-2 font-medium">
                                Añadir escena
                              </p>
                            </button>
                            {savedScenes.map((scene) => {
                              const isSelected =
                                selectedSavedSceneId === scene.id;
                              return (
                                <div
                                  key={scene.id}
                                  onClick={() =>
                                    handleEscenaGuardadaSelect(scene.id)
                                  }
                                  className={`relative w-full h-fit text-left border rounded-md p-2 transition-colors cursor-pointer ${
                                    isSelected
                                      ? "border-brand-primary ring-2 ring-brand-primary"
                                      : "border-neutral-200 bg-white hover:bg-neutral-50"
                                  }`}
                                >
                                  <div className="relative overflow-hidden rounded-sm h-28 bg-neutral-100">
                                    {scene.signedUrl ? (
                                      <img
                                        src={scene.signedUrl}
                                        alt={scene.nombre || "Escena"}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <span className="material-symbols-rounded text-[24px] text-neutral-300">
                                          image
                                        </span>
                                      </div>
                                    )}
                                    {isSelected && (
                                      <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-primary text-white">
                                        <Check className="w-3 h-3" />
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center mt-2">
                                    <p className="text-[13px] text-neutral-700 truncate flex-1">
                                      {scene.nombre || "Escena"}
                                    </p>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenSceneMenuId(
                                          openSceneMenuId === scene.id
                                            ? null
                                            : scene.id,
                                        );
                                      }}
                                      className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full hover:bg-neutral-100 transition-colors"
                                    >
                                      <MoreHorizontal className="w-3.5 h-3.5 text-neutral-400" />
                                    </button>
                                  </div>
                                  {openSceneMenuId === scene.id && (
                                    <div className="absolute bottom-0 right-2 translate-y-full z-20 mt-1 bg-white rounded-md shadow-lg border border-neutral-200 py-1 min-w-[140px]">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteScene(scene.id);
                                        }}
                                        className="w-full text-left px-3 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                        Eliminar
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>,
            document.body,
          )}
        </>
      )}

      {/* Añadir Texto Menu */}
      {showTextoMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              textoMenuAnimated
                ? "translate-x-0"
                : "translate-x-[calc(100%+32px)]"
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">
                Añadir texto
              </h3>
              <button
                type="button"
                onClick={closeTextoMenu}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col">
              {textoLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
                </div>
              )}

              {!textoLoading && textoCategorias.length > 0 && (
                <>
                  <div className="overflow-x-auto mb-4">
                    <div className="inline-flex gap-2">
                      {textoCategorias.map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setTextoActiveCategoria(cat)}
                          className={`px-4 h-9 rounded-full border text-[15px] whitespace-nowrap ${
                            textoActiveCategoria === cat
                              ? "bg-brand-primary text-white border-brand-primary"
                              : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 overflow-y-auto flex-1">
                    {textoItemsActuales.map((comp) => {
                      const isSelected = textoSelectedCompId === comp.id;
                      return (
                        <div
                          key={comp.id}
                          className={`w-full text-left border rounded-md p-2 transition-colors ${
                            isSelected
                              ? "border-brand-primary ring-2 ring-brand-primary col-span-3"
                              : "border-neutral-200 bg-white"
                          }`}
                        >
                          <div
                            className={`flex ${isSelected ? "flex-row gap-3" : "flex-col"}`}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setTextoSelectedCompId(comp.id);
                                setTextoSelectedCompUrl(comp.link_img);
                              }}
                              className={`relative overflow-hidden rounded-md h-24 ${isSelected ? "w-32 flex-shrink-0" : "w-full"} focus:outline-none hover:opacity-90`}
                            >
                              <CachedImg
                                src={comp.link_img}
                                alt={comp.nombre || "Composición"}
                                className="w-full h-full object-cover"
                              />
                              {isSelected && (
                                <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-primary text-white">
                                  <Check className="w-3 h-3" />
                                </span>
                              )}
                            </button>
                            {isSelected && (
                              <div className="flex-1 min-w-0">
                                <label className="block text-[13px] font-medium text-neutral-700 mb-1">
                                  Texto
                                </label>
                                <textarea
                                  value={textoText}
                                  onChange={(e) => setTextoText(e.target.value)}
                                  placeholder="Escribí tu texto..."
                                  rows={3}
                                  className="w-full text-[15px] text-neutral-900 resize-none border border-neutral-200 rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-brand-primary"
                                />
                              </div>
                            )}
                          </div>
                          {!isSelected && (
                            <p className="text-[13px] text-neutral-700 mt-2 truncate">
                              {comp.nombre || "Composición"}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeTextoMenu}
                className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleTextoSubmit}
                disabled={!textoCanSave}
                className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${textoCanSave ? "text-white bg-brand-primary hover:bg-brand-primary-hover" : "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed"}`}
              >
                Aceptar
              </button>
            </div>
          </div>
          {/* Mobile Slide - rendered via portal to cover Menu button */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${textoMenuAnimated ? "opacity-100" : "opacity-0"}`}
                onClick={closeTextoMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
                style={{
                  height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                  transform:
                    textoMenuDragY > 0
                      ? `translateY(${textoMenuDragY}px)`
                      : textoMenuAnimated
                        ? "translateY(0)"
                        : "translateY(100%)",
                  transition:
                    textoMenuDragY === 0 ? "transform 0.3s ease-out" : "none",
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleTextoTouchStart}
                  onTouchMove={handleTextoTouchMove}
                  onTouchEnd={handleTextoTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button
                    type="button"
                    onClick={closeTextoMenu}
                    className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                  >
                    Cancelar
                  </button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Añadir texto
                  </h3>
                  <button
                    type="button"
                    onClick={handleTextoSubmit}
                    disabled={!textoCanSave}
                    className={`text-[15px] font-semibold w-20 text-right transition-opacity ${textoCanSave ? "text-[#1644EB] active:opacity-50" : "text-[#3C3C43]/20 cursor-not-allowed"}`}
                  >
                    Aceptar
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col">
                  {textoLoading && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
                    </div>
                  )}

                  {!textoLoading && textoCategorias.length > 0 && (
                    <>
                      <div className="overflow-x-auto mb-4 flex-shrink-0">
                        <div className="inline-flex gap-2">
                          {textoCategorias.map((cat) => (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => setTextoActiveCategoria(cat)}
                              className={`px-4 h-9 rounded-full border text-[15px] whitespace-nowrap ${
                                textoActiveCategoria === cat
                                  ? "bg-brand-primary text-white border-brand-primary"
                                  : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                              }`}
                            >
                              {cat}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 overflow-y-auto flex-1">
                        {textoItemsActuales.map((comp) => {
                          const isSelected = textoSelectedCompId === comp.id;
                          return (
                            <div
                              key={comp.id}
                              className={`w-full text-left border rounded-md p-2 transition-colors ${
                                isSelected
                                  ? "border-brand-primary ring-2 ring-brand-primary col-span-2"
                                  : "border-neutral-200 bg-white"
                              }`}
                            >
                              <div
                                className={`flex ${isSelected ? "flex-col gap-3" : "flex-col"}`}
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTextoSelectedCompId(comp.id);
                                    setTextoSelectedCompUrl(comp.link_img);
                                  }}
                                  className="relative overflow-hidden rounded-md h-28 w-full focus:outline-none hover:opacity-90"
                                >
                                  <CachedImg
                                    src={comp.link_img}
                                    alt={comp.nombre || "Composición"}
                                    className="w-full h-full object-cover"
                                  />
                                  {isSelected && (
                                    <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-primary text-white">
                                      <Check className="w-3 h-3" />
                                    </span>
                                  )}
                                </button>
                                {isSelected && (
                                  <div className="w-full">
                                    <label className="block text-[13px] font-medium text-neutral-700 mb-1">
                                      Texto
                                    </label>
                                    <textarea
                                      value={textoText}
                                      onChange={(e) =>
                                        setTextoText(e.target.value)
                                      }
                                      placeholder="Escribí tu texto..."
                                      rows={3}
                                      className="w-full text-[15px] text-neutral-900 resize-none border border-neutral-200 rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-brand-primary"
                                    />
                                  </div>
                                )}
                              </div>
                              {!isSelected && (
                                <p className="text-[13px] text-neutral-700 mt-2 truncate">
                                  {comp.nombre || "Composición"}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>,
            document.body,
          )}
        </>
      )}

      {/* Brand Analysis Menu */}
      {showBrandAnalysisMenu && brandAnalysis && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              brandAnalysisMenuAnimated
                ? "translate-x-0"
                : "translate-x-[calc(100%+32px)]"
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">
                {activeBrandName
                  ? `Análisis de ${activeBrandName}`
                  : "Análisis de tu marca"}
              </h3>
              <button
                type="button"
                onClick={closeBrandAnalysisMenu}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="bg-info-soft rounded-md p-4 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-info" />
                  <span className="text-[15px] font-medium text-info-text">
                    Análisis generado por IA
                  </span>
                </div>
                <p className="text-[13px] text-info-text">
                  Este análisis fue creado automáticamente basándose en la
                  información de tu marca.
                </p>
              </div>
              {renderMarkdown(brandAnalysis)}
            </div>
            <div className="px-5 py-3 flex justify-end">
              <button
                type="button"
                onClick={closeBrandAnalysisMenu}
                className="text-[15px] font-semibold text-[#1644EB] hover:opacity-70 px-3 py-2 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
          {/* Mobile Slide - rendered via portal to cover Menu button */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${brandAnalysisMenuAnimated ? "opacity-100" : "opacity-0"}`}
                onClick={closeBrandAnalysisMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
                style={{
                  height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                  transform:
                    brandAnalysisMenuDragY > 0
                      ? `translateY(${brandAnalysisMenuDragY}px)`
                      : brandAnalysisMenuAnimated
                        ? "translateY(0)"
                        : "translateY(100%)",
                  transition:
                    brandAnalysisMenuDragY === 0
                      ? "transform 0.3s ease-out"
                      : "none",
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleBrandAnalysisTouchStart}
                  onTouchMove={handleBrandAnalysisTouchMove}
                  onTouchEnd={handleBrandAnalysisTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <span className="w-20" />
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Análisis de tu marca
                  </h3>
                  <button
                    type="button"
                    onClick={closeBrandAnalysisMenu}
                    className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right"
                  >
                    Cerrar
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  <div className="bg-info-soft rounded-md p-4 mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-4 h-4 text-info" />
                      <span className="text-[15px] font-medium text-info-text">
                        Análisis generado por IA
                      </span>
                    </div>
                    <p className="text-[13px] text-info-text">
                      Este análisis fue creado automáticamente basándose en la
                      información de tu marca.
                    </p>
                  </div>
                  {renderMarkdown(brandAnalysis)}
                </div>
              </div>
            </>,
            document.body,
          )}
        </>
      )}

      {/* Creativo Ideas Menu */}
      {showCreativoIdeasMenu && creativoIdeas.length > 0 && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              creativoIdeasMenuAnimated
                ? "translate-x-0"
                : "translate-x-[calc(100%+32px)]"
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">
                Ideas generadas
              </h3>
              <button
                type="button"
                onClick={closeCreativoIdeasMenu}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <p className="text-[15px] text-neutral-500 mb-4">
                Seleccioná una idea para usarla como descripción de tu imagen
              </p>
              <div className="space-y-0 rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                {creativoIdeas.map((idea, index, arr) => (
                  <button
                    key={`${index}-${idea.titulo}`}
                    type="button"
                    onClick={() => {
                      setCreativoPrompt(idea.prompt);
                      setCreativoPromptFromIdea(true);
                      closeCreativoIdeasMenu();
                    }}
                    className={`w-full text-left px-4 py-3 text-[15px] text-brand-black/80 hover:bg-neutral-50 active:bg-neutral-100 transition-colors ${index < arr.length - 1 ? "border-b border-black/[0.06]" : ""}`}
                  >
                    {idea.titulo}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-5 py-3 flex items-center justify-between">
              <button
                type="button"
                onClick={handleGenerateCreativoIdeas}
                disabled={creativoIdeasLoading}
                className="text-[15px] text-[#1644EB] hover:opacity-70 px-3 py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                <Lightbulb
                  className={`w-4 h-4 ${creativoIdeasLoading ? "animate-pulse" : ""}`}
                />
                <span>
                  {creativoIdeasLoading ? "Generando..." : "Generar más ideas"}
                </span>
              </button>
              <button
                type="button"
                onClick={closeCreativoIdeasMenu}
                className="text-[15px] font-semibold text-[#1644EB] hover:opacity-70 px-3 py-2 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
          {/* Mobile Slide - rendered via portal to cover Menu button */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${creativoIdeasMenuAnimated ? "opacity-100" : "opacity-0"}`}
                onClick={closeCreativoIdeasMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
                style={{
                  height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                  transform:
                    creativoIdeasMenuDragY > 0
                      ? `translateY(${creativoIdeasMenuDragY}px)`
                      : creativoIdeasMenuAnimated
                        ? "translateY(0)"
                        : "translateY(100%)",
                  transition:
                    creativoIdeasMenuDragY === 0
                      ? "transform 0.3s ease-out"
                      : "none",
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleCreativoIdeasTouchStart}
                  onTouchMove={handleCreativoIdeasTouchMove}
                  onTouchEnd={handleCreativoIdeasTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <span className="w-20" />
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Ideas generadas
                  </h3>
                  <button
                    type="button"
                    onClick={closeCreativoIdeasMenu}
                    className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right"
                  >
                    Cerrar
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  <p className="text-[15px] text-neutral-500 mb-4">
                    Seleccioná una idea para usarla como descripción de tu
                    imagen
                  </p>
                  <div className="space-y-0 rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                    {creativoIdeas.map((idea, index, arr) => (
                      <button
                        key={`${index}-${idea.titulo}`}
                        type="button"
                        onClick={() => {
                          setCreativoPrompt(idea.prompt);
                          setCreativoPromptFromIdea(true);
                          closeCreativoIdeasMenu();
                        }}
                        className={`w-full text-left px-4 py-3 text-[15px] text-brand-black/80 active:bg-neutral-100 transition-colors ${index < arr.length - 1 ? "border-b border-black/[0.06]" : ""}`}
                      >
                        {idea.titulo}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>,
            document.body,
          )}
        </>
      )}

      {/* Creativo Historial Menu */}
      {showCreativoHistorialMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              creativoHistorialMenuAnimated
                ? "translate-x-0"
                : "translate-x-[calc(100%+32px)]"
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">
                Historial
              </h3>
              <button
                type="button"
                onClick={closeCreativoHistorialMenu}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {creativoHistorial.length > 0 ? (
                <>
                  <p className="text-[15px] text-neutral-500 mb-4">
                    Seleccioná una instrucción anterior para reutilizarla
                  </p>
                  <div className="space-y-0 rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                    {creativoHistorial.map((prompt, index, arr) => (
                      <button
                        key={`${index}-${prompt.slice(0, 20)}`}
                        type="button"
                        onClick={() => {
                          setCreativoPrompt(prompt);
                          setCreativoPromptFromIdea(false);
                          closeCreativoHistorialMenu();
                        }}
                        className={`w-full text-left px-4 py-3 text-[15px] text-brand-black/80 hover:bg-neutral-50 active:bg-neutral-100 transition-colors ${index < arr.length - 1 ? "border-b border-black/[0.06]" : ""}`}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-[15px] text-neutral-400 text-center mt-8">
                  No hay instrucciones anteriores
                </p>
              )}
            </div>
            <div className="px-5 py-3 flex justify-end">
              <button
                type="button"
                onClick={closeCreativoHistorialMenu}
                className="text-[15px] font-semibold text-[#1644EB] hover:opacity-70 px-3 py-2 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
          {/* Mobile Slide */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${creativoHistorialMenuAnimated ? "opacity-100" : "opacity-0"}`}
                onClick={closeCreativoHistorialMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
                style={{
                  height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                  transform:
                    creativoHistorialMenuDragY > 0
                      ? `translateY(${creativoHistorialMenuDragY}px)`
                      : creativoHistorialMenuAnimated
                        ? "translateY(0)"
                        : "translateY(100%)",
                  transition:
                    creativoHistorialMenuDragY === 0
                      ? "transform 0.3s ease-out"
                      : "none",
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleCreativoHistorialTouchStart}
                  onTouchMove={handleCreativoHistorialTouchMove}
                  onTouchEnd={handleCreativoHistorialTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <span className="w-20" />
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Historial
                  </h3>
                  <button
                    type="button"
                    onClick={closeCreativoHistorialMenu}
                    className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right"
                  >
                    Cerrar
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  {creativoHistorial.length > 0 ? (
                    <>
                      <p className="text-[15px] text-neutral-500 mb-4">
                        Seleccioná una instrucción anterior para reutilizarla
                      </p>
                      <div className="space-y-0 rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                        {creativoHistorial.map((prompt, index, arr) => (
                          <button
                            key={`${index}-${prompt.slice(0, 20)}`}
                            type="button"
                            onClick={() => {
                              setCreativoPrompt(prompt);
                              setCreativoPromptFromIdea(false);
                              closeCreativoHistorialMenu();
                            }}
                            className={`w-full text-left px-4 py-3 text-[15px] text-brand-black/80 active:bg-neutral-100 transition-colors ${index < arr.length - 1 ? "border-b border-black/[0.06]" : ""}`}
                          >
                            {prompt}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-[15px] text-neutral-400 text-center mt-8">
                      No hay instrucciones anteriores
                    </p>
                  )}
                </div>
              </div>
            </>,
            document.body,
          )}
        </>
      )}

      {/* Modelo Personalizado Menu */}
      {showModeloPersonalizadoMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              modeloPersonalizadoMenuAnimated
                ? "translate-x-0"
                : "translate-x-[calc(100%+32px)]"
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">
                Modelo personalizado
              </h3>
              <button
                type="button"
                onClick={closeModeloPersonalizadoMenu}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <p className="text-[15px] text-neutral-500 mb-4">
                Definí las características del modelo para que se ajuste al
                estilo de tu marca.
              </p>

              <div className="space-y-5">
                {/* Género */}
                <div>
                  <label className="block text-[15px] text-neutral-700 mb-2">
                    Género
                  </label>
                  <div className="flex gap-2">
                    {["Hombre", "Mujer"].map((g) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() =>
                          setModeloGenero(modeloGenero === g ? "" : g)
                        }
                        className={`px-4 h-10 rounded-full border text-[15px] ${
                          modeloGenero === g
                            ? "bg-brand-primary text-white border-brand-primary"
                            : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                        }`}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Edad */}
                <div>
                  <label className="block text-[15px] text-neutral-700 mb-2">
                    Edad
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "Bebé",
                      "Niño",
                      "Joven (25 años)",
                      "Adulto/a(30 a 40 años)",
                      "Mayor",
                    ].map((e) => (
                      <button
                        key={e}
                        type="button"
                        onClick={() => setModeloEdad(modeloEdad === e ? "" : e)}
                        className={`px-4 h-10 rounded-full border text-[15px] ${
                          modeloEdad === e
                            ? "bg-brand-primary text-white border-brand-primary"
                            : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                        }`}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tipo de cuerpo */}
                <div>
                  <label className="block text-[15px] text-neutral-700 mb-2">
                    Tipo de cuerpo
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {["Promedio", "Talle Grande", "Talle Extra Grande"].map(
                      (c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() =>
                            setModeloTipoCuerpo(modeloTipoCuerpo === c ? "" : c)
                          }
                          className={`px-4 h-10 rounded-full border text-[15px] ${
                            modeloTipoCuerpo === c
                              ? "bg-brand-primary text-white border-brand-primary"
                              : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                          }`}
                        >
                          {c}
                        </button>
                      ),
                    )}
                  </div>
                </div>

                {/* Tono de piel */}
                <div>
                  <label className="block text-[15px] text-neutral-700 mb-2">
                    Tono de piel
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setModeloTonoPiel("")}
                      className={`px-4 h-10 rounded-full border text-[15px] ${
                        modeloTonoPiel === ""
                          ? "bg-brand-primary text-white border-brand-primary"
                          : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                      }`}
                    >
                      Cualquiera
                    </button>
                    {skinToneOptions.map(({ label, color }) => (
                      <button
                        key={label}
                        type="button"
                        title={label}
                        onClick={() =>
                          setModeloTonoPiel(
                            modeloTonoPiel === label ? "" : label,
                          )
                        }
                        className={`w-9 h-9 rounded-full border ${
                          modeloTonoPiel === label
                            ? "ring-2 ring-brand-primary ring-offset-2 border-brand-primary"
                            : "border-neutral-300"
                        } flex items-center justify-center`}
                        style={{ backgroundColor: color }}
                      >
                        {modeloTonoPiel === label && (
                          <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color de pelo */}
                <div>
                  <label className="block text-[15px] text-neutral-700 mb-2">
                    Color de pelo
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setModeloColorPelo("")}
                      className={`px-4 h-10 rounded-full border text-[15px] ${
                        modeloColorPelo === ""
                          ? "bg-brand-primary text-white border-brand-primary"
                          : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                      }`}
                    >
                      Cualquiera
                    </button>
                    {hairColorOptions.map(({ label, color }) => (
                      <button
                        key={label}
                        type="button"
                        title={label}
                        onClick={() =>
                          setModeloColorPelo(
                            modeloColorPelo === label ? "" : label,
                          )
                        }
                        className={`w-9 h-9 rounded-full border ${
                          modeloColorPelo === label
                            ? "ring-2 ring-brand-primary ring-offset-2 border-brand-primary"
                            : "border-neutral-300"
                        } flex items-center justify-center`}
                        style={{ backgroundColor: color }}
                      >
                        {modeloColorPelo === label && (
                          <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Largo de pelo */}
                <div>
                  <label className="block text-[15px] text-neutral-700 mb-2">
                    Largo de pelo
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setModeloLargoPelo("")}
                      className={`px-4 h-10 rounded-full border text-[15px] ${
                        modeloLargoPelo === ""
                          ? "bg-brand-primary text-white border-brand-primary"
                          : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                      }`}
                    >
                      Cualquiera
                    </button>
                    {["Rapado", "Corto", "Medio", "Largo"].map((l) => (
                      <button
                        key={l}
                        type="button"
                        onClick={() =>
                          setModeloLargoPelo(modeloLargoPelo === l ? "" : l)
                        }
                        className={`px-4 h-10 rounded-full border text-[15px] ${
                          modeloLargoPelo === l
                            ? "bg-brand-primary text-white border-brand-primary"
                            : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeModeloPersonalizadoMenu}
                className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleModeloPersonalizadoSubmit}
                className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors"
              >
                Aceptar
              </button>
            </div>
          </div>
          {/* Mobile Slide */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${modeloPersonalizadoMenuAnimated ? "opacity-100" : "opacity-0"}`}
                onClick={closeModeloPersonalizadoMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
                style={{
                  height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                  transform:
                    modeloPersonalizadoMenuDragY > 0
                      ? `translateY(${modeloPersonalizadoMenuDragY}px)`
                      : modeloPersonalizadoMenuAnimated
                        ? "translateY(0)"
                        : "translateY(100%)",
                  transition:
                    modeloPersonalizadoMenuDragY === 0
                      ? "transform 0.3s ease-out"
                      : "none",
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleModeloPersonalizadoTouchStart}
                  onTouchMove={handleModeloPersonalizadoTouchMove}
                  onTouchEnd={handleModeloPersonalizadoTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button
                    type="button"
                    onClick={closeModeloPersonalizadoMenu}
                    className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                  >
                    Cancelar
                  </button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Modelo personalizado
                  </h3>
                  <button
                    type="button"
                    onClick={handleModeloPersonalizadoSubmit}
                    className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right"
                  >
                    Aceptar
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  <p className="text-[15px] text-neutral-500 mb-4">
                    Definí las características del modelo para que se ajuste al
                    estilo de tu marca.
                  </p>

                  <div className="space-y-5">
                    {/* Género */}
                    <div>
                      <label className="block text-[15px] text-neutral-700 mb-2">
                        Género
                      </label>
                      <div className="flex gap-2">
                        {["Hombre", "Mujer"].map((g) => (
                          <button
                            key={g}
                            type="button"
                            onClick={() =>
                              setModeloGenero(modeloGenero === g ? "" : g)
                            }
                            className={`px-4 h-10 rounded-full border text-[15px] ${
                              modeloGenero === g
                                ? "bg-brand-primary text-white border-brand-primary"
                                : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                            }`}
                          >
                            {g}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Edad */}
                    <div>
                      <label className="block text-[15px] text-neutral-700 mb-2">
                        Edad
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          "Bebé",
                          "Niño",
                          "Joven (25 años)",
                          "Adulto/a(30 a 40 años)",
                          "Mayor",
                        ].map((e) => (
                          <button
                            key={e}
                            type="button"
                            onClick={() =>
                              setModeloEdad(modeloEdad === e ? "" : e)
                            }
                            className={`px-4 h-10 rounded-full border text-[15px] ${
                              modeloEdad === e
                                ? "bg-brand-primary text-white border-brand-primary"
                                : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                            }`}
                          >
                            {e}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Tipo de cuerpo */}
                    <div>
                      <label className="block text-[15px] text-neutral-700 mb-2">
                        Tipo de cuerpo
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {["Promedio", "Talle Grande", "Talle Extra Grande"].map(
                          (c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() =>
                                setModeloTipoCuerpo(
                                  modeloTipoCuerpo === c ? "" : c,
                                )
                              }
                              className={`px-4 h-10 rounded-full border text-[15px] ${
                                modeloTipoCuerpo === c
                                  ? "bg-brand-primary text-white border-brand-primary"
                                  : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                              }`}
                            >
                              {c}
                            </button>
                          ),
                        )}
                      </div>
                    </div>

                    {/* Tono de piel */}
                    <div>
                      <label className="block text-[15px] text-neutral-700 mb-2">
                        Tono de piel
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => setModeloTonoPiel("")}
                          className={`px-4 h-10 rounded-full border text-[15px] ${
                            modeloTonoPiel === ""
                              ? "bg-brand-primary text-white border-brand-primary"
                              : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                          }`}
                        >
                          Cualquiera
                        </button>
                        {skinToneOptions.map(({ label, color }) => (
                          <button
                            key={label}
                            type="button"
                            title={label}
                            onClick={() =>
                              setModeloTonoPiel(
                                modeloTonoPiel === label ? "" : label,
                              )
                            }
                            className={`w-9 h-9 rounded-full border ${
                              modeloTonoPiel === label
                                ? "ring-2 ring-brand-primary ring-offset-2 border-brand-primary"
                                : "border-neutral-300"
                            } flex items-center justify-center`}
                            style={{ backgroundColor: color }}
                          >
                            {modeloTonoPiel === label && (
                              <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Color de pelo */}
                    <div>
                      <label className="block text-[15px] text-neutral-700 mb-2">
                        Color de pelo
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => setModeloColorPelo("")}
                          className={`px-4 h-10 rounded-full border text-[15px] ${
                            modeloColorPelo === ""
                              ? "bg-brand-primary text-white border-brand-primary"
                              : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                          }`}
                        >
                          Cualquiera
                        </button>
                        {hairColorOptions.map(({ label, color }) => (
                          <button
                            key={label}
                            type="button"
                            title={label}
                            onClick={() =>
                              setModeloColorPelo(
                                modeloColorPelo === label ? "" : label,
                              )
                            }
                            className={`w-9 h-9 rounded-full border ${
                              modeloColorPelo === label
                                ? "ring-2 ring-brand-primary ring-offset-2 border-brand-primary"
                                : "border-neutral-300"
                            } flex items-center justify-center`}
                            style={{ backgroundColor: color }}
                          >
                            {modeloColorPelo === label && (
                              <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Largo de pelo */}
                    <div>
                      <label className="block text-[15px] text-neutral-700 mb-2">
                        Largo de pelo
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setModeloLargoPelo("")}
                          className={`px-4 h-10 rounded-full border text-[15px] ${
                            modeloLargoPelo === ""
                              ? "bg-brand-primary text-white border-brand-primary"
                              : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                          }`}
                        >
                          Cualquiera
                        </button>
                        {["Rapado", "Corto", "Medio", "Largo"].map((l) => (
                          <button
                            key={l}
                            type="button"
                            onClick={() =>
                              setModeloLargoPelo(modeloLargoPelo === l ? "" : l)
                            }
                            className={`px-4 h-10 rounded-full border text-[15px] ${
                              modeloLargoPelo === l
                                ? "bg-brand-primary text-white border-brand-primary"
                                : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                            }`}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>,
            document.body,
          )}
        </>
      )}

      {/* Modelo Guardado Menu */}
      {showModeloGuardadoMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              modeloGuardadoMenuAnimated
                ? "translate-x-0"
                : "translate-x-[calc(100%+32px)]"
            }`}
          >
            {showAddModelForm === "chooser" ? (
              <>
                <div className="flex items-center justify-between px-5 py-4">
                  <h3 className="text-[17px] font-semibold text-brand-black/90">
                    Añadir modelo
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModelForm(false);
                      resetAddModelState();
                    }}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="mx-5 h-px bg-black/[0.08]" />
                <div className="flex-1 overflow-y-auto px-5 py-4">
                  <p className="text-[15px] text-neutral-500 mb-4">
                    Elegí cómo querés crear tu modelo.
                  </p>
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => setShowAddModelForm("real")}
                      className="w-full flex items-center gap-4 p-4 bg-surface-card border border-neutral-200 rounded-lg hover:border-neutral-300 transition-colors text-left"
                    >
                      <div className="w-12 h-12 rounded-md bg-neutral-100 flex items-center justify-center shrink-0">
                        <ImagePlus className="w-6 h-6 text-neutral-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-semibold text-brand-black/90">
                          Añadir modelo real
                        </p>
                        <p className="text-[13px] text-neutral-500 mt-0.5">
                          Subí una foto del rostro de una persona real.
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-neutral-400 shrink-0" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddModelForm("ia")}
                      className="w-full flex items-center gap-4 p-4 bg-surface-card border border-neutral-200 rounded-lg hover:border-neutral-300 transition-colors text-left"
                    >
                      <div className="w-12 h-12 rounded-md bg-neutral-100 flex items-center justify-center shrink-0">
                        <Sparkles className="w-6 h-6 text-neutral-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-semibold text-brand-black/90">
                          Crear modelo con IA
                        </p>
                        <p className="text-[13px] text-neutral-500 mt-0.5">
                          Describí las características y generamos un modelo
                          para vos.
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-neutral-400 shrink-0" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddModelForm("historial")}
                      className="w-full flex items-center gap-4 p-4 bg-surface-card border border-neutral-200 rounded-lg hover:border-neutral-300 transition-colors text-left"
                    >
                      <div className="w-12 h-12 rounded-md bg-neutral-100 flex items-center justify-center shrink-0">
                        <ImageIcon className="w-6 h-6 text-neutral-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-semibold text-brand-black/90">
                          Desde imagen generada
                        </p>
                        <p className="text-[13px] text-neutral-500 mt-0.5">
                          Usá una imagen ya generada del historial como modelo.
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-neutral-400 shrink-0" />
                    </button>
                  </div>
                </div>
                <div className="px-5 py-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModelForm(false);
                      resetAddModelState();
                    }}
                    className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
                  >
                    Volver
                  </button>
                </div>
              </>
            ) : showAddModelForm === "real" ? (
              <>
                <div className="flex items-center justify-between px-5 py-4">
                  <h3 className="text-[17px] font-semibold text-brand-black/90">
                    Añadir modelo real
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModelForm(false);
                      resetAddModelState();
                    }}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="mx-5 h-px bg-black/[0.08]" />
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                  {/* Nombre */}
                  <div>
                    <label className="block text-[15px] font-medium text-neutral-700 mb-1">
                      Nombre del modelo
                    </label>
                    <input
                      type="text"
                      value={addModelName}
                      onChange={(e) => setAddModelName(e.target.value)}
                      placeholder="Ej: María, Juan, etc."
                      className="w-full rounded-md border border-neutral-200 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                  </div>

                  {/* Imagen */}
                  <div>
                    <label className="block text-[15px] font-medium text-neutral-700 mb-1">
                      Foto del rostro
                    </label>
                    {/* Example image + instructions (shown when no file uploaded) */}
                    {!addModelFile && (
                      <div className="bg-surface-card border border-neutral-200 rounded-lg p-3 sm:p-4 mb-3">
                        <div className="flex gap-3">
                          <div className="w-24 h-24 shrink-0 rounded-lg border border-neutral-200 bg-surface-card overflow-hidden relative">
                            <img
                              src="/images/cara_modelo_ejemplo.png"
                              alt="Ejemplo de foto de rostro"
                              className="w-full h-full object-cover"
                            />
                            <span className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-success text-white shadow-sm">
                              <Check className="w-3.5 h-3.5" />
                            </span>
                          </div>
                          <ul className="text-[13px] text-neutral-600 list-disc pl-4 space-y-1">
                            <li>
                              Subí una foto en donde se vea claramente el rostro
                              de la persona.
                            </li>
                            <li>
                              Evitá fotos con lentes de sol, máscaras o
                              elementos que tapen la cara.
                            </li>
                            <li>
                              Usá una foto con buena iluminación y fondo simple.
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}
                    {/* Converting HEIC indicator */}
                    {convertingHeic && !addModelFile && (
                      <div className="bg-surface-card border border-neutral-200 rounded-lg p-4 mb-3 flex items-center justify-center gap-3">
                        <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
                        <p className="text-[15px] text-neutral-600">
                          Subiendo imagen...
                        </p>
                      </div>
                    )}
                    {/* Uploaded file preview */}
                    {addModelFile && (
                      <div className="bg-surface-card border border-neutral-200 rounded-lg p-3 mb-3">
                        <div className="flex items-center gap-3">
                          {addModelPreviewUrl ? (
                            <img
                              src={addModelPreviewUrl}
                              alt="Preview"
                              className="w-20 h-20 object-cover rounded-md shrink-0"
                            />
                          ) : (
                            <div className="w-20 h-20 rounded-md shrink-0 bg-neutral-100 flex items-center justify-center">
                              <span className="material-symbols-rounded text-[24px] text-neutral-400">
                                image
                              </span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-[15px] text-neutral-700 truncate">
                              {addModelFile.name}
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                setAddModelFile(null);
                                setAddModelPreviewUrl(null);
                              }}
                              className="text-[13px] text-error hover:opacity-70 mt-1"
                            >
                              Eliminar
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Upload button */}
                    <input
                      type="file"
                      id="add-model-file-desktop"
                      accept="image/*,.heic,.heif"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleModelFileSelect(file);
                        e.target.value = "";
                      }}
                      className="hidden"
                    />
                    <label
                      htmlFor="add-model-file-desktop"
                      className={`inline-flex w-full justify-center items-center h-10 px-4 shadow-sm text-[15px] font-medium rounded-md cursor-pointer transition-colors ${convertingHeic ? "pointer-events-none opacity-50 border border-neutral-200 text-neutral-900 bg-surface-card" : !addModelFile ? "bg-brand-primary text-white hover:bg-brand-primary-hover" : "border border-neutral-200 text-neutral-900 bg-surface-card hover:bg-neutral-100"}`}
                    >
                      <ImagePlus className="w-4 h-4 mr-2" />
                      {convertingHeic
                        ? "Subiendo..."
                        : !addModelFile
                          ? "Subir foto del rostro"
                          : "Cambiar imagen"}
                    </label>
                  </div>

                  {/* Consentimientos */}
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={addModelConsentA}
                        onChange={(e) => setAddModelConsentA(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded border-neutral-300 text-brand-primary focus:ring-brand-primary"
                      />
                      <span className="text-[15px] text-neutral-600">
                        Confirmo que tengo los derechos de uso de esta imagen.
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={addModelConsentB}
                        onChange={(e) => setAddModelConsentB(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded border-neutral-300 text-brand-primary focus:ring-brand-primary"
                      />
                      <span className="text-[15px] text-neutral-600">
                        Acepto que esta imagen sea utilizada para generar
                        contenido con IA.
                      </span>
                    </label>
                  </div>

                  {addModelError && (
                    <p className="text-[15px] text-error">{addModelError}</p>
                  )}
                </div>
                <div className="px-5 py-3 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModelForm("chooser")}
                    className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
                  >
                    Volver
                  </button>
                  <button
                    type="button"
                    onClick={handleAddModelSave}
                    disabled={
                      addModelSaving ||
                      !addModelName.trim() ||
                      !addModelFile ||
                      !addModelConsentA ||
                      !addModelConsentB
                    }
                    className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors inline-flex items-center gap-1.5 ${addModelSaving || !addModelName.trim() || !addModelFile || !addModelConsentA || !addModelConsentB ? "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed" : "text-white bg-brand-primary hover:bg-brand-primary-hover"}`}
                  >
                    {addModelSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      "Guardar modelo"
                    )}
                  </button>
                </div>
              </>
            ) : showAddModelForm === "ia" ? (
              <>
                <div className="flex items-center justify-between px-5 py-4">
                  <h3 className="text-[17px] font-semibold text-brand-black/90">
                    Crear modelo con IA
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModelForm(false);
                      resetAddModelState();
                    }}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="mx-5 h-px bg-black/[0.08]" />
                <div className="flex-1 overflow-y-auto px-5 py-4">
                  {aiModelGenerating ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-3">
                      <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
                      <p className="text-[15px] text-neutral-500">
                        Generando modelo...
                      </p>
                    </div>
                  ) : aiModelResultB64 ? (
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-full max-w-[280px] aspect-square rounded-lg border border-neutral-200 overflow-hidden bg-neutral-50">
                        <img
                          src={`data:image/png;base64,${aiModelResultB64}`}
                          alt={aiModelName || "Modelo generado"}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <p className="text-[15px] font-medium text-brand-black/90">
                        {aiModelName}
                      </p>
                      <button
                        type="button"
                        onClick={handleAiModelGenerate}
                        className="text-[13px] text-brand-primary hover:opacity-70 transition-opacity"
                      >
                        Volver a generar
                      </button>
                      {aiModelError && (
                        <p className="text-[13px] text-error">{aiModelError}</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="text-[15px] text-neutral-500 mb-4">
                        Definí las características del modelo que querés
                        generar.
                      </p>
                      <div className="space-y-5">
                        {/* Nombre */}
                        <div>
                          <label className="block text-[15px] text-neutral-700 mb-2">
                            Nombre del modelo
                          </label>
                          <input
                            type="text"
                            value={aiModelName}
                            onChange={(e) => setAiModelName(e.target.value)}
                            placeholder="Ej: María, Juan, etc."
                            className="w-full rounded-md border border-neutral-200 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary"
                          />
                        </div>
                        {/* Género */}
                        <div>
                          <label className="block text-[15px] text-neutral-700 mb-2">
                            Género
                          </label>
                          <div className="flex gap-2">
                            {["Hombre", "Mujer"].map((g) => (
                              <button
                                key={g}
                                type="button"
                                onClick={() =>
                                  setAiModelGenero(aiModelGenero === g ? "" : g)
                                }
                                className={`px-4 h-10 rounded-full border text-[15px] ${aiModelGenero === g ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                              >
                                {g}
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Edad */}
                        <div>
                          <label className="block text-[15px] text-neutral-700 mb-2">
                            Edad
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {[
                              "Bebé",
                              "Niño",
                              "Joven (25 años)",
                              "Adulto/a(30 a 40 años)",
                              "Mayor",
                            ].map((e) => (
                              <button
                                key={e}
                                type="button"
                                onClick={() =>
                                  setAiModelEdad(aiModelEdad === e ? "" : e)
                                }
                                className={`px-4 h-10 rounded-full border text-[15px] ${aiModelEdad === e ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                              >
                                {e}
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Tipo de cuerpo */}
                        <div>
                          <label className="block text-[15px] text-neutral-700 mb-2">
                            Tipo de cuerpo
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {[
                              "Promedio",
                              "Talle Grande",
                              "Talle Extra Grande",
                            ].map((c) => (
                              <button
                                key={c}
                                type="button"
                                onClick={() =>
                                  setAiModelTipoCuerpo(
                                    aiModelTipoCuerpo === c ? "" : c,
                                  )
                                }
                                className={`px-4 h-10 rounded-full border text-[15px] ${aiModelTipoCuerpo === c ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                              >
                                {c}
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Tono de piel */}
                        <div>
                          <label className="block text-[15px] text-neutral-700 mb-2">
                            Tono de piel
                          </label>
                          <div className="flex gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => setAiModelTonoPiel("")}
                              className={`px-4 h-10 rounded-full border text-[15px] ${aiModelTonoPiel === "" ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                            >
                              Cualquiera
                            </button>
                            {skinToneOptions.map(({ label, color }) => (
                              <button
                                key={label}
                                type="button"
                                title={label}
                                onClick={() =>
                                  setAiModelTonoPiel(
                                    aiModelTonoPiel === label ? "" : label,
                                  )
                                }
                                className={`w-9 h-9 rounded-full border ${aiModelTonoPiel === label ? "ring-2 ring-brand-primary ring-offset-2 border-brand-primary" : "border-neutral-300"} flex items-center justify-center`}
                                style={{ backgroundColor: color }}
                              >
                                {aiModelTonoPiel === label && (
                                  <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Color de pelo */}
                        <div>
                          <label className="block text-[15px] text-neutral-700 mb-2">
                            Color de pelo
                          </label>
                          <div className="flex gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => setAiModelColorPelo("")}
                              className={`px-4 h-10 rounded-full border text-[15px] ${aiModelColorPelo === "" ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                            >
                              Cualquiera
                            </button>
                            {hairColorOptions.map(({ label, color }) => (
                              <button
                                key={label}
                                type="button"
                                title={label}
                                onClick={() =>
                                  setAiModelColorPelo(
                                    aiModelColorPelo === label ? "" : label,
                                  )
                                }
                                className={`w-9 h-9 rounded-full border ${aiModelColorPelo === label ? "ring-2 ring-brand-primary ring-offset-2 border-brand-primary" : "border-neutral-300"} flex items-center justify-center`}
                                style={{ backgroundColor: color }}
                              >
                                {aiModelColorPelo === label && (
                                  <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Largo de pelo */}
                        <div>
                          <label className="block text-[15px] text-neutral-700 mb-2">
                            Largo de pelo
                          </label>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => setAiModelLargoPelo("")}
                              className={`px-4 h-10 rounded-full border text-[15px] ${aiModelLargoPelo === "" ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                            >
                              Cualquiera
                            </button>
                            {["Rapado", "Corto", "Medio", "Largo"].map((l) => (
                              <button
                                key={l}
                                type="button"
                                onClick={() =>
                                  setAiModelLargoPelo(
                                    aiModelLargoPelo === l ? "" : l,
                                  )
                                }
                                className={`px-4 h-10 rounded-full border text-[15px] ${aiModelLargoPelo === l ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                              >
                                {l}
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Descripción adicional */}
                        <div>
                          <label className="block text-[15px] text-neutral-700 mb-2">
                            Descripción adicional
                          </label>
                          <textarea
                            rows={3}
                            value={aiModelDescripcion}
                            onChange={(e) =>
                              setAiModelDescripcion(e.target.value)
                            }
                            placeholder="Ej: rulos, sonriendo, ojos celestes, brazos cruzados"
                            className="w-full rounded-md border border-neutral-200 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary"
                          />
                        </div>
                      </div>
                      {aiModelError && (
                        <p className="text-[15px] text-error mt-4">
                          {aiModelError}
                        </p>
                      )}
                    </>
                  )}
                </div>
                <div className="px-5 py-3 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      if (aiModelResultB64) {
                        setAiModelResultB64(null);
                      } else {
                        setShowAddModelForm("chooser");
                      }
                    }}
                    className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
                  >
                    Volver
                  </button>
                  {aiModelResultB64 ? (
                    <button
                      type="button"
                      onClick={handleAiModelSave}
                      disabled={aiModelSaving}
                      className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors inline-flex items-center gap-1.5 ${aiModelSaving ? "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed" : "text-white bg-brand-primary hover:bg-brand-primary-hover"}`}
                    >
                      {aiModelSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Guardando...
                        </>
                      ) : (
                        "Guardar modelo"
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleAiModelGenerate}
                      disabled={!aiModelName.trim() || aiModelGenerating}
                      className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors inline-flex items-center gap-1.5 ${!aiModelName.trim() || aiModelGenerating ? "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed" : "text-white bg-brand-primary hover:bg-brand-primary-hover"}`}
                    >
                      <Sparkles className="w-4 h-4" />
                      Generar
                    </button>
                  )}
                </div>
              </>
            ) : showAddModelForm === "historial" ? (
              <>
                <div className="flex items-center justify-between px-5 py-4">
                  <h3 className="text-[17px] font-semibold text-brand-black/90">
                    Desde imagen generada
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModelForm(false);
                      resetAddModelState();
                    }}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="mx-5 h-px bg-black/[0.08]" />
                <div
                  ref={genContainerRefDesktop}
                  className="flex-1 overflow-y-auto px-5 py-4 relative"
                >
                  {(savingModelFromImage || historialSuccess) && (
                    <div className="absolute inset-0 z-20 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center text-center p-4">
                      {savingModelFromImage && !historialSuccess && (
                        <>
                          <Loader2 className="w-10 h-10 animate-spin text-brand-primary" />
                          <p className="mt-3 text-[15px] text-neutral-700">
                            Analizando modelo...
                          </p>
                          <p className="mt-1 text-[13px] text-neutral-500">
                            Esto puede tardar unos segundos.
                          </p>
                        </>
                      )}
                      {historialSuccess && (
                        <>
                          <CheckCircle2 className="w-12 h-12 text-green-600" />
                          <p className="mt-2 text-[15px] text-neutral-800">
                            Modelo añadido
                          </p>
                        </>
                      )}
                    </div>
                  )}
                  {selectedGenImage ? (
                    <div className="flex flex-col items-center gap-4">
                      <button
                        type="button"
                        onClick={() => setSelectedGenImage(null)}
                        className="self-start inline-flex items-center gap-1 text-[13px] text-brand-primary hover:opacity-70 transition-opacity"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Elegir otra
                      </button>
                      <div className="w-full max-w-[320px] aspect-square rounded-lg border border-neutral-200 overflow-hidden bg-neutral-50">
                        <img
                          src={
                            selectedGenImage.fullUrl ||
                            selectedGenImage.thumbUrl
                          }
                          alt="Imagen seleccionada"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <p className="text-[13px] text-neutral-500 text-center">
                        Se extraerá el rostro del modelo presente en esta
                        imagen.
                      </p>
                      {historialError && (
                        <p className="text-[13px] text-red-600">
                          {historialError}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="text-[15px] text-neutral-500 mb-4">
                        Elegí una imagen del historial para extraer el rostro
                        del modelo.
                      </p>
                      {historialError && (
                        <p className="text-[13px] text-red-600 mb-3">
                          {historialError}
                        </p>
                      )}
                      {genLoading && genImages.length === 0 ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
                        </div>
                      ) : genImages.length === 0 ? (
                        <div className="text-center py-12 text-[15px] text-neutral-500">
                          No hay imágenes generadas todavía.
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {genImages.map((g) => (
                            <button
                              key={g.id}
                              type="button"
                              onClick={() => setSelectedGenImage(g)}
                              className="relative rounded-md overflow-hidden border border-neutral-200 hover:border-neutral-300 transition-colors focus:outline-none"
                            >
                              {g.thumbUrl ? (
                                <img
                                  src={g.thumbUrl}
                                  alt={`Img ${g.id}`}
                                  className="w-full aspect-square object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full aspect-square bg-neutral-100" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                      {(genHasMore || genLoadingMore) && (
                        <div
                          ref={genLoadMoreRef}
                          className="py-3 text-center text-[13px] text-neutral-500"
                        >
                          {genLoadingMore ? (
                            <span className="inline-flex items-center">
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              Cargando más...
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="px-5 py-3 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModelForm("chooser")}
                    className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
                  >
                    Volver
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveFromGenerated}
                    disabled={
                      !selectedGenImage ||
                      savingModelFromImage ||
                      historialSuccess
                    }
                    className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${!selectedGenImage || savingModelFromImage || historialSuccess ? "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed" : "text-white bg-brand-primary hover:bg-brand-primary-hover"}`}
                  >
                    {savingModelFromImage
                      ? "Analizando..."
                      : "Usar este modelo"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between px-5 py-4">
                  <h3 className="text-[17px] font-semibold text-brand-black/90">
                    Modelo guardado
                  </h3>
                  <button
                    type="button"
                    onClick={closeModeloGuardadoMenu}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="mx-5 h-px bg-black/[0.08]" />
                <div className="flex-1 overflow-y-auto px-5 py-4">
                  <p className="text-[15px] text-neutral-500 mb-4">
                    Usá un modelo que ya tengas guardado en tu marca. Se
                    recomienda usar Prioridad en Calidad para mantener mejor la
                    consistencia de los rasgos faciales.
                  </p>

                  {savedModelsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-3">
                      {/* Añadir modelo button */}
                      <button
                        type="button"
                        onClick={() => setShowAddModelForm("chooser")}
                        className="relative rounded-md border border-neutral-200 overflow-hidden transition-colors hover:border-neutral-300 group bg-white"
                      >
                        <div className="w-full h-32 bg-neutral-50 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-brand-primary text-white flex items-center justify-center transition-colors group-hover:bg-brand-primary-hover">
                            <Plus className="w-6 h-6" />
                          </div>
                        </div>
                        <p className="text-[15px] text-neutral-900 p-2 font-medium">
                          Añadir modelo
                        </p>
                      </button>
                      {savedModels.map((m) => {
                        const selected = selectedSavedModelId === m.id;
                        return (
                          <div
                            key={m.id}
                            onClick={() => {
                              if (openModelMenuId !== null) {
                                setOpenModelMenuId(null);
                                return;
                              }
                              handleModeloGuardadoSelect(
                                selected ? null : m.id,
                              );
                            }}
                            className={`relative rounded-md border bg-white cursor-pointer transition-all duration-300 ${
                              deletingModelId === m.id
                                ? "opacity-0 scale-95"
                                : "opacity-100 scale-100"
                            } ${openModelMenuId === m.id ? "z-30" : ""} ${
                              selected
                                ? "border-brand-primary ring-2 ring-brand-primary"
                                : "border-neutral-200 hover:border-neutral-300"
                            }`}
                          >
                            <div className="relative overflow-hidden rounded-t-sm">
                              {m.signedUrl ? (
                                <img
                                  src={m.signedUrl}
                                  alt={m.nombre || "Modelo"}
                                  loading="lazy"
                                  className="w-full h-32 object-cover"
                                />
                              ) : (
                                <div className="w-full h-32 bg-neutral-100 flex items-center justify-center">
                                  <User className="w-8 h-8 text-neutral-400" />
                                </div>
                              )}
                              {selected && (
                                <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white">
                                  <Check className="w-4 h-4" />
                                </span>
                              )}
                            </div>
                            <div className="flex items-center p-2">
                              <p className="text-[15px] text-neutral-900 font-medium truncate flex-1">
                                {m.nombre || `Modelo #${m.id}`}
                              </p>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenModelMenuId(
                                    openModelMenuId === m.id ? null : m.id,
                                  );
                                }}
                                className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full hover:bg-neutral-100 transition-colors"
                              >
                                <MoreHorizontal className="w-3.5 h-3.5 text-neutral-400" />
                              </button>
                            </div>
                            {openModelMenuId === m.id && (
                              <div
                                className="absolute bottom-0 right-2 translate-y-full z-20 mt-1 bg-white rounded-md shadow-lg border border-neutral-200 py-1 min-w-[140px]"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onTouchEnd={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  onClick={() => handleDeleteModel(m.id)}
                                  className="w-full text-left px-3 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  Eliminar
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="px-5 py-3 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeModeloGuardadoMenu}
                    className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleModeloGuardadoSubmit}
                    disabled={selectedSavedModelId === null}
                    className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${selectedSavedModelId !== null ? "text-white bg-brand-primary hover:bg-brand-primary-hover" : "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed"}`}
                  >
                    Aceptar
                  </button>
                </div>
              </>
            )}
          </div>
          {/* Mobile Slide */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${modeloGuardadoMenuAnimated ? "opacity-100" : "opacity-0"}`}
                onClick={closeModeloGuardadoMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
                style={{
                  height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                  transform:
                    modeloGuardadoMenuDragY > 0
                      ? `translateY(${modeloGuardadoMenuDragY}px)`
                      : modeloGuardadoMenuAnimated
                        ? "translateY(0)"
                        : "translateY(100%)",
                  transition:
                    modeloGuardadoMenuDragY === 0
                      ? "transform 0.3s ease-out"
                      : "none",
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleModeloGuardadoTouchStart}
                  onTouchMove={handleModeloGuardadoTouchMove}
                  onTouchEnd={handleModeloGuardadoTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                {showAddModelForm === "chooser" ? (
                  <>
                    <div className="flex items-center justify-between px-4 pb-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddModelForm(false);
                          resetAddModelState();
                        }}
                        className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                      >
                        Volver
                      </button>
                      <h3 className="text-[15px] font-semibold text-brand-black/90">
                        Añadir modelo
                      </h3>
                      <span className="w-20" />
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 py-4">
                      <p className="text-[15px] text-neutral-500 mb-4">
                        Elegí cómo querés crear tu modelo.
                      </p>
                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={() => setShowAddModelForm("real")}
                          className="w-full flex items-center gap-4 p-4 bg-surface-card border border-neutral-200 rounded-lg active:bg-neutral-50 transition-colors text-left"
                        >
                          <div className="w-12 h-12 rounded-md bg-neutral-100 flex items-center justify-center shrink-0">
                            <ImagePlus className="w-6 h-6 text-neutral-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[15px] font-semibold text-brand-black/90">
                              Añadir modelo real
                            </p>
                            <p className="text-[13px] text-neutral-500 mt-0.5">
                              Subí una foto del rostro de una persona real.
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-neutral-400 shrink-0" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAddModelForm("ia")}
                          className="w-full flex items-center gap-4 p-4 bg-surface-card border border-neutral-200 rounded-lg active:bg-neutral-50 transition-colors text-left"
                        >
                          <div className="w-12 h-12 rounded-md bg-neutral-100 flex items-center justify-center shrink-0">
                            <Sparkles className="w-6 h-6 text-neutral-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[15px] font-semibold text-brand-black/90">
                              Crear modelo con IA
                            </p>
                            <p className="text-[13px] text-neutral-500 mt-0.5">
                              Describí las características y generamos un modelo
                              para vos.
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-neutral-400 shrink-0" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowAddModelForm("historial")}
                          className="w-full flex items-center gap-4 p-4 bg-surface-card border border-neutral-200 rounded-lg active:bg-neutral-50 transition-colors text-left"
                        >
                          <div className="w-12 h-12 rounded-md bg-neutral-100 flex items-center justify-center shrink-0">
                            <ImageIcon className="w-6 h-6 text-neutral-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[15px] font-semibold text-brand-black/90">
                              Desde imagen generada
                            </p>
                            <p className="text-[13px] text-neutral-500 mt-0.5">
                              Usá una imagen ya generada del historial como
                              modelo.
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-neutral-400 shrink-0" />
                        </button>
                      </div>
                    </div>
                  </>
                ) : showAddModelForm === "real" ? (
                  <>
                    <div className="flex items-center justify-between px-4 pb-3">
                      <button
                        type="button"
                        onClick={() => setShowAddModelForm("chooser")}
                        className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                      >
                        Volver
                      </button>
                      <h3 className="text-[15px] font-semibold text-brand-black/90">
                        Modelo real
                      </h3>
                      <button
                        type="button"
                        onClick={handleAddModelSave}
                        disabled={
                          addModelSaving ||
                          !addModelName.trim() ||
                          !addModelFile ||
                          !addModelConsentA ||
                          !addModelConsentB
                        }
                        className={`text-[15px] font-semibold w-20 text-right transition-opacity ${addModelSaving || !addModelName.trim() || !addModelFile || !addModelConsentA || !addModelConsentB ? "text-[#3C3C43]/20 cursor-not-allowed" : "text-[#1644EB] active:opacity-50"}`}
                      >
                        {addModelSaving ? "Guardando" : "Guardar"}
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                      {/* Nombre */}
                      <div>
                        <label className="block text-[15px] font-medium text-neutral-700 mb-1">
                          Nombre del modelo
                        </label>
                        <input
                          type="text"
                          value={addModelName}
                          onChange={(e) => setAddModelName(e.target.value)}
                          placeholder="Ej: María, Juan, etc."
                          className="w-full rounded-md border border-neutral-200 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary"
                        />
                      </div>

                      {/* Imagen */}
                      <div>
                        <label className="block text-[15px] font-medium text-neutral-700 mb-1">
                          Foto del rostro
                        </label>
                        {/* Example image + instructions (shown when no file uploaded) */}
                        {!addModelFile && (
                          <div className="bg-surface-card border border-neutral-200 rounded-lg p-3 mb-3">
                            <div className="flex gap-3">
                              <div className="w-24 h-24 shrink-0 rounded-lg border border-neutral-200 bg-surface-card overflow-hidden relative">
                                <img
                                  src="/images/cara_modelo_ejemplo.png"
                                  alt="Ejemplo de foto de rostro"
                                  className="w-full h-full object-cover"
                                />
                                <span className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-success text-white shadow-sm">
                                  <Check className="w-3.5 h-3.5" />
                                </span>
                              </div>
                              <ul className="text-[13px] text-neutral-600 list-disc pl-4 space-y-1">
                                <li>
                                  Subí una foto en donde se vea claramente el
                                  rostro de la persona.
                                </li>
                                <li>
                                  Evitá fotos con lentes de sol, máscaras o
                                  elementos que tapen la cara.
                                </li>
                                <li>
                                  Usá una foto con buena iluminación y fondo
                                  simple.
                                </li>
                              </ul>
                            </div>
                          </div>
                        )}
                        {/* Converting HEIC indicator */}
                        {convertingHeic && !addModelFile && (
                          <div className="bg-surface-card border border-neutral-200 rounded-lg p-4 mb-3 flex items-center justify-center gap-3">
                            <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
                            <p className="text-[15px] text-neutral-600">
                              Subiendo imagen...
                            </p>
                          </div>
                        )}
                        {/* Uploaded file preview */}
                        {addModelFile && (
                          <div className="bg-surface-card border border-neutral-200 rounded-lg p-3 mb-3">
                            <div className="flex items-center gap-3">
                              {addModelPreviewUrl ? (
                                <img
                                  src={addModelPreviewUrl}
                                  alt="Preview"
                                  className="w-20 h-20 object-cover rounded-md shrink-0"
                                />
                              ) : (
                                <div className="w-20 h-20 rounded-md shrink-0 bg-neutral-100 flex items-center justify-center">
                                  <span className="material-symbols-rounded text-[24px] text-neutral-400">
                                    image
                                  </span>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-[15px] text-neutral-700 truncate">
                                  {addModelFile.name}
                                </p>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAddModelFile(null);
                                    setAddModelPreviewUrl(null);
                                  }}
                                  className="text-[13px] text-error hover:opacity-70 mt-1"
                                >
                                  Eliminar
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Upload button */}
                        <input
                          type="file"
                          id="add-model-file-mobile"
                          accept="image/*,.heic,.heif"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleModelFileSelect(file);
                            e.target.value = "";
                          }}
                          className="hidden"
                        />
                        <label
                          htmlFor="add-model-file-mobile"
                          className={`inline-flex w-full justify-center items-center h-10 px-4 shadow-sm text-[15px] font-medium rounded-md cursor-pointer transition-colors ${convertingHeic ? "pointer-events-none opacity-50 border border-neutral-200 text-neutral-900 bg-surface-card" : !addModelFile ? "bg-brand-primary text-white hover:bg-brand-primary-hover" : "border border-neutral-200 text-neutral-900 bg-surface-card hover:bg-neutral-100"}`}
                        >
                          <ImagePlus className="w-4 h-4 mr-2" />
                          {convertingHeic
                            ? "Subiendo..."
                            : !addModelFile
                              ? "Subir foto del rostro"
                              : "Cambiar imagen"}
                        </label>
                      </div>

                      {/* Consentimientos */}
                      <div className="space-y-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={addModelConsentA}
                            onChange={(e) =>
                              setAddModelConsentA(e.target.checked)
                            }
                            className="mt-1 w-4 h-4 rounded border-neutral-300 text-brand-primary focus:ring-brand-primary"
                          />
                          <span className="text-[15px] text-neutral-600">
                            Confirmo que tengo los derechos de uso de esta
                            imagen.
                          </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={addModelConsentB}
                            onChange={(e) =>
                              setAddModelConsentB(e.target.checked)
                            }
                            className="mt-1 w-4 h-4 rounded border-neutral-300 text-brand-primary focus:ring-brand-primary"
                          />
                          <span className="text-[15px] text-neutral-600">
                            Acepto que esta imagen sea utilizada para generar
                            contenido con IA.
                          </span>
                        </label>
                      </div>

                      {addModelError && (
                        <p className="text-[15px] text-error">
                          {addModelError}
                        </p>
                      )}
                    </div>
                  </>
                ) : showAddModelForm === "ia" ? (
                  <>
                    <div className="flex items-center justify-between px-4 pb-3">
                      <button
                        type="button"
                        onClick={() => {
                          if (aiModelResultB64) {
                            setAiModelResultB64(null);
                          } else {
                            setShowAddModelForm("chooser");
                          }
                        }}
                        className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                      >
                        Volver
                      </button>
                      <h3 className="text-[15px] font-semibold text-brand-black/90">
                        Modelo con IA
                      </h3>
                      {aiModelResultB64 ? (
                        <button
                          type="button"
                          onClick={handleAiModelSave}
                          disabled={aiModelSaving}
                          className={`text-[15px] font-semibold w-20 text-right transition-opacity ${aiModelSaving ? "text-[#3C3C43]/20 cursor-not-allowed" : "text-[#1644EB] active:opacity-50"}`}
                        >
                          {aiModelSaving ? "Guardando" : "Guardar"}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleAiModelGenerate}
                          disabled={!aiModelName.trim() || aiModelGenerating}
                          className={`text-[15px] font-semibold w-20 text-right transition-opacity ${!aiModelName.trim() || aiModelGenerating ? "text-[#3C3C43]/20 cursor-not-allowed" : "text-[#1644EB] active:opacity-50"}`}
                        >
                          Generar
                        </button>
                      )}
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 py-4">
                      {aiModelGenerating ? (
                        <div className="flex flex-col items-center justify-center py-16 gap-3">
                          <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
                          <p className="text-[15px] text-neutral-500">
                            Generando modelo...
                          </p>
                        </div>
                      ) : aiModelResultB64 ? (
                        <div className="flex flex-col items-center gap-4">
                          <div className="w-full max-w-[240px] aspect-square rounded-lg border border-neutral-200 overflow-hidden bg-neutral-50">
                            <img
                              src={`data:image/png;base64,${aiModelResultB64}`}
                              alt={aiModelName || "Modelo generado"}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="text-[15px] font-medium text-brand-black/90">
                            {aiModelName}
                          </p>
                          <button
                            type="button"
                            onClick={handleAiModelGenerate}
                            className="text-[13px] text-brand-primary active:opacity-50 transition-opacity"
                          >
                            Volver a generar
                          </button>
                          {aiModelError && (
                            <p className="text-[13px] text-error">
                              {aiModelError}
                            </p>
                          )}
                        </div>
                      ) : (
                        <>
                          <p className="text-[15px] text-neutral-500 mb-4">
                            Definí las características del modelo que querés
                            generar.
                          </p>
                          <div className="space-y-5">
                            {/* Nombre */}
                            <div>
                              <label className="block text-[15px] text-neutral-700 mb-2">
                                Nombre del modelo
                              </label>
                              <input
                                type="text"
                                value={aiModelName}
                                onChange={(e) => setAiModelName(e.target.value)}
                                placeholder="Ej: María, Juan, etc."
                                className="w-full rounded-md border border-neutral-200 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary"
                              />
                            </div>
                            {/* Género */}
                            <div>
                              <label className="block text-[15px] text-neutral-700 mb-2">
                                Género
                              </label>
                              <div className="flex gap-2">
                                {["Hombre", "Mujer"].map((g) => (
                                  <button
                                    key={g}
                                    type="button"
                                    onClick={() =>
                                      setAiModelGenero(
                                        aiModelGenero === g ? "" : g,
                                      )
                                    }
                                    className={`px-4 h-10 rounded-full border text-[15px] ${aiModelGenero === g ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                                  >
                                    {g}
                                  </button>
                                ))}
                              </div>
                            </div>
                            {/* Edad */}
                            <div>
                              <label className="block text-[15px] text-neutral-700 mb-2">
                                Edad
                              </label>
                              <div className="flex flex-wrap gap-2">
                                {[
                                  "Bebé",
                                  "Niño",
                                  "Joven (25 años)",
                                  "Adulto/a(30 a 40 años)",
                                  "Mayor",
                                ].map((e) => (
                                  <button
                                    key={e}
                                    type="button"
                                    onClick={() =>
                                      setAiModelEdad(aiModelEdad === e ? "" : e)
                                    }
                                    className={`px-4 h-10 rounded-full border text-[15px] ${aiModelEdad === e ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                                  >
                                    {e}
                                  </button>
                                ))}
                              </div>
                            </div>
                            {/* Tipo de cuerpo */}
                            <div>
                              <label className="block text-[15px] text-neutral-700 mb-2">
                                Tipo de cuerpo
                              </label>
                              <div className="flex flex-wrap gap-2">
                                {[
                                  "Promedio",
                                  "Talle Grande",
                                  "Talle Extra Grande",
                                ].map((c) => (
                                  <button
                                    key={c}
                                    type="button"
                                    onClick={() =>
                                      setAiModelTipoCuerpo(
                                        aiModelTipoCuerpo === c ? "" : c,
                                      )
                                    }
                                    className={`px-4 h-10 rounded-full border text-[15px] ${aiModelTipoCuerpo === c ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                                  >
                                    {c}
                                  </button>
                                ))}
                              </div>
                            </div>
                            {/* Tono de piel */}
                            <div>
                              <label className="block text-[15px] text-neutral-700 mb-2">
                                Tono de piel
                              </label>
                              <div className="flex gap-2 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() => setAiModelTonoPiel("")}
                                  className={`px-4 h-10 rounded-full border text-[15px] ${aiModelTonoPiel === "" ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                                >
                                  Cualquiera
                                </button>
                                {skinToneOptions.map(({ label, color }) => (
                                  <button
                                    key={label}
                                    type="button"
                                    title={label}
                                    onClick={() =>
                                      setAiModelTonoPiel(
                                        aiModelTonoPiel === label ? "" : label,
                                      )
                                    }
                                    className={`w-9 h-9 rounded-full border ${aiModelTonoPiel === label ? "ring-2 ring-brand-primary ring-offset-2 border-brand-primary" : "border-neutral-300"} flex items-center justify-center`}
                                    style={{ backgroundColor: color }}
                                  >
                                    {aiModelTonoPiel === label && (
                                      <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                            {/* Color de pelo */}
                            <div>
                              <label className="block text-[15px] text-neutral-700 mb-2">
                                Color de pelo
                              </label>
                              <div className="flex gap-2 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() => setAiModelColorPelo("")}
                                  className={`px-4 h-10 rounded-full border text-[15px] ${aiModelColorPelo === "" ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                                >
                                  Cualquiera
                                </button>
                                {hairColorOptions.map(({ label, color }) => (
                                  <button
                                    key={label}
                                    type="button"
                                    title={label}
                                    onClick={() =>
                                      setAiModelColorPelo(
                                        aiModelColorPelo === label ? "" : label,
                                      )
                                    }
                                    className={`w-9 h-9 rounded-full border ${aiModelColorPelo === label ? "ring-2 ring-brand-primary ring-offset-2 border-brand-primary" : "border-neutral-300"} flex items-center justify-center`}
                                    style={{ backgroundColor: color }}
                                  >
                                    {aiModelColorPelo === label && (
                                      <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                            {/* Largo de pelo */}
                            <div>
                              <label className="block text-[15px] text-neutral-700 mb-2">
                                Largo de pelo
                              </label>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => setAiModelLargoPelo("")}
                                  className={`px-4 h-10 rounded-full border text-[15px] ${aiModelLargoPelo === "" ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                                >
                                  Cualquiera
                                </button>
                                {["Rapado", "Corto", "Medio", "Largo"].map(
                                  (l) => (
                                    <button
                                      key={l}
                                      type="button"
                                      onClick={() =>
                                        setAiModelLargoPelo(
                                          aiModelLargoPelo === l ? "" : l,
                                        )
                                      }
                                      className={`px-4 h-10 rounded-full border text-[15px] ${aiModelLargoPelo === l ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"}`}
                                    >
                                      {l}
                                    </button>
                                  ),
                                )}
                              </div>
                            </div>
                            {/* Descripción adicional */}
                            <div>
                              <label className="block text-[15px] text-neutral-700 mb-2">
                                Descripción adicional
                              </label>
                              <textarea
                                rows={3}
                                value={aiModelDescripcion}
                                onChange={(e) =>
                                  setAiModelDescripcion(e.target.value)
                                }
                                placeholder="Ej: rulos, sonriendo, ojos celestes, brazos cruzados"
                                className="w-full rounded-md border border-neutral-200 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary"
                              />
                            </div>
                          </div>
                          {aiModelError && (
                            <p className="text-[15px] text-error mt-4">
                              {aiModelError}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </>
                ) : showAddModelForm === "historial" ? (
                  <>
                    <div className="flex items-center justify-between px-4 pb-3">
                      <button
                        type="button"
                        onClick={() => {
                          if (selectedGenImage) {
                            setSelectedGenImage(null);
                          } else {
                            setShowAddModelForm("chooser");
                          }
                        }}
                        className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                      >
                        Volver
                      </button>
                      <h3 className="text-[15px] font-semibold text-brand-black/90">
                        Historial
                      </h3>
                      <button
                        type="button"
                        onClick={handleSaveFromGenerated}
                        disabled={
                          !selectedGenImage ||
                          savingModelFromImage ||
                          historialSuccess
                        }
                        className={`text-[15px] font-semibold w-20 text-right transition-opacity ${!selectedGenImage || savingModelFromImage || historialSuccess ? "text-[#3C3C43]/20 cursor-not-allowed" : "text-[#1644EB] active:opacity-50"}`}
                      >
                        {savingModelFromImage ? "..." : "Usar"}
                      </button>
                    </div>
                    <div
                      ref={genContainerRefMobile}
                      className="flex-1 overflow-y-auto px-4 py-4 relative"
                    >
                      {(savingModelFromImage || historialSuccess) && (
                        <div className="absolute inset-0 z-20 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center text-center p-4">
                          {savingModelFromImage && !historialSuccess && (
                            <>
                              <Loader2 className="w-10 h-10 animate-spin text-brand-primary" />
                              <p className="mt-3 text-[15px] text-neutral-700">
                                Analizando modelo...
                              </p>
                              <p className="mt-1 text-[13px] text-neutral-500">
                                Esto puede tardar unos segundos.
                              </p>
                            </>
                          )}
                          {historialSuccess && (
                            <>
                              <CheckCircle2 className="w-12 h-12 text-green-600" />
                              <p className="mt-2 text-[15px] text-neutral-800">
                                Modelo añadido
                              </p>
                            </>
                          )}
                        </div>
                      )}
                      {selectedGenImage ? (
                        <div className="flex flex-col items-center gap-4">
                          <div className="w-full max-w-[320px] aspect-square rounded-lg border border-neutral-200 overflow-hidden bg-neutral-50">
                            <img
                              src={
                                selectedGenImage.fullUrl ||
                                selectedGenImage.thumbUrl
                              }
                              alt="Imagen seleccionada"
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <p className="text-[13px] text-neutral-500 text-center">
                            Se extraerá el rostro del modelo presente en esta
                            imagen.
                          </p>
                          {historialError && (
                            <p className="text-[13px] text-red-600">
                              {historialError}
                            </p>
                          )}
                        </div>
                      ) : (
                        <>
                          <p className="text-[15px] text-neutral-500 mb-4">
                            Elegí una imagen del historial para extraer el
                            rostro del modelo.
                          </p>
                          {historialError && (
                            <p className="text-[13px] text-red-600 mb-3">
                              {historialError}
                            </p>
                          )}
                          {genLoading && genImages.length === 0 ? (
                            <div className="flex items-center justify-center py-12">
                              <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
                            </div>
                          ) : genImages.length === 0 ? (
                            <div className="text-center py-12 text-[15px] text-neutral-500">
                              No hay imágenes generadas todavía.
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-2">
                              {genImages.map((g) => (
                                <button
                                  key={g.id}
                                  type="button"
                                  onClick={() => setSelectedGenImage(g)}
                                  className="relative rounded-md overflow-hidden border border-neutral-200 active:border-neutral-300 transition-colors focus:outline-none"
                                >
                                  {g.thumbUrl ? (
                                    <img
                                      src={g.thumbUrl}
                                      alt={`Img ${g.id}`}
                                      className="w-full aspect-square object-cover"
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div className="w-full aspect-square bg-neutral-100" />
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                          {(genHasMore || genLoadingMore) && (
                            <div
                              ref={genLoadMoreRef}
                              className="py-3 text-center text-[13px] text-neutral-500"
                            >
                              {genLoadingMore ? (
                                <span className="inline-flex items-center">
                                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                  Cargando más...
                                </span>
                              ) : (
                                ""
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between px-4 pb-3">
                      <button
                        type="button"
                        onClick={closeModeloGuardadoMenu}
                        className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                      >
                        Cancelar
                      </button>
                      <h3 className="text-[15px] font-semibold text-brand-black/90">
                        Modelo guardado
                      </h3>
                      <button
                        type="button"
                        onClick={handleModeloGuardadoSubmit}
                        disabled={selectedSavedModelId === null}
                        className={`text-[15px] font-semibold w-20 text-right transition-opacity ${selectedSavedModelId !== null ? "text-[#1644EB] active:opacity-50" : "text-[#3C3C43]/20 cursor-not-allowed"}`}
                      >
                        Aceptar
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 py-4">
                      <p className="text-[15px] text-neutral-500 mb-4">
                        Usá un modelo que ya tengas guardado en tu marca. Se
                        recomienda usar Prioridad en Calidad para mantener mejor
                        la consistencia de los rasgos faciales.
                      </p>

                      {savedModelsLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          {/* Añadir modelo button */}
                          <button
                            type="button"
                            onClick={() => setShowAddModelForm("chooser")}
                            className="relative rounded-md border border-neutral-200 overflow-hidden transition-colors hover:border-neutral-300 group bg-white"
                          >
                            <div className="w-full h-32 bg-neutral-50 flex items-center justify-center">
                              <div className="w-12 h-12 rounded-full bg-brand-primary text-white flex items-center justify-center transition-colors group-hover:bg-brand-primary-hover">
                                <Plus className="w-6 h-6" />
                              </div>
                            </div>
                            <p className="text-[15px] text-neutral-900 p-2 font-medium">
                              Añadir modelo
                            </p>
                          </button>
                          {savedModels.map((m) => {
                            const selected = selectedSavedModelId === m.id;
                            return (
                              <div
                                key={m.id}
                                onClick={() => {
                                  if (openModelMenuId !== null) {
                                    setOpenModelMenuId(null);
                                    return;
                                  }
                                  setSelectedSavedModelId(
                                    selected ? null : m.id,
                                  );
                                }}
                                className={`relative rounded-md border bg-white cursor-pointer transition-all duration-300 ${
                                  deletingModelId === m.id
                                    ? "opacity-0 scale-95"
                                    : "opacity-100 scale-100"
                                } ${openModelMenuId === m.id ? "z-30" : ""} ${
                                  selected
                                    ? "border-brand-primary ring-2 ring-brand-primary"
                                    : "border-neutral-200 hover:border-neutral-300"
                                }`}
                              >
                                <div className="relative overflow-hidden rounded-t-sm">
                                  {m.signedUrl ? (
                                    <img
                                      src={m.signedUrl}
                                      alt={m.nombre || "Modelo"}
                                      loading="lazy"
                                      className="w-full h-32 object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-32 bg-neutral-100 flex items-center justify-center">
                                      <User className="w-8 h-8 text-neutral-400" />
                                    </div>
                                  )}
                                  {selected && (
                                    <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white">
                                      <Check className="w-4 h-4" />
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center p-2">
                                  <p className="text-[15px] text-neutral-900 font-medium truncate flex-1">
                                    {m.nombre || `Modelo #${m.id}`}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenModelMenuId(
                                        openModelMenuId === m.id ? null : m.id,
                                      );
                                    }}
                                    className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full hover:bg-neutral-100 transition-colors"
                                  >
                                    <MoreHorizontal className="w-3.5 h-3.5 text-neutral-400" />
                                  </button>
                                </div>
                                {openModelMenuId === m.id && (
                                  <div
                                    className="absolute bottom-0 right-2 translate-y-full z-20 mt-1 bg-white rounded-md shadow-lg border border-neutral-200 py-1 min-w-[140px]"
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onTouchEnd={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteModel(m.id)}
                                      className="w-full text-left px-3 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                      Eliminar
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>,
            document.body,
          )}
        </>
      )}

      {/* Pose Menu */}
      {showPoseMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              poseMenuAnimated
                ? "translate-x-0"
                : "translate-x-[calc(100%+32px)]"
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">
                Pose del modelo
              </h3>
              <button
                type="button"
                onClick={closePoseMenu}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[15px] text-brand-black/90">
                  Seleccionar múltiples
                </span>
                <label className="relative inline-flex cursor-pointer">
                  <input
                    type="checkbox"
                    checked={multiPoseToggle}
                    onChange={(e) => handleMultiPoseToggle(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-[51px] h-[31px] bg-black/[0.09] rounded-full peer peer-checked:bg-[#1644EB] transition-colors" />
                  <div className="absolute top-[2px] left-[2px] w-[27px] h-[27px] bg-white rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.15)] transition-transform peer-checked:translate-x-[20px]" />
                </label>
              </div>
              {multiPoseToggle && selectedPoses.length > 1 && (
                <p className="text-[13px] text-[#3C3C43]/60 mb-3">
                  {selectedPoses.length} poses seleccionadas — se generará 1
                  imagen por pose.
                </p>
              )}

              <div className="flex flex-wrap gap-2 mb-4">
                {POSE_TABS.map((tab) => {
                  const isActive = poseActiveTab === tab.key;
                  const hasSel =
                    multiPoseToggle &&
                    !isActive &&
                    tab.key !== "recomendado" &&
                    poseTabsWithSelection.has(tab.key);
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setPoseActiveTab(tab.key)}
                      className={`relative inline-flex items-center gap-1.5 px-4 h-9 rounded-full border text-[15px] whitespace-nowrap ${
                        isActive
                          ? "bg-brand-primary text-white border-brand-primary"
                          : "bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50"
                      }`}
                    >
                      {tab.key === "recomendado" && (
                        <Sparkles className="w-3.5 h-3.5" />
                      )}
                      {tab.label}
                      {hasSel && (
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#1644EB] border-2 border-white" />
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {filteredPoses.map((pose) => {
                  const isSelected = selectedPoses.includes(pose.id);
                  return (
                    <button
                      key={pose.id}
                      type="button"
                      onClick={() => handlePoseClick(pose.id)}
                      className={`relative rounded-[13px] overflow-hidden transition-all flex flex-col bg-white ${isSelected ? "ring-2 ring-[#1644EB]" : "ring-1 ring-black/[0.06] active:bg-black/[0.02]"}`}
                    >
                      {pose.image && (
                        <img
                          src={pose.image}
                          alt={pose.name}
                          loading="lazy"
                          className="block w-full aspect-[4/5] object-cover object-[center_15%]"
                        />
                      )}
                      {isSelected && (
                        <span className="absolute top-2.5 right-2.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1644EB] text-white shadow-sm">
                          <Check className="w-3.5 h-3.5" />
                        </span>
                      )}
                      {multiPoseToggle && !isSelected && (
                        <span className="absolute top-2.5 right-2.5 inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-black/[0.15] bg-white/80" />
                      )}
                      <p
                        className={`text-[13px] text-brand-black/90 px-3 py-2.5 font-medium text-center flex-1 flex items-center justify-center ${!pose.image ? "py-6" : ""}`}
                      >
                        {pose.name}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button
                type="button"
                onClick={closePoseMenu}
                className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handlePoseSubmit}
                disabled={selectedPoses.length === 0}
                className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${selectedPoses.length > 0 ? "text-white bg-brand-primary hover:bg-brand-primary-hover" : "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed"}`}
              >
                Aceptar
              </button>
            </div>
          </div>
          {/* Mobile Slide */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${poseMenuAnimated ? "opacity-100" : "opacity-0"}`}
                onClick={closePoseMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
                style={{
                  height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                  transform:
                    poseMenuDragY > 0
                      ? `translateY(${poseMenuDragY}px)`
                      : poseMenuAnimated
                        ? "translateY(0)"
                        : "translateY(100%)",
                  transition:
                    poseMenuDragY === 0 ? "transform 0.3s ease-out" : "none",
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handlePoseTouchStart}
                  onTouchMove={handlePoseTouchMove}
                  onTouchEnd={handlePoseTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button
                    type="button"
                    onClick={closePoseMenu}
                    className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                  >
                    Cancelar
                  </button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Pose del modelo
                  </h3>
                  <button
                    type="button"
                    onClick={handlePoseSubmit}
                    disabled={selectedPoses.length === 0}
                    className={`text-[15px] font-semibold w-20 text-right transition-opacity ${selectedPoses.length > 0 ? "text-[#1644EB] active:opacity-50" : "text-[#3C3C43]/20 cursor-not-allowed"}`}
                  >
                    Aceptar
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 pt-2 pb-4">
                  <div className="bg-white rounded-[13px] px-4 py-[11px] flex items-center justify-between mb-4">
                    <span className="text-[15px] text-brand-black/90">
                      Seleccionar múltiples
                    </span>
                    <label className="relative inline-flex cursor-pointer">
                      <input
                        type="checkbox"
                        checked={multiPoseToggle}
                        onChange={(e) =>
                          handleMultiPoseToggle(e.target.checked)
                        }
                        className="sr-only peer"
                      />
                      <div className="w-[51px] h-[31px] bg-black/[0.09] rounded-full peer peer-checked:bg-[#1644EB] transition-colors" />
                      <div className="absolute top-[2px] left-[2px] w-[27px] h-[27px] bg-white rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.15)] transition-transform peer-checked:translate-x-[20px]" />
                    </label>
                  </div>
                  {multiPoseToggle && selectedPoses.length > 1 && (
                    <p className="text-[13px] text-[#3C3C43]/60 px-4 mb-3">
                      {selectedPoses.length} poses seleccionadas — se generará 1
                      imagen por pose.
                    </p>
                  )}

                  <div className="flex flex-wrap gap-2 mb-4">
                    {POSE_TABS.map((tab) => {
                      const isActive = poseActiveTab === tab.key;
                      const hasSel =
                        multiPoseToggle &&
                        !isActive &&
                        tab.key !== "recomendado" &&
                        poseTabsWithSelection.has(tab.key);
                      return (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setPoseActiveTab(tab.key)}
                          className={`relative inline-flex items-center gap-1.5 px-4 h-9 rounded-full border text-[15px] whitespace-nowrap ${
                            isActive
                              ? "bg-brand-primary text-white border-brand-primary"
                              : "bg-white text-neutral-900 border-neutral-200 active:bg-neutral-100"
                          }`}
                        >
                          {tab.key === "recomendado" && (
                            <Sparkles className="w-3.5 h-3.5" />
                          )}
                          {tab.label}
                          {hasSel && (
                            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#1644EB] border-2 border-white" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {filteredPoses.map((pose) => {
                      const isSelected = selectedPoses.includes(pose.id);
                      return (
                        <button
                          key={pose.id}
                          type="button"
                          onClick={() => handlePoseClick(pose.id)}
                          className={`relative rounded-[13px] overflow-hidden transition-all ${isSelected ? "ring-2 ring-[#1644EB]" : "ring-1 ring-black/[0.06] active:bg-black/[0.02]"}`}
                        >
                          {pose.image && (
                            <div className="bg-white">
                              <img
                                src={pose.image}
                                alt={pose.name}
                                loading="lazy"
                                className="block w-full aspect-[4/5] object-cover"
                              />
                            </div>
                          )}
                          {isSelected && (
                            <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1644EB] text-white shadow-sm">
                              <Check className="w-3.5 h-3.5" />
                            </span>
                          )}
                          {multiPoseToggle && !isSelected && (
                            <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-black/[0.15] bg-white/80" />
                          )}
                          <p
                            className={`text-[13px] text-brand-black/90 px-3 py-2 font-medium text-center bg-white ${!pose.image ? "py-6" : ""}`}
                          >
                            {pose.name}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>,
            document.body,
          )}
        </>
      )}

      {/* Vista del Producto Menu */}
      {showVistaProductoMenu && (
        <>
          {/* Desktop Side Panel */}
          <div
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[630px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              vistaProductoMenuAnimated
                ? "translate-x-0"
                : "translate-x-[calc(100%+32px)]"
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">
                Vista del producto
              </h3>
              <button
                type="button"
                onClick={closeVistaProductoMenu}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {/* Switch multi-vista (todos los productos) */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-[15px] text-brand-black/90">
                  Seleccionar múltiples vistas
                </span>
                <label className="relative inline-flex cursor-pointer">
                  <input
                    type="checkbox"
                    checked={multiVistaToggle}
                    onChange={(e) => handleMultiVistaToggle(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-[51px] h-[31px] bg-black/[0.09] rounded-full peer peer-checked:bg-[#1644EB] transition-colors" />
                  <div className="absolute top-[2px] left-[2px] w-[27px] h-[27px] bg-white rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.15)] transition-transform peer-checked:translate-x-[20px]" />
                </label>
              </div>
              {multiVistaToggle && selectedVistas.length > 1 && (
                <p className="text-[13px] text-[#3C3C43]/60 mb-3">
                  {selectedVistas.length} vistas seleccionadas — se generará 1
                  imagen por vista.
                </p>
              )}
              <p className="text-[15px] text-neutral-500 mb-4">
                Elegí desde qué ángulo querés mostrar el producto.
              </p>
              <div className="grid grid-cols-3 gap-3">
                {getVistaOptions()
                  .filter((vista) => !(multiVistaToggle && vista.id === "automatica"))
                  .map((vista) => {
                  const isSelected = multiVistaToggle
                    ? selectedVistas.includes(vista.id)
                    : selectedVistaProducto === vista.id;
                  const vistaImage = (vista as any).image as string | undefined;
                  return (
                    <button
                      key={vista.id}
                      type="button"
                      onClick={() => handleVistaProductoClick(vista.id)}
                      className={`relative rounded-[13px] overflow-hidden transition-all flex flex-col bg-white ${
                        isSelected
                          ? "ring-2 ring-[#1644EB]"
                          : "ring-1 ring-black/[0.06] hover:ring-black/[0.12]"
                      }`}
                    >
                      {vistaImage && (
                        <img
                          src={vistaImage}
                          alt={vista.name}
                          loading="lazy"
                          className="block w-full aspect-[4/5] object-cover"
                        />
                      )}
                      {isSelected && (
                        <span className="absolute top-2.5 right-2.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1644EB] text-white shadow-sm">
                          <Check className="w-3.5 h-3.5" />
                        </span>
                      )}
                      {multiVistaToggle && !isSelected && (
                        <span className="absolute top-2.5 right-2.5 inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-black/[0.15] bg-white/80" />
                      )}
                      <p
                        className={`text-[13px] text-brand-black/90 px-3 font-medium text-center flex-1 flex items-center justify-center ${
                          vistaImage ? "py-2.5" : "py-6"
                        }`}
                      >
                        {vista.name}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeVistaProductoMenu}
                className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleVistaProductoSubmit}
                disabled={
                  multiVistaToggle
                    ? selectedVistas.length === 0
                    : !selectedVistaProducto
                }
                className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${
                  (multiVistaToggle ? selectedVistas.length > 0 : selectedVistaProducto)
                    ? "text-white bg-brand-primary hover:bg-brand-primary-hover"
                    : "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed"
                }`}
              >
                Aceptar
              </button>
            </div>
          </div>
          {/* Mobile Slide */}
          {ReactDOM.createPortal(
            <>
              <div
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${vistaProductoMenuAnimated ? "opacity-100" : "opacity-0"}`}
                onClick={closeVistaProductoMenu}
              />
              <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
                style={{
                  height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
                  paddingBottom:
                    "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                  transform:
                    vistaProductoMenuDragY > 0
                      ? `translateY(${vistaProductoMenuDragY}px)`
                      : vistaProductoMenuAnimated
                        ? "translateY(0)"
                        : "translateY(100%)",
                  transition:
                    vistaProductoMenuDragY === 0
                      ? "transform 0.3s ease-out"
                      : "none",
                }}
              >
                <div
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleVistaProductoTouchStart}
                  onTouchMove={handleVistaProductoTouchMove}
                  onTouchEnd={handleVistaProductoTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button
                    type="button"
                    onClick={closeVistaProductoMenu}
                    className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
                  >
                    Cancelar
                  </button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Vista del producto
                  </h3>
                  <button
                    type="button"
                    onClick={handleVistaProductoSubmit}
                    disabled={
                      multiVistaToggle
                        ? selectedVistas.length === 0
                        : !selectedVistaProducto
                    }
                    className={`text-[15px] font-semibold w-20 text-right transition-opacity ${
                      (multiVistaToggle ? selectedVistas.length > 0 : selectedVistaProducto)
                        ? "text-[#1644EB] active:opacity-50"
                        : "text-[#3C3C43]/20 cursor-not-allowed"
                    }`}
                  >
                    Aceptar
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  {/* Switch multi-vista (todos los productos) */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[15px] text-brand-black/90">
                      Seleccionar múltiples vistas
                    </span>
                    <label className="relative inline-flex cursor-pointer">
                      <input
                        type="checkbox"
                        checked={multiVistaToggle}
                        onChange={(e) =>
                          handleMultiVistaToggle(e.target.checked)
                        }
                        className="sr-only peer"
                      />
                      <div className="w-[51px] h-[31px] bg-black/[0.09] rounded-full peer peer-checked:bg-[#1644EB] transition-colors" />
                      <div className="absolute top-[2px] left-[2px] w-[27px] h-[27px] bg-white rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.15)] transition-transform peer-checked:translate-x-[20px]" />
                    </label>
                  </div>
                  {multiVistaToggle && selectedVistas.length > 1 && (
                    <p className="text-[13px] text-[#3C3C43]/60 mb-3">
                      {selectedVistas.length} vistas seleccionadas — se generará
                      1 imagen por vista.
                    </p>
                  )}
                  <p className="text-[15px] text-neutral-500 mb-4">
                    Elegí desde qué ángulo querés mostrar el producto.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {getVistaOptions()
                      .filter((vista) => !(multiVistaToggle && vista.id === "automatica"))
                      .map((vista) => {
                      const isSelected = multiVistaToggle
                        ? selectedVistas.includes(vista.id)
                        : selectedVistaProducto === vista.id;
                      const vistaImage = (vista as any).image as string | undefined;
                      return (
                        <button
                          key={vista.id}
                          type="button"
                          onClick={() => handleVistaProductoClick(vista.id)}
                          className={`relative rounded-[13px] overflow-hidden transition-all flex flex-col bg-white ${
                            isSelected
                              ? "ring-2 ring-[#1644EB]"
                              : "ring-1 ring-black/[0.06] active:bg-black/[0.02]"
                          }`}
                        >
                          {vistaImage && (
                            <img
                              src={vistaImage}
                              alt={vista.name}
                              loading="lazy"
                              className="block w-full aspect-[4/5] object-cover"
                            />
                          )}
                          {isSelected && (
                            <span className="absolute top-2.5 right-2.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1644EB] text-white shadow-sm">
                              <Check className="w-3.5 h-3.5" />
                            </span>
                          )}
                          {multiVistaToggle && !isSelected && (
                            <span className="absolute top-2.5 right-2.5 inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-black/[0.15] bg-white/80" />
                          )}
                          <p
                            className={`text-[13px] text-brand-black/90 px-3 font-medium text-center bg-white ${
                              vistaImage ? "py-2.5" : "py-6"
                            }`}
                          >
                            {vista.name}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>,
            document.body,
          )}
        </>
      )}

      {/* Contact QR Modal - Desktop only */}
      {showContactQR && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6 relative">
            <button
              onClick={() => setShowContactQR(false)}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-[17px] font-semibold text-neutral-900 mb-2 text-center">
              Contactanos por WhatsApp
            </h3>
            <p className="text-[15px] text-neutral-600 mb-4 text-center">
              Escaneá el código QR con tu celular para abrir WhatsApp
            </p>
            <div className="flex justify-center mb-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent("https://wa.me/5491122635392?text=Hola%2C%20tuve%20un%20error%20al%20generar%20im%C3%A1genes%20en%20Atlas")}`}
                alt="QR Code WhatsApp"
                className="w-48 h-48"
              />
            </div>
            <button
              onClick={() => setShowContactQR(false)}
              className="w-full py-2 px-4 bg-neutral-100 text-neutral-700 rounded-full hover:bg-neutral-200"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PruebaNuevaInterfaz;
